// src/index.ts
import { z as z29 } from "zod";

// src/brain.ts
import { z } from "zod";
var knowledgeKindSchema = z.enum([
  "company",
  "product",
  "service",
  "price",
  "policy",
  "faq",
  "location",
  "person",
  "process",
  "restriction"
]);
var createBrainRequestSchema = z.object({
  workspaceId: z.string().min(1).max(200),
  name: z.string().min(2).max(160)
});
var brainViewSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  status: z.enum(["draft", "active", "archived"]),
  onboardingStep: z.number().int().min(0).max(10),
  onboardingProgress: z.number().int().min(0).max(100),
  nextQuestion: z.string().nullable(),
  draftVersion: z.number().int().positive(),
  proposalCount: z.number().int().nonnegative(),
  approvedCount: z.number().int().nonnegative(),
  publishedVersion: z.number().int().positive().nullable(),
  createdAt: z.iso.datetime({ offset: true })
});
var brainListSchema = z.object({
  items: z.array(brainViewSchema),
  nextCursor: z.string().nullable()
});
var onboardingMessageRequestSchema = z.object({ answer: z.string().min(1).max(5e3) });
var knowledgeProposalSchema = z.object({
  id: z.string(),
  brainId: z.string(),
  workspaceId: z.string(),
  kind: knowledgeKindSchema,
  canonicalContent: z.string(),
  confidence: z.number().min(0).max(1),
  approvalState: z.enum(["proposed", "approved", "rejected", "published"]),
  sourceId: z.string().nullable(),
  sourceLabel: z.string(),
  createdAt: z.iso.datetime({ offset: true })
});
var onboardingMessageResponseSchema = z.object({
  proposal: knowledgeProposalSchema,
  nextQuestion: z.string().nullable(),
  progress: z.number().int().min(0).max(100)
});
var addTextSourceRequestSchema = z.object({
  workspaceId: z.string().min(1),
  title: z.string().min(2).max(240),
  content: z.string().min(1).max(5e5)
});
var sourceAcceptedSchema = z.object({
  sourceId: z.string(),
  ingestionJobId: z.string(),
  status: z.literal("completed"),
  proposalCount: z.number().int().nonnegative()
});
var voiceOnboardingTopicViewSchema = z.object({
  key: z.string(),
  kind: z.string(),
  required: z.boolean(),
  question: z.string(),
  why: z.string()
});
var voiceOnboardingStatusSchema = z.object({
  answeredKeys: z.array(z.string()),
  missingRequiredKeys: z.array(z.string()),
  nextTopic: voiceOnboardingTopicViewSchema.nullable(),
  progress: z.number().int().min(0).max(100),
  /** False while a required phone question is unanswered. */
  callReady: z.boolean(),
  topics: z.array(voiceOnboardingTopicViewSchema.extend({ answered: z.boolean() }))
});
var voiceOnboardingAnswerRequestSchema = z.object({
  topicKey: z.string().min(2).max(80),
  answer: z.string().min(2).max(5e3)
});
var knowledgeGapSchema = z.object({
  key: z.string(),
  question: z.string(),
  variants: z.array(z.string()),
  occurrences: z.number().int().positive(),
  spokenInstead: z.string().nullable(),
  lastSeenAt: z.string().nullable()
});
var knowledgeGapReportSchema = z.object({
  sinceDays: z.number().int().positive(),
  unansweredTurns: z.number().int().nonnegative(),
  gaps: z.array(knowledgeGapSchema)
});
var websiteSourceRequestSchema = z.object({
  workspaceId: z.string().min(1).max(200),
  url: z.string().min(4).max(2e3),
  /** Pages to read, including the entry page. Server caps this. */
  maxPages: z.number().int().min(1).max(10).optional()
});
var proposalListSchema = z.object({
  items: z.array(knowledgeProposalSchema),
  nextCursor: z.string().nullable()
});
var publishBrainRequestSchema = z.object({
  expectedDraftVersion: z.number().int().positive()
});
var publishBrainResponseSchema = z.object({
  brainId: z.string(),
  brainVersionId: z.string(),
  version: z.number().int().positive(),
  itemCount: z.number().int().positive(),
  publishedAt: z.iso.datetime({ offset: true })
});
var retrievalResultSchema = z.object({
  knowledgeItemId: z.string(),
  knowledgeVersionId: z.string(),
  kind: knowledgeKindSchema,
  content: z.string(),
  sourceId: z.string().nullable(),
  sourceLabel: z.string(),
  score: z.number().min(0)
});
var retrievalResponseSchema = z.object({
  brainId: z.string(),
  brainVersion: z.number().int().positive(),
  items: z.array(retrievalResultSchema)
});

// src/agent.ts
import { z as z11 } from "zod";

// src/agent-playbook.ts
import { z as z2 } from "zod";
var playbookStepKindSchema = z2.enum([
  "message",
  "question",
  "branch",
  "end",
  "handoff",
  "lead",
  "appointment"
]);
var stepIdSchema = z2.string().min(1).max(40).regex(/^[a-zA-Z0-9_-]+$/u);
var branchOptionSchema = z2.object({
  label: z2.string().min(1).max(80),
  /** Freeform next-step hint for the model (linear compile; not a graph edge). */
  nextHint: z2.string().max(200).optional(),
  /**
   * Optional target step id for visual edges (Akış canvas).
   * Compile still linear; when set, instruction lines prefer `→ step:{id}`.
   */
  nextStepId: stepIdSchema.optional()
});
var playbookStepSchema = z2.discriminatedUnion("kind", [
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("message"),
    text: z2.string().min(1).max(2e3)
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("question"),
    text: z2.string().min(1).max(2e3),
    /** Optional slot name for the answer (prompt guidance only). */
    saveAs: z2.string().min(1).max(80).optional()
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("branch"),
    prompt: z2.string().min(1).max(500),
    options: z2.array(branchOptionSchema).min(2).max(3)
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("end"),
    text: z2.string().max(1e3).optional()
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("handoff"),
    reason: z2.string().max(500).optional()
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("lead"),
    note: z2.string().max(500).optional()
  }),
  z2.object({
    id: stepIdSchema,
    kind: z2.literal("appointment"),
    note: z2.string().max(500).optional()
  })
]);
var agentPlaybookSchema = z2.object({
  version: z2.literal(1).default(1),
  steps: z2.array(playbookStepSchema).max(40).default([]),
  /**
   * When true (default), draft save merges a compiled `## Playbook (linear)` block
   * into agent instructions so the runtime needs no new interpreter.
   */
  compileIntoInstructions: z2.boolean().default(true)
});
var PLAYBOOK_HEADER = "## Playbook (linear)";
var PLAYBOOK_FOOTER = "## End playbook";
var toolsFromPlaybook = (playbook) => {
  if (!playbook?.steps?.length) return [];
  const tools = /* @__PURE__ */ new Set(["conversation.add_note"]);
  for (const step of playbook.steps) {
    if (step.kind === "handoff") tools.add("handoff.request");
    if (step.kind === "lead") tools.add("lead.create");
    if (step.kind === "appointment") tools.add("appointment.book");
  }
  return [...tools];
};
var capabilitiesFromPlaybook = (playbook) => {
  const steps = playbook?.steps ?? [];
  return {
    handoff: steps.some((s) => s.kind === "handoff"),
    leadCreate: steps.some((s) => s.kind === "lead"),
    appointmentBooking: steps.some((s) => s.kind === "appointment")
  };
};
var compilePlaybookInstructions = (playbook) => {
  if (!playbook?.steps?.length) return "";
  const lines = [
    PLAYBOOK_HEADER,
    "Follow these steps in order. One step at a time. Do not skip unless the caller clearly changes intent.",
    "For BRANCH steps, match the caller answer to the closest option and continue with the matching hint.",
    "For HANDOFF use handoff.request. For LEAD use lead.create. For END close politely.",
    ""
  ];
  playbook.steps.forEach((step, index) => {
    const n = index + 1;
    switch (step.kind) {
      case "message":
        lines.push(`${n}. [MESSAGE] ${step.text}`);
        break;
      case "question":
        lines.push(
          `${n}. [QUESTION] ${step.text}` + (step.saveAs ? ` (save as: ${step.saveAs})` : "")
        );
        break;
      case "branch": {
        lines.push(`${n}. [BRANCH] ${step.prompt}`);
        step.options.forEach((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          let arrow = "";
          if (opt.nextStepId && opt.nextHint) {
            arrow = ` \u2192 step:${opt.nextStepId} (${opt.nextHint})`;
          } else if (opt.nextStepId) {
            arrow = ` \u2192 step:${opt.nextStepId}`;
          } else if (opt.nextHint) {
            arrow = ` \u2192 ${opt.nextHint}`;
          }
          lines.push(`   - ${letter}) ${opt.label}${arrow}`);
        });
        break;
      }
      case "end":
        lines.push(`${n}. [END] ${step.text?.trim() || "Thank the caller and end the conversation."}`);
        break;
      case "handoff":
        lines.push(
          `${n}. [HANDOFF] ${step.reason?.trim() || "Transfer to a human via handoff.request."}`
        );
        break;
      case "lead":
        lines.push(
          `${n}. [LEAD] ${step.note?.trim() || "Capture lead with lead.create, then confirm next step."}`
        );
        break;
      case "appointment":
        lines.push(
          `${n}. [APPOINTMENT] ${step.note?.trim() || "Collect preferred day/time and service; use appointment.book (no external calendar invent). Confirm human will verify."}`
        );
        break;
      default:
        break;
    }
  });
  lines.push("", PLAYBOOK_FOOTER);
  return lines.join("\n");
};
var mergePlaybookIntoInstructions = (baseInstructions, playbook) => {
  const compiled = compilePlaybookInstructions(playbook);
  const stripped = stripPlaybookSection(baseInstructions).trim();
  if (!compiled) return stripped;
  if (!stripped) return compiled;
  return `${stripped}

${compiled}`;
};
var stripPlaybookSection = (instructions) => {
  const start = instructions.indexOf(PLAYBOOK_HEADER);
  if (start < 0) return instructions;
  const afterHeader = instructions.slice(start + PLAYBOOK_HEADER.length);
  const endRel = afterHeader.indexOf(PLAYBOOK_FOOTER);
  if (endRel < 0) {
    return instructions.slice(0, start).trimEnd();
  }
  const end = start + PLAYBOOK_HEADER.length + endRel + PLAYBOOK_FOOTER.length;
  return `${instructions.slice(0, start).trimEnd()}
${instructions.slice(end).trimStart()}`.trim();
};
var defaultInboundPlaybook = () => agentPlaybookSchema.parse({
  version: 1,
  compileIntoInstructions: true,
  steps: [
    {
      id: "s1",
      kind: "message",
      text: "G\xF6r\xFC\u015Fmeyi nazik\xE7e a\xE7; ad\u0131n\u0131 s\xF6yle; nas\u0131l yard\u0131mc\u0131 olabilece\u011Fini sor."
    },
    {
      id: "s2",
      kind: "question",
      text: "Arayan\u0131n ihtiyac\u0131n\u0131 tek c\xFCmleyle \xF6\u011Fren.",
      saveAs: "need"
    },
    {
      id: "s3",
      kind: "branch",
      prompt: "\u0130htiya\xE7 sat\u0131\u015F m\u0131, destek mi, yoksa ba\u015Fka m\u0131?",
      options: [
        {
          label: "Sat\u0131\u015F / teklif",
          nextHint: "lead ad\u0131m\u0131na git",
          nextStepId: "s4"
        },
        {
          label: "Destek / sorun",
          nextHint: "handoff veya SSS",
          nextStepId: "s5"
        },
        {
          label: "Di\u011Fer",
          nextHint: "netle\u015Ftir veya handoff",
          nextStepId: "s5"
        }
      ]
    },
    {
      id: "s4",
      kind: "lead",
      note: "Uygunsa lead.create ile kaydet."
    },
    {
      id: "s5",
      kind: "handoff",
      reason: "\u0130nsan gerekti\u011Finde handoff.request kullan."
    },
    {
      id: "s6",
      kind: "end",
      text: "Te\u015Fekk\xFCr et ve kapat."
    }
  ]
});

// src/speech-protection-legacy.ts
import { z as z3 } from "zod";
var SPEECH_PROTECTION_LEGACY_PARAM_REJECTED = "SPEECH_PROTECTION_LEGACY_PARAM_REJECTED";
var LEGACY_SPEECH_PROTECTION_PARAM_KEYS = [
  "speechProtectionMode",
  "requestedProtection",
  "desiredProtection",
  "forceUnguarded",
  "skipProtection",
  "disableSpeechProtection",
  "bypassValidator"
];
var rejectIfPresent = z3.unknown().optional().superRefine((value, ctx) => {
  if (value !== void 0) {
    ctx.addIssue({
      code: "custom",
      message: SPEECH_PROTECTION_LEGACY_PARAM_REJECTED
    });
  }
});
var legacySpeechProtectionRejectionFields = {
  speechProtectionMode: rejectIfPresent,
  requestedProtection: rejectIfPresent,
  desiredProtection: rejectIfPresent,
  forceUnguarded: rejectIfPresent,
  skipProtection: rejectIfPresent,
  disableSpeechProtection: rejectIfPresent,
  bypassValidator: rejectIfPresent
};

// src/conversation-control.ts
import { z as z5 } from "zod";

// src/conversation-tool-intent.ts
import { z as z4 } from "zod";
var requestToolBusinessIntentSchema = z4.enum([
  "CREATE_LEAD",
  "SCHEDULE_CALLBACK",
  "BOOK_APPOINTMENT",
  "CREATE_CASE"
]);
var REQUEST_TOOL_BUSINESS_INTENTS = requestToolBusinessIntentSchema.options;

// src/conversation-control.ts
var factKeySchema = z5.string().min(1).max(64).regex(/^[a-z][a-z0-9_]{0,63}$/u);
var stageIdSchema = z5.string().min(1).max(40).regex(/^[a-z][a-z0-9_]{0,39}$/u);
var factTypeSchema = z5.enum([
  "string",
  "number",
  "boolean",
  "enum",
  "datetime",
  "integer"
]);
var factStatusSchema = z5.enum([
  "unknown",
  "inferred",
  "provided",
  "confirmed",
  "conflicted",
  "not_applicable"
]);
var confirmationPolicySchema = z5.enum(["never", "always", "if_low_confidence"]);
var memoryScopeSchema = z5.enum(["turn", "call", "cross_call"]);
var factExtractionMethodSchema = z5.enum([
  "none",
  "user_text",
  "tool",
  "operator",
  "system"
]);
var genericActionKindSchema = z5.enum([
  "ASK_FACT",
  "CONFIRM_FACT",
  "PRESENT_VALUE",
  "HANDLE_OBJECTION",
  "REQUEST_TOOL",
  "HANDOFF",
  "CLOSE_SUCCESS",
  "CLOSE_DECLINE",
  "WAIT",
  "CLARIFY"
]);
var eligibleActionSchema = z5.object({
  kind: genericActionKindSchema,
  factKey: factKeySchema.optional(),
  toolName: z5.string().min(1).max(160).optional(),
  /**
   * Typed REQUEST_TOOL payload. Absent on generic/legacy REQUEST_TOOL.
   * Missing businessIntent authorizes no side-effect tool.
   */
  businessIntent: requestToolBusinessIntentSchema.optional()
});
var factValidationSchema = z5.object({
  min: z5.number().optional(),
  max: z5.number().optional(),
  pattern: z5.string().min(1).max(200).optional()
}).optional();
var agentFactSchemaEntrySchema = z5.object({
  key: factKeySchema,
  label: z5.string().min(1).max(80),
  type: factTypeSchema.default("string"),
  description: z5.string().max(400).optional(),
  enumValues: z5.array(z5.string().min(1).max(80)).max(32).optional(),
  requiredIn: z5.array(stageIdSchema).max(40).default([]),
  confirmationPolicy: confirmationPolicySchema.default("if_low_confidence"),
  scope: memoryScopeSchema.default("call"),
  validation: factValidationSchema
});
var agentMissionSchema = z5.object({
  primaryGoal: z5.string().min(1).max(200),
  successCondition: z5.string().min(1).max(400)
});
var completionRulesSchema = z5.object({
  successStageIds: z5.array(stageIdSchema).max(40).default([]),
  requiredFactKeys: z5.array(factKeySchema).max(64).default([]),
  allowCloseOnDecline: z5.boolean().default(true)
});
var conversationPoliciesSchema = z5.object({
  doNotRepeatConfirmed: z5.boolean().default(true),
  maxQuestionsPerTurn: z5.number().int().min(1).max(5).default(1),
  lowConfidenceThreshold: z5.number().min(0).max(1).default(0.7),
  closeRequiresCompletion: z5.boolean().default(true)
});
var playbookStageSchema = z5.object({
  id: stageIdSchema,
  objective: z5.string().min(1).max(400),
  requiredFactKeys: z5.array(factKeySchema).max(64).default([]),
  allowedActions: z5.array(genericActionKindSchema).max(20).default([]),
  /**
   * Specific REQUEST_TOOL intent for this stage. Omitted = generic REQUEST_TOOL
   * (authorization NONE). Does not invent tool names; resolver owns that mapping.
   */
  requestToolIntent: requestToolBusinessIntentSchema.optional(),
  exitConditions: z5.array(z5.string().min(1).max(200)).max(20).default([]),
  nextStageIds: z5.array(stageIdSchema).max(40).default([])
});
var conversationPlaybookSchema = z5.object({
  version: z5.literal(2),
  initialStageId: stageIdSchema,
  stages: z5.array(playbookStageSchema).min(1).max(40)
});
var agentConversationControlSchema = z5.object({
  mission: agentMissionSchema.optional(),
  factSchema: z5.array(agentFactSchemaEntrySchema).max(64).default([]),
  playbook: conversationPlaybookSchema.optional().nullable(),
  completionRules: completionRulesSchema.optional(),
  policies: conversationPoliciesSchema.optional()
});
var conversationFactRecordSchema = z5.object({
  key: factKeySchema,
  value: z5.unknown().nullable(),
  status: factStatusSchema,
  confidence: z5.number().min(0).max(1).nullable(),
  sourceTurnId: z5.string().min(1).max(80).nullable(),
  sourceMessageId: z5.string().min(1).max(80).nullable(),
  extractionMethod: factExtractionMethodSchema.default("none"),
  updatedAt: z5.iso.datetime({ offset: true })
});
var unique = (items) => {
  const seen = /* @__PURE__ */ new Set();
  const dupes = [];
  for (const item of items) {
    if (seen.has(item)) dupes.push(item);
    seen.add(item);
  }
  return dupes;
};
var validateConversationControl = (control) => {
  if (!control) return [];
  const issues = [];
  const factKeys = control.factSchema.map((entry) => entry.key);
  const factKeySet = new Set(factKeys);
  for (const dupe of unique(factKeys)) {
    issues.push({ path: "factSchema", message: `DUPLICATE_FACT_KEY:${dupe}` });
  }
  control.factSchema.forEach((entry, index) => {
    if (entry.type === "enum" && !(entry.enumValues && entry.enumValues.length > 0)) {
      issues.push({
        path: `factSchema.${index}.enumValues`,
        message: "ENUM_VALUES_REQUIRED"
      });
    }
  });
  const playbook = control.playbook ?? null;
  const stageIds = playbook ? playbook.stages.map((stage) => stage.id) : [];
  const stageSet = new Set(stageIds);
  if (playbook) {
    for (const dupe of unique(stageIds)) {
      issues.push({ path: "playbook.stages", message: `DUPLICATE_STAGE_ID:${dupe}` });
    }
    if (!stageSet.has(playbook.initialStageId)) {
      issues.push({
        path: "playbook.initialStageId",
        message: `UNKNOWN_STAGE:${playbook.initialStageId}`
      });
    }
    playbook.stages.forEach((stage, index) => {
      for (const nextId of stage.nextStageIds) {
        if (!stageSet.has(nextId)) {
          issues.push({
            path: `playbook.stages.${index}.nextStageIds`,
            message: `UNKNOWN_STAGE:${nextId}`
          });
        }
      }
      for (const factKey of stage.requiredFactKeys) {
        if (!factKeySet.has(factKey)) {
          issues.push({
            path: `playbook.stages.${index}.requiredFactKeys`,
            message: `UNKNOWN_FACT_KEY:${factKey}`
          });
        }
      }
    });
  }
  control.factSchema.forEach((entry, index) => {
    for (const stageId of entry.requiredIn) {
      if (!stageSet.has(stageId)) {
        issues.push({
          path: `factSchema.${index}.requiredIn`,
          message: `UNKNOWN_STAGE:${stageId}`
        });
      }
    }
  });
  const completion = control.completionRules;
  if (completion) {
    for (const factKey of completion.requiredFactKeys) {
      if (!factKeySet.has(factKey)) {
        issues.push({
          path: "completionRules.requiredFactKeys",
          message: `UNKNOWN_FACT_KEY:${factKey}`
        });
      }
    }
    for (const stageId of completion.successStageIds) {
      if (!stageSet.has(stageId)) {
        issues.push({
          path: "completionRules.successStageIds",
          message: `UNKNOWN_STAGE:${stageId}`
        });
      }
    }
  }
  return issues;
};

// src/agent-widget.ts
import { z as z6 } from "zod";
var hexColor = z6.string().regex(/^#[0-9A-Fa-f]{6}$/u, "HEX_COLOR_REQUIRED");
var hhmm = z6.string().regex(/^\d{2}:\d{2}$/u);
var agentWidgetConfigSchema = z6.object({
  enabled: z6.boolean().default(true),
  /** Header title; falls back to agent name at runtime. */
  title: z6.string().min(1).max(80).optional(),
  subtitle: z6.string().max(120).optional(),
  primaryColor: hexColor.default("#4f46e5"),
  accentColor: hexColor.optional(),
  position: z6.enum(["bottom-right", "bottom-left"]).default("bottom-right"),
  launcherLabel: z6.string().min(1).max(40).default("Chat"),
  /** Optional pre-session bubble text (not sent as a user message). */
  welcomeMessage: z6.string().max(500).optional(),
  /**
   * When non-empty, client should only load on matching hostnames
   * (e.g. example.com, *.example.com). Enforcement is client + soft server hint.
   */
  allowedDomains: z6.array(z6.string().min(1).max(200)).max(20).default([]),
  businessHours: z6.object({
    timezone: z6.string().min(2).max(80).default("Europe/Istanbul"),
    /** Empty days = always open. day: 0=Sunday … 6=Saturday */
    days: z6.array(
      z6.object({
        day: z6.number().int().min(0).max(6),
        open: hhmm,
        close: hhmm
      })
    ).max(7).default([])
  }).optional(),
  showBranding: z6.boolean().default(true)
});
var defaultAgentWidgetConfig = () => agentWidgetConfigSchema.parse({});
var webchatWidgetThemeSchema = agentWidgetConfigSchema;
var buildWebchatEmbedSnippet = (input) => {
  const base = input.apiBaseUrl.replace(/\/$/u, "");
  const scriptSrc = input.embedScriptUrl?.trim() || `${base}/embed.js`;
  const primary = input.widget?.primaryColor ?? "#4f46e5";
  const position = input.widget?.position ?? "bottom-right";
  const side = position === "bottom-left" ? "left:20px" : "right:20px";
  return `<!-- CenterOS Webchat Widget -->
<script type="module" src="${scriptSrc}"></script>
<style>
  agentos-webchat {
    position: fixed;
    bottom: 20px;
    ${side};
    z-index: 99999;
    width: min(380px, calc(100vw - 24px));
    max-height: min(640px, calc(100vh - 40px));
    --centeros-primary: ${primary};
    box-shadow: 0 12px 40px rgba(15,23,42,.18);
    border-radius: 16px;
    overflow: hidden;
  }
</style>
<agentos-webchat
  api-base-url="${base}"
  deployment-key="${input.deploymentKey}"
></agentos-webchat>`;
};
var isWidgetDomainAllowed = (hostname, allowedDomains) => {
  if (!allowedDomains?.length) return true;
  if (!hostname) return false;
  const host = hostname.toLowerCase().replace(/\.$/u, "");
  return allowedDomains.some((rule) => {
    const r = rule.trim().toLowerCase();
    if (!r) return false;
    if (r.startsWith("*.")) {
      const suffix = r.slice(1);
      return host.endsWith(suffix) || host === r.slice(2);
    }
    return host === r;
  });
};

// src/agent-templates.ts
import { z as z8 } from "zod";

// src/voice-profiles.ts
import { z as z7 } from "zod";
var namedVoiceProfileSchema = z7.object({
  id: z7.string().min(1).max(80),
  label: z7.string().min(1).max(120),
  locale: z7.string().min(2).max(35),
  gender: z7.enum(["female", "male", "neutral"]),
  character: z7.string().min(1).max(80),
  recommendedForPhone: z7.boolean(),
  stylePrompt: z7.string().min(10).max(1e3),
  greetingTemplate: z7.string().min(10).max(500),
  qaScore: z7.number().min(0).max(10).nullable(),
  provider: z7.object({
    openaiVoiceId: z7.string().min(1).max(80),
    /** ElevenLabs voice_id (account library / shared catalogue). */
    elevenlabsVoiceId: z7.string().min(1).max(80),
    /** Human-readable ElevenLabs library name shown in studio UI. */
    elevenlabsVoiceLabel: z7.string().min(1).max(120)
  })
});
var activeTtsProvider = (env = process.env) => env.CENTEROS_TTS_PROVIDER?.trim().toLowerCase() === "elevenlabs" ? "elevenlabs" : "openai";
var profileTtsVoiceId = (profile, ttsProvider = activeTtsProvider()) => ttsProvider === "elevenlabs" ? profile.provider.elevenlabsVoiceId : profile.provider.openaiVoiceId;
var naturalPhoneStyle = "Speak Turkish like a real person on a phone call with a customer: warm, relaxed, conversational. Natural pace (not slow, not rushed). Soft intonation, slight smile in the voice. Do not sound robotic, formal-announcer, or over-enunciated. Short sentences only.";
var namedVoiceProfiles = [
  {
    id: "tr_female_warm",
    label: "T\xFCrk\xE7e \u2014 S\u0131cak kad\u0131n (\xF6nerilen)",
    locale: "tr-TR",
    gender: "female",
    character: "warm",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Friendly female voice.`,
    greetingTemplate: "Merhaba, ben {agentName}. Sizi duyuyorum, nas\u0131l yard\u0131mc\u0131 olabilirim?",
    qaScore: 8,
    // coral tends to sound less "forced" than shimmer on Turkish phone audio.
    // Emine SÜMER — Turkish conversational female (account library).
    provider: {
      openaiVoiceId: "coral",
      elevenlabsVoiceId: "AzDtLFQ0galizFNFE2xQ",
      elevenlabsVoiceLabel: "Emine S\xDCMER"
    }
  },
  {
    id: "tr_female_corporate",
    label: "T\xFCrk\xE7e \u2014 Kurumsal kad\u0131n",
    locale: "tr-TR",
    gender: "female",
    character: "corporate",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Polite professional female voice, still human and easy.`,
    greetingTemplate: "Merhaba, ben {agentName}. Size nas\u0131l yard\u0131mc\u0131 olabilirim?",
    qaScore: 7,
    provider: {
      openaiVoiceId: "marin",
      elevenlabsVoiceId: "XrExE9yKIg1WjnnlVkGX",
      elevenlabsVoiceLabel: "Matilda"
    }
  },
  {
    id: "tr_female_clear",
    label: "T\xFCrk\xE7e \u2014 Canl\u0131 kad\u0131n",
    locale: "tr-TR",
    gender: "female",
    character: "clear",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Bright but not loud female voice.`,
    greetingTemplate: "Merhaba, ben {agentName}. Sizi duyuyorum, size nas\u0131l yard\u0131mc\u0131 olay\u0131m?",
    qaScore: 7,
    provider: {
      openaiVoiceId: "nova",
      elevenlabsVoiceId: "cgSgspJ2msm6clMCkdW9",
      elevenlabsVoiceLabel: "Jessica"
    }
  },
  {
    id: "tr_female_soft",
    label: "T\xFCrk\xE7e \u2014 Yumu\u015Fak kad\u0131n",
    locale: "tr-TR",
    gender: "female",
    character: "soft",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Soft female voice, gentle and close-mic feel.`,
    greetingTemplate: "Merhaba, ben {agentName}. Size yard\u0131mc\u0131 olmak i\xE7in buraday\u0131m.",
    qaScore: 7,
    provider: {
      openaiVoiceId: "shimmer",
      elevenlabsVoiceId: "pFZP5JQG7iQjIQuC4Bku",
      elevenlabsVoiceLabel: "Lily"
    }
  },
  {
    id: "tr_male_professional",
    label: "T\xFCrk\xE7e \u2014 Profesyonel erkek",
    locale: "tr-TR",
    gender: "male",
    character: "professional",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Confident male voice, friendly not stiff.`,
    greetingTemplate: "Merhaba, ben {agentName}. Sizi duyuyorum, nas\u0131l yard\u0131mc\u0131 olabilirim?",
    qaScore: 7,
    provider: {
      openaiVoiceId: "cedar",
      elevenlabsVoiceId: "sqN4QwtcnanCCWx6TTYj",
      elevenlabsVoiceLabel: "Eyyup Okan"
    }
  },
  {
    id: "tr_male_calm",
    label: "T\xFCrk\xE7e \u2014 Sakin erkek",
    locale: "tr-TR",
    gender: "male",
    character: "calm",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Calm male voice, unhurried but not sleepy.`,
    greetingTemplate: "Merhaba, ben {agentName}. Size nas\u0131l yard\u0131mc\u0131 olabilirim?",
    qaScore: 7,
    provider: {
      openaiVoiceId: "ash",
      elevenlabsVoiceId: "fXhoW006nc5Wf8xkGVSy",
      elevenlabsVoiceLabel: "Yunus"
    }
  },
  {
    id: "tr_male_clear",
    label: "T\xFCrk\xE7e \u2014 Net erkek",
    locale: "tr-TR",
    gender: "male",
    character: "clear",
    recommendedForPhone: true,
    stylePrompt: `${naturalPhoneStyle} Clear male voice, natural and direct.`,
    greetingTemplate: "Merhaba, ben {agentName}. Sizi duyuyorum, nas\u0131l yard\u0131mc\u0131 olabilirim?",
    qaScore: 6,
    provider: {
      openaiVoiceId: "onyx",
      elevenlabsVoiceId: "onwK4e9ZLuTAKqWW03F9",
      elevenlabsVoiceLabel: "Daniel"
    }
  }
];
var namedVoiceProfileIdSchema = z7.enum(
  namedVoiceProfiles.map((profile) => profile.id)
);
var DEFAULT_VOICE_PROFILE_ID = "tr_female_warm";
var resolveNamedVoiceProfile = (profileId) => {
  const match = namedVoiceProfiles.find((profile) => profile.id === profileId);
  return match ?? namedVoiceProfiles.find((profile) => profile.id === DEFAULT_VOICE_PROFILE_ID);
};
var resolveNamedVoiceProfileByProviderVoice = (providerVoiceId) => {
  const match = namedVoiceProfiles.find(
    (profile) => profile.provider.openaiVoiceId === providerVoiceId || profile.provider.elevenlabsVoiceId === providerVoiceId
  );
  return match ?? resolveNamedVoiceProfile(DEFAULT_VOICE_PROFILE_ID);
};
var listNamedVoiceProfiles = () => namedVoiceProfiles;
var DEFAULT_VOICE_PREVIEW_TEXT_TR = "Merhaba, ben sizin dijital asistan\u0131n\u0131z\u0131m. Size nas\u0131l yard\u0131mc\u0131 olabilirim?";
var DEFAULT_VOICE_PREVIEW_TEXT_EN = "Hello, I am your digital assistant. How can I help you today?";
var voicePreviewRequestSchema = z7.object({
  /** Named CenterOS profile id, e.g. tr_female_warm */
  profileId: namedVoiceProfileIdSchema.optional(),
  /** Raw provider voice id (ElevenLabs / OpenAI) when profileId not sent */
  voiceId: z7.string().min(1).max(80).optional(),
  language: z7.string().min(2).max(35).optional(),
  text: z7.string().min(1).max(280).optional()
}).refine((v) => Boolean(v.profileId || v.voiceId), {
  message: "PROFILE_OR_VOICE_REQUIRED"
});
var voicePreviewResponseSchema = z7.object({
  contentType: z7.literal("audio/mpeg"),
  audioBase64: z7.string().min(8),
  profileId: z7.string().nullable(),
  voiceId: z7.string(),
  text: z7.string(),
  byteSize: z7.number().int().nonnegative()
});
var providerVoiceCatalogItemSchema = z7.object({
  voiceId: z7.string().min(1).max(80),
  name: z7.string().min(1).max(200),
  gender: z7.enum(["female", "male", "neutral", "unknown"]).default("unknown"),
  language: z7.string().min(1).max(40).nullable(),
  accent: z7.string().min(1).max(80).nullable(),
  category: z7.string().min(1).max(80).nullable(),
  description: z7.string().max(500).nullable(),
  /** Official sample clip from provider when available */
  previewUrl: z7.string().url().nullable(),
  provider: z7.enum(["elevenlabs", "openai"]),
  /** True when this library voice is wired to a CenterOS named shortcut profile */
  recommended: z7.boolean().default(false),
  mappedProfileId: namedVoiceProfileIdSchema.nullable().optional()
});
var providerVoiceCatalogSchema = z7.object({
  provider: z7.enum(["elevenlabs", "openai"]),
  total: z7.number().int().nonnegative(),
  items: z7.array(providerVoiceCatalogItemSchema)
});

// src/agent-templates.ts
var agentTemplateCategorySchema = z8.enum([
  "receptionist",
  "lead_qualification",
  "appointment",
  "support",
  "sales_outbound",
  "survey",
  "collections",
  "event",
  "general"
]);
var agentChannelModeSchema = z8.enum(["inbound", "outbound", "webchat", "omni"]);
var agentCapabilitiesSchema = z8.object({
  handoff: z8.boolean().default(true),
  endConversation: z8.boolean().default(true),
  leadCreate: z8.boolean().default(false),
  callbackSchedule: z8.boolean().default(false),
  /** Appointment booking request (appointment.book tool; no Google Calendar in v1). */
  appointmentBooking: z8.boolean().default(false),
  caseCreate: z8.boolean().default(false),
  /**
   * Product intent: post/mid-call WhatsApp template (or text) send via workspace connectors.
   * Does not invent Meta credentials — runtime tools remain `connector:{id}#messages_send_*`.
   */
  messagingWhatsappTemplate: z8.boolean().default(false),
  /**
   * Product intent: SMS pack awareness (Numbers → SMS PACK).
   * No first-party agent SMS tool; outbound SMS stays on Numbers pack / configured adapter.
   */
  messagingSmsPack: z8.boolean().default(false)
});
var isWhatsAppMessagingConnectorTool = (tool) => /^connector:[^#]+#messages_send_(text|template)$/u.test(tool);
var agentTemplateSchema = z8.object({
  id: z8.string().min(2).max(80),
  category: agentTemplateCategorySchema,
  name: z8.string().min(2).max(160),
  /** Optional English gallery title for bilingual UI. */
  nameEn: z8.string().min(2).max(160).optional(),
  description: z8.string().min(10).max(500),
  descriptionEn: z8.string().min(10).max(500).optional(),
  localeHints: z8.array(z8.string().min(2).max(12)).min(1).max(6),
  role: z8.string().min(2).max(240),
  persona: z8.string().min(2).max(2e3),
  instructions: z8.string().min(10).max(2e4),
  /** Default (TR) opening line; supports {{business_name}} {{agent_name}} {{purpose}}. */
  firstMessage: z8.string().min(1).max(2e3),
  /** English opening line for EN quick-start. */
  firstMessageEn: z8.string().min(1).max(2e3).optional(),
  variables: z8.array(z8.string().min(1).max(60)).max(20),
  suggestedChannelMode: agentChannelModeSchema,
  suggestedTools: z8.array(z8.string().min(1).max(160)).max(20),
  suggestedVoiceProfileId: namedVoiceProfileIdSchema,
  suggestedCapabilities: agentCapabilitiesSchema,
  blockedTopics: z8.array(z8.string().min(1).max(160)).max(20).default([])
});
var agentTemplateListSchema = z8.object({
  items: z8.array(agentTemplateSchema),
  categories: z8.array(agentTemplateCategorySchema)
});
var recTools = ["conversation.add_note", "handoff.request"];
var salesTools = [
  "conversation.add_note",
  "lead.create",
  "callback.schedule",
  "handoff.request"
];
var AGENT_TEMPLATE_CATALOG = [
  {
    id: "tpl_virtual_receptionist",
    category: "receptionist",
    name: "Sanal resepsiyon",
    nameEn: "Virtual receptionist",
    description: "Gelen aramalar\u0131 kar\u015F\u0131lar, SSS yan\u0131tlar, mesaj al\u0131r ve gerekirse insana aktar\u0131r. 7/24 \xF6n hat i\xE7in.",
    localeHints: ["tr-TR", "en"],
    role: "Profesyonel sanal resepsiyon asistan\u0131",
    persona: "Sakin, nazik ve verimli bir \xF6n b\xFCro g\xF6revlisi. K\u0131sa c\xFCmleler; tek seferde tek soru.",
    instructions: `Sen {{business_name}} i\xE7in sanal resepsiyonsun.
- Arayan\u0131 s\u0131cak kar\u015F\u0131l, ad\u0131n\u0131 {{agent_name}} olarak kullan.
- \xC7al\u0131\u015Fma saatleri, adres, hizmetler gibi SSS i\xE7in yaln\u0131zca yay\u0131nlanm\u0131\u015F Brain bilgisini kullan.
- Bilmedi\u011Fin konuda uydurma; mesaj al veya handoff.request ile aktar.
- Ayn\u0131 anda birden fazla soru sorma.
- Gizli anahtar veya i\xE7 politika a\xE7\u0131klama.`,
    firstMessage: "Merhaba, {{business_name}} sanal asistan\u0131 {{agent_name}}. Size nas\u0131l yard\u0131mc\u0131 olabilirim?",
    firstMessageEn: "Hello, this is {{agent_name}} at {{business_name}}. How can I help you today?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "inbound",
    suggestedTools: [...recTools],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: true,
      caseCreate: false
    },
    blockedTopics: ["fiyat uydurma", "rakipler", "yasal taahh\xFCt"]
  },
  {
    id: "tpl_lead_qualification",
    category: "lead_qualification",
    name: "Lead nitelendirme",
    nameEn: "Lead qualification",
    description: "B\xFCt\xE7e, zaman \xE7izelgesi ve karar yetkisini sorarak lead nitelendirir; skor i\xE7in not ve lead kayd\u0131 tutar.",
    localeHints: ["tr-TR", "en"],
    role: "Sat\u0131\u015F \xF6ncesi lead nitelendirme uzman\u0131",
    persona: "Merakl\u0131, profesyonel, bask\u0131s\u0131z. Dinler, \xF6zetler, net sonraki ad\u0131m \xF6nerir.",
    instructions: `Sen {{business_name}} i\xE7in lead nitelendirme asistan\u0131s\u0131n.
Hedef: ihtiyac\u0131 anlamak, BANT benzeri sorular (b\xFCt\xE7e, yetki, ihtiya\xE7, zaman) sormak.
- Tek seferde bir soru sor.
- Uydurma \xFCr\xFCn veya fiyat s\xF6yleme; Brain d\u0131\u015F\u0131nda vaat yok.
- Nitelikli g\xF6r\xFCn\xFCrse lead.create kullan; gerekirse callback.schedule.
- Uygunsuz ise nazik\xE7e kapat.`,
    firstMessage: "Merhaba, ben {{agent_name}}, {{business_name}} ekibinden. K\u0131sa bir g\xF6r\xFC\u015Fme i\xE7in ar\u0131yorum \u2014 size uygun mu?",
    firstMessageEn: "Hi, this is {{agent_name}} from {{business_name}}. Do you have a moment for a short call?",
    variables: ["business_name", "agent_name", "purpose"],
    suggestedChannelMode: "outbound",
    suggestedTools: [...salesTools],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: true,
      callbackSchedule: true,
      caseCreate: false
    },
    blockedTopics: ["garanti vaat", "rakipleri k\xF6t\xFCle"]
  },
  {
    id: "tpl_appointment_setter",
    category: "appointment",
    name: "Randevu ayarlama",
    nameEn: "Appointment booking",
    description: "Hizmet se\xE7imi ve uygun zaman\u0131 toplayarak randevu talebi olu\u015Fturur; onay c\xFCmlesi s\xF6yler.",
    localeHints: ["tr-TR", "en"],
    role: "Randevu planlama asistan\u0131",
    persona: "D\xFCzenli, net, yard\u0131mc\u0131. Tarih/saat teyidi yapar.",
    instructions: `Sen {{business_name}} randevu asistan\u0131s\u0131n.
- Hangi hizmet / kim i\xE7in randevu isteniyor \xF6\u011Fren.
- Tercih edilen g\xFCn ve saat aral\u0131\u011F\u0131n\u0131 sor.
- appointment.book ile talebi kaydet. Harici takvim senkronu yaln\u0131zca workspace'te aktif takvim ba\u011Flant\u0131s\u0131 varsa \xE7al\u0131\u015F\u0131r; yoksa kay\u0131t CRM-lite kal\u0131r. M\xFCsaitlik/\xE7ak\u0131\u015Fma uydurma; insan teyidi gerekti\u011Fini s\xF6yle.
- \xC7ak\u0131\u015Fma veya m\xFCsaitlik uydurma.`,
    firstMessage: "Merhaba, {{business_name}} randevu hatt\u0131, ben {{agent_name}}. Hangi hizmet i\xE7in randevu planlamak istersiniz?",
    firstMessageEn: "Hello, {{business_name}} appointments \u2014 {{agent_name}} speaking. Which service would you like to book?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "omni",
    suggestedTools: [
      "conversation.add_note",
      "appointment.book",
      "callback.schedule",
      "lead.create",
      "handoff.request"
    ],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: true,
      callbackSchedule: true,
      appointmentBooking: true,
      caseCreate: false
    },
    blockedTopics: []
  },
  {
    id: "tpl_customer_support",
    category: "support",
    name: "M\xFC\u015Fteri destek",
    nameEn: "Customer support",
    description: "Destek taleplerini kar\u015F\u0131lar, Brain bilgisinden \xE7\xF6z\xFCm \xF6nerir, gerekirse vaka a\xE7ar veya aktar\u0131r.",
    localeHints: ["tr-TR", "en"],
    role: "M\xFC\u015Fteri destek uzman\u0131",
    persona: "Empatik, sab\u0131rl\u0131, \xE7\xF6z\xFCm odakl\u0131. Teknik jargonu sadele\u015Ftirir.",
    instructions: `Sen {{business_name}} m\xFC\u015Fteri destek asistan\u0131s\u0131n.
- Sorunu netle\u015Ftir (ne oldu, ne zaman, hangi kanal).
- Yaln\u0131zca Brain / do\u011Frulanm\u0131\u015F ba\u011Flamla yan\u0131t ver.
- \xC7\xF6z\xFClemiyorsa case.create veya handoff.request.
- \u0130ade/garanti politikas\u0131 uydurma.`,
    firstMessage: "Merhaba, {{business_name}} destek, ben {{agent_name}}. Size nas\u0131l yard\u0131mc\u0131 olabilirim?",
    firstMessageEn: "Hello, {{business_name}} support, this is {{agent_name}}. How can I help?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "inbound",
    suggestedTools: [
      "conversation.add_note",
      "case.create",
      "callback.schedule",
      "handoff.request"
    ],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: true,
      caseCreate: true
    },
    blockedTopics: ["\xFCcretsiz taahh\xFCt", "rakipler"]
  },
  {
    id: "tpl_outbound_sales",
    category: "sales_outbound",
    name: "Giden sat\u0131\u015F / tan\u0131t\u0131m",
    nameEn: "Outbound sales",
    description: "K\u0131sa outbound pitch, ilgi \xF6l\xE7\xFCm\xFC, itirazlara sakin yan\u0131t; randevu veya lead kayd\u0131.",
    localeHints: ["tr-TR", "en"],
    role: "Outbound sat\u0131\u015F dan\u0131\u015Fman\u0131",
    persona: "Canl\u0131 ama sayg\u0131l\u0131; bask\u0131 yapmaz. De\u011Fer odakl\u0131 konu\u015Fur.",
    instructions: `Sen {{business_name}} ad\u0131na giden arama yapan sat\u0131\u015F asistan\u0131s\u0131n.
- Ama\xE7 notu (purpose) varsa ona sad\u0131k kal; script ba\u015Fl\u0131klar\u0131n\u0131 okuma.
- 2\u20133 c\xFCmlede de\u011Fer \xF6nerisi, sonra izinle soru sor.
- \u0130lgisizse nazik kapan\u0131\u015F; ilgiliyse lead.create / callback.schedule.
- Fiyat ve indirim uydurma.`,
    firstMessage: "Merhaba, ben {{agent_name}}, {{business_name}} ad\u0131na ar\u0131yorum. {{purpose}} konusunda k\u0131saca bilgi vermek istiyorum \u2014 uygun musunuz?",
    firstMessageEn: "Hi, this is {{agent_name}} calling on behalf of {{business_name}} about {{purpose}} \u2014 is now a good time?",
    variables: ["business_name", "agent_name", "purpose"],
    suggestedChannelMode: "outbound",
    suggestedTools: [...salesTools],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: true,
      callbackSchedule: true,
      caseCreate: false
    },
    blockedTopics: ["garanti", "rakip k\xF6t\xFCleme"]
  },
  {
    id: "tpl_nps_survey",
    category: "survey",
    name: "NPS / memnuniyet anketi",
    nameEn: "NPS / satisfaction survey",
    description: "0\u201310 tavsiye skoru ve k\u0131sa geri bildirim toplar; d\xFC\u015F\xFCk skorlarda aktar\u0131m \xF6nerir.",
    localeHints: ["tr-TR", "en"],
    role: "M\xFC\u015Fteri deneyimi anket asistan\u0131",
    persona: "N\xF6tr, te\u015Fekk\xFCr eden, k\u0131sa. Tart\u0131\u015Fmaya girmez.",
    instructions: `Sen {{business_name}} i\xE7in memnuniyet anketi yap\u0131yorsun.
1) 0\u201310 aras\u0131nda tavsiye skoru sor (NPS).
2) Nedenini k\u0131saca sor.
3) Skor \u22646 ise handoff veya callback teklif et.
4) Skor \u22659 ise te\u015Fekk\xFCr et; testimonial zorlama.
Notlar\u0131 conversation.add_note ile kaydet.`,
    firstMessage: "Merhaba, {{business_name}} ad\u0131na arayan {{agent_name}}. Bir dakikan\u0131z\u0131 ay\u0131r\u0131p deneyiminizi 0 ile 10 aras\u0131nda puanlar m\u0131s\u0131n\u0131z?",
    firstMessageEn: "Hello, this is {{agent_name}} from {{business_name}}. On a scale of 0 to 10, how likely are you to recommend us?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "outbound",
    suggestedTools: ["conversation.add_note", "callback.schedule", "handoff.request"],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: true,
      caseCreate: false
    },
    blockedTopics: []
  },
  {
    id: "tpl_payment_reminder",
    category: "collections",
    name: "\xD6deme hat\u0131rlatma",
    nameEn: "Payment reminder",
    description: "Vadesi ge\xE7mi\u015F \xF6demeyi kibarca hat\u0131rlat\u0131r; se\xE7enek sunar, tart\u0131\u015Fmada insana aktar\u0131r.",
    localeHints: ["tr-TR", "en"],
    role: "\xD6deme hat\u0131rlatma asistan\u0131",
    persona: "Sayg\u0131l\u0131, net, yarg\u0131lamayan. Hukuki tehdit yok.",
    instructions: `Sen {{business_name}} \xF6deme hat\u0131rlatma asistan\u0131s\u0131n.
- Kimlik teyidi yapmadan detay payla\u015Fma.
- \xD6deme hat\u0131rlat; Brain'deki kanallar d\u0131\u015F\u0131nda y\xF6ntem uydurma.
- Anla\u015Fmazl\u0131k / \xF6deme plan\u0131 talebinde handoff.request.
- Hakaret veya bask\u0131 yok.`,
    firstMessage: "Merhaba, {{business_name}} finans biriminden {{agent_name}}. K\u0131sa bir \xF6deme hat\u0131rlatmas\u0131 i\xE7in ar\u0131yorum; uygun musunuz?",
    firstMessageEn: "Hello, this is {{agent_name}} from {{business_name}} billing with a short payment reminder \u2014 is now okay?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "outbound",
    suggestedTools: ["conversation.add_note", "callback.schedule", "handoff.request"],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: true,
      caseCreate: true
    },
    blockedTopics: ["yasal tehdit", "ki\u015Fisel hakaret"]
  },
  {
    id: "tpl_event_registration",
    category: "event",
    name: "Etkinlik kayd\u0131",
    nameEn: "Event registration",
    description: "Etkinlik kayd\u0131 i\xE7in ileti\u015Fim ve tercih toplar; onay mesaj\u0131 verir.",
    localeHints: ["tr-TR", "en"],
    role: "Etkinlik kay\u0131t asistan\u0131",
    persona: "Enerjik ama k\u0131sa; organizasyon detaylar\u0131n\u0131 Brain'den okur.",
    instructions: `Sen {{business_name}} etkinlik kay\u0131t asistan\u0131s\u0131n.
- Etkinlik ad\u0131n\u0131 ve tarihi Brain'den do\u011Frula.
- Kat\u0131l\u0131mc\u0131 ad\u0131, ileti\u015Fim, \xF6zel ihtiya\xE7 (diyet/eri\u015Filebilirlik) sor.
- lead.create ile kaydet; kontenjan uydurma.
- Bilgi yoksa handoff.`,
    firstMessage: "Merhaba, {{business_name}} etkinlik hatt\u0131, ben {{agent_name}}. Kay\u0131t i\xE7in yard\u0131mc\u0131 olay\u0131m m\u0131?",
    firstMessageEn: "Hello, {{business_name}} events \u2014 {{agent_name}} here. Can I help you register?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "omni",
    suggestedTools: ["conversation.add_note", "lead.create", "handoff.request"],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: true,
      callbackSchedule: false,
      caseCreate: false
    },
    blockedTopics: []
  },
  {
    id: "tpl_tech_support",
    category: "support",
    name: "Teknik destek (troubleshooting)",
    nameEn: "Technical support",
    description: "Ad\u0131m ad\u0131m sorun giderme; \xE7\xF6z\xFClmezse vaka veya uzman aktar\u0131m\u0131.",
    localeHints: ["tr-TR", "en"],
    role: "Teknik destek uzman\u0131",
    persona: "Sistematik, sakin, ad\u0131m ad\u0131m y\xF6nlendiren.",
    instructions: `Sen {{business_name}} teknik destek asistan\u0131s\u0131n.
- Ortam bilgisini topla (cihaz, taray\u0131c\u0131, hata mesaj\u0131).
- Brain'deki bilinen \xE7\xF6z\xFCmleri dene; i\u015Fe yaramazsa case.create.
- Tehlikeli sistem komutlar\u0131 \xF6nerme.
- G\xFCvenlik / \u015Fifre isteme.`,
    firstMessage: "Merhaba, {{business_name}} teknik destek, {{agent_name}}. Ya\u015Fad\u0131\u011F\u0131n\u0131z sorunu k\u0131saca anlat\u0131r m\u0131s\u0131n\u0131z?",
    firstMessageEn: "Hello, {{business_name}} tech support, {{agent_name}} speaking. Could you briefly describe the issue?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "inbound",
    suggestedTools: ["conversation.add_note", "case.create", "handoff.request"],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: true,
      caseCreate: true
    },
    blockedTopics: ["\u015Fifre iste", "uzak eri\u015Fim izinsiz"]
  },
  {
    id: "tpl_info_hotline",
    category: "general",
    name: "Bilgi hatt\u0131",
    nameEn: "Information hotline",
    description: "Genel bilgi ve y\xF6nlendirme; derin sat\u0131\u015F veya destek yerine do\u011Fru kanala y\xF6nlendirir.",
    localeHints: ["tr-TR", "en"],
    role: "Genel bilgi asistan\u0131",
    persona: "A\xE7\u0131k, tarafs\u0131z, y\xF6nlendirici.",
    instructions: `Sen {{business_name}} genel bilgi hatt\u0131s\u0131n.
- SSS i\xE7in Brain kullan.
- Sat\u0131\u015F / \u015Fikayet / acil teknik konular\u0131nda do\u011Fru ak\u0131\u015Fa y\xF6nlendir veya handoff.
- Spek\xFClasyon yok.`,
    firstMessage: "Merhaba, {{business_name}} bilgi hatt\u0131, ben {{agent_name}}. Nas\u0131l yard\u0131mc\u0131 olabilirim?",
    firstMessageEn: "Hello, {{business_name}} information line, this is {{agent_name}}. How can I help?",
    variables: ["business_name", "agent_name"],
    suggestedChannelMode: "omni",
    suggestedTools: [...recTools],
    suggestedVoiceProfileId: DEFAULT_VOICE_PROFILE_ID,
    suggestedCapabilities: {
      handoff: true,
      endConversation: true,
      leadCreate: false,
      callbackSchedule: false,
      caseCreate: false
    },
    blockedTopics: []
  }
].map((item) => agentTemplateSchema.parse(item));
var listAgentTemplates = (category) => {
  const items = category ? AGENT_TEMPLATE_CATALOG.filter((t) => t.category === category) : [...AGENT_TEMPLATE_CATALOG];
  const categories = [
    ...new Set(AGENT_TEMPLATE_CATALOG.map((t) => t.category))
  ];
  return agentTemplateListSchema.parse({ items, categories });
};
var getAgentTemplate = (templateId) => AGENT_TEMPLATE_CATALOG.find((t) => t.id === templateId) ?? null;
var toolsFromCapabilities = (caps, extra = []) => {
  const tools = new Set(extra);
  tools.add("conversation.add_note");
  if (caps.handoff) tools.add("handoff.request");
  if (caps.endConversation) tools.add("conversation.end");
  if (caps.leadCreate) tools.add("lead.create");
  if (caps.callbackSchedule) tools.add("callback.schedule");
  if (caps.appointmentBooking) tools.add("appointment.book");
  if (caps.caseCreate) tools.add("case.create");
  return [...tools].slice(0, 20);
};
var capabilitiesFromTools = (tools) => ({
  handoff: tools.includes("handoff.request"),
  // Historically always on; still true by default so operators see the hangup capability.
  endConversation: true,
  leadCreate: tools.includes("lead.create"),
  callbackSchedule: tools.includes("callback.schedule"),
  appointmentBooking: tools.includes("appointment.book"),
  caseCreate: tools.includes("case.create"),
  messagingWhatsappTemplate: tools.some(isWhatsAppMessagingConnectorTool),
  messagingSmsPack: false
});
var AGENT_TEMPLATE_VARIABLES = [
  "business_name",
  "agent_name",
  "purpose"
];
var pickTemplateFirstMessage = (template, locale) => {
  const loc = (locale ?? template.localeHints[0] ?? "tr").toLowerCase();
  if (loc.startsWith("en") && template.firstMessageEn) return template.firstMessageEn;
  return template.firstMessage;
};
var resolveAgentTemplateVars = (text, vars) => {
  const map = {
    business_name: vars.businessName?.trim() || "i\u015Fletmemiz",
    company_name: vars.businessName?.trim() || "i\u015Fletmemiz",
    agent_name: vars.agentName?.trim() || "asistan",
    purpose: vars.purpose?.trim() || "g\xF6r\xFC\u015Fme"
  };
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/gu, (full, key) => {
    const k = key.toLowerCase();
    return k in map ? map[k] : full;
  });
};

// src/billing.ts
import { z as z9 } from "zod";
var usageMeterSchema = z9.enum([
  "voice_seconds",
  "telephony_minutes",
  "stt_seconds",
  "tts_characters",
  "llm_input_tokens",
  "llm_output_tokens",
  "messages",
  "active_phone_numbers",
  "storage_gb_days",
  "workflow_runs",
  "seats",
  /** Live COUNT of workspace agents (gate only; not usage_events ledger). */
  "active_agents",
  /** Live COUNT of non-archived workspace campaigns (gate only; not usage_events). */
  "active_campaigns",
  /** Live COUNT of workspace contacts with status=active (gate only; not usage_events). */
  "active_contacts",
  /** Live COUNT of workspace voice_leads rows (list-meta / soft banner only; not usage_events). */
  "active_leads"
]);
var quotaEnforcementSchema = z9.enum(["none", "soft", "hard"]);
var planEntitlementSchema = z9.object({
  key: z9.string().regex(/^[a-z][a-z0-9_.-]{1,99}$/u),
  meter: usageMeterSchema.nullable(),
  limit: z9.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema
});
var createPlanRequestSchema = z9.object({
  code: z9.string().regex(/^[a-z0-9][a-z0-9_-]{1,49}$/u),
  name: z9.string().min(2).max(120),
  currency: z9.string().length(3),
  monthlyPriceMinor: z9.number().int().nonnegative(),
  partnerMarkupBps: z9.number().int().min(0).max(1e5).default(0),
  entitlements: z9.array(planEntitlementSchema).min(1).max(100)
});
var planViewSchema = createPlanRequestSchema.extend({
  id: z9.string(),
  status: z9.enum(["active", "archived"]),
  createdAt: z9.iso.datetime({ offset: true })
});
var brandingSchema = z9.object({
  productName: z9.string().min(2).max(80),
  logoUrl: z9.url().nullable(),
  primaryColor: z9.string().regex(/^#[0-9a-fA-F]{6}$/u),
  accentColor: z9.string().regex(/^#[0-9a-fA-F]{6}$/u),
  supportUrl: z9.url().nullable(),
  customDomain: z9.string().regex(/^[a-z0-9.-]+$/u).nullable(),
  /**
   * Must stay in sync with domain `navigationRules` ids.
   * Missing entries hide sidebar modules via visibleNavigation(allowedModules).
   */
  allowedModules: z9.array(
    z9.enum([
      "overview",
      "campaigns",
      "conversations",
      "leads",
      "directory",
      "numbers",
      "brain",
      "agents",
      "channels",
      "providers",
      "integrations",
      "workflows",
      "live",
      "analytics",
      "billing",
      "settings"
    ])
  ).min(1)
});
var updatePartnerBrandingRequestSchema = brandingSchema.extend({
  expectedVersion: z9.number().int().nonnegative()
});
var partnerBrandingViewSchema = brandingSchema.extend({
  platformId: z9.string(),
  version: z9.number().int().positive(),
  updatedAt: z9.iso.datetime({ offset: true })
});
var provisionPartnerTenantRequestSchema = z9.object({
  tenantExternalId: z9.string().min(1).max(200),
  tenantName: z9.string().min(2).max(160),
  workspaceExternalId: z9.string().min(1).max(200),
  workspaceName: z9.string().min(2).max(160),
  ownerExternalId: z9.string().min(1).max(200),
  ownerDisplayName: z9.string().min(2).max(160),
  ownerLocale: z9.string().min(2).max(35).default("tr-TR"),
  planId: z9.string().min(1),
  dataRegion: z9.string().min(2).max(80),
  idempotencyKey: z9.string().min(8).max(200)
});
var subscriptionStatusSchema = z9.enum([
  "trialing",
  "active",
  "grace",
  "suspended",
  "ended"
]);
var subscriptionViewSchema = z9.object({
  id: z9.string(),
  plan: planViewSchema,
  status: subscriptionStatusSchema,
  currentPeriodStart: z9.iso.datetime({ offset: true }),
  currentPeriodEnd: z9.iso.datetime({ offset: true }),
  graceEndsAt: z9.iso.datetime({ offset: true }).nullable(),
  entitlements: z9.array(planEntitlementSchema),
  version: z9.number().int().positive()
});
var provisionedTenantViewSchema = z9.object({
  tenantId: z9.string(),
  tenantExternalId: z9.string(),
  workspaceId: z9.string(),
  workspaceExternalId: z9.string(),
  ownerId: z9.string(),
  ownerExternalId: z9.string(),
  subscription: subscriptionViewSchema,
  branding: partnerBrandingViewSchema,
  createdAt: z9.iso.datetime({ offset: true })
});
var recordUsageRequestSchema = z9.object({
  tenantId: z9.string().min(1),
  workspaceId: z9.string().min(1),
  meter: usageMeterSchema,
  quantity: z9.number().positive(),
  source: z9.enum(["runtime", "provider", "workflow", "storage", "admin"]),
  sourceRef: z9.string().min(1).max(300),
  idempotencyKey: z9.string().min(8).max(200),
  occurredAt: z9.iso.datetime({ offset: true })
});
var reverseUsageRequestSchema = z9.object({
  reason: z9.string().min(3).max(500),
  idempotencyKey: z9.string().min(8).max(200)
});
var usageEventViewSchema = z9.object({
  id: z9.string(),
  workspaceId: z9.string(),
  meter: usageMeterSchema,
  quantity: z9.number(),
  source: z9.string(),
  sourceRef: z9.string(),
  reversesEventId: z9.string().nullable(),
  occurredAt: z9.iso.datetime({ offset: true })
});
var quotaDecisionSchema = z9.object({
  meter: usageMeterSchema,
  allowed: z9.boolean(),
  enforcement: quotaEnforcementSchema,
  used: z9.number().nonnegative(),
  limit: z9.number().nonnegative().nullable(),
  remaining: z9.number().nonnegative().nullable(),
  warning: z9.boolean(),
  reason: z9.enum(["within_limit", "soft_limit_exceeded", "hard_limit_exceeded", "unlimited"])
});
var authorizeUsageRequestSchema = z9.object({
  workspaceId: z9.string().min(1).max(200),
  meter: usageMeterSchema,
  requested: z9.number().positive()
});
var billingSummarySchema = z9.object({
  workspaceId: z9.string(),
  from: z9.iso.datetime({ offset: true }),
  to: z9.iso.datetime({ offset: true }),
  subscription: subscriptionViewSchema,
  totals: z9.array(
    z9.object({
      meter: usageMeterSchema,
      quantity: z9.number(),
      limit: z9.number().nonnegative().nullable(),
      enforcement: quotaEnforcementSchema
    })
  )
});
var moneyDualSchema = z9.object({
  tryMinor: z9.number().int(),
  usdMinor: z9.number().int()
});
var costFxSchema = z9.object({
  usdTry: z9.number().positive(),
  source: z9.literal("config"),
  asOf: z9.string().nullable()
});
var costServiceSchema = z9.enum([
  "voice",
  "telephony",
  "stt",
  "tts",
  "llm",
  "messaging",
  "storage",
  "other"
]);
var ZERO_MONEY_DUAL = { tryMinor: 0, usdMinor: 0 };
var normalizeUsageCostMargin = (value) => {
  const estimatedCost = value.estimatedCost;
  return {
    estimatedCost,
    estimatedWholesaleCost: value.estimatedWholesaleCost ?? estimatedCost,
    estimatedRetailCharge: value.estimatedRetailCharge ?? estimatedCost,
    estimatedGrossProfit: value.estimatedGrossProfit ?? ZERO_MONEY_DUAL
  };
};
var normalizeUsageCostMarginWithBps = (value) => ({
  ...normalizeUsageCostMargin(value),
  grossMarginBps: value.grossMarginBps ?? 0
});
var billingUsageDashboardSchema = z9.object({
  workspaceId: z9.string(),
  from: z9.iso.datetime({ offset: true }),
  to: z9.iso.datetime({ offset: true }),
  fx: costFxSchema,
  kpis: z9.object({
    talkSeconds: z9.number(),
    telephonyMinutes: z9.number(),
    calls: z9.number().int().nonnegative(),
    ttsCharacters: z9.number(),
    sttSeconds: z9.number(),
    llmTokens: z9.number(),
    /** Back-compat alias for estimatedWholesaleCost (PR2 wire shape). */
    estimatedCost: moneyDualSchema,
    estimatedWholesaleCost: moneyDualSchema.optional(),
    estimatedRetailCharge: moneyDualSchema.optional(),
    estimatedGrossProfit: moneyDualSchema.optional(),
    grossMarginBps: z9.number().int().optional()
  }).transform((value) => ({
    talkSeconds: value.talkSeconds,
    telephonyMinutes: value.telephonyMinutes,
    calls: value.calls,
    ttsCharacters: value.ttsCharacters,
    sttSeconds: value.sttSeconds,
    llmTokens: value.llmTokens,
    ...normalizeUsageCostMarginWithBps(value)
  })),
  series: z9.object({
    byDay: z9.array(
      z9.object({
        date: z9.string(),
        talkSeconds: z9.number(),
        telephonyMinutes: z9.number(),
        calls: z9.number().int().nonnegative(),
        /** Back-compat alias for estimatedWholesaleCost (PR2 wire shape). */
        estimatedCost: moneyDualSchema,
        estimatedWholesaleCost: moneyDualSchema.optional(),
        estimatedRetailCharge: moneyDualSchema.optional(),
        estimatedGrossProfit: moneyDualSchema.optional()
      }).transform((value) => ({
        date: value.date,
        talkSeconds: value.talkSeconds,
        telephonyMinutes: value.telephonyMinutes,
        calls: value.calls,
        ...normalizeUsageCostMargin(value)
      }))
    ),
    byMeter: z9.array(
      z9.object({
        meter: usageMeterSchema,
        quantity: z9.number(),
        /** Back-compat alias for estimatedWholesaleCost (PR2 wire shape). */
        estimatedCost: moneyDualSchema,
        estimatedWholesaleCost: moneyDualSchema.optional(),
        estimatedRetailCharge: moneyDualSchema.optional(),
        estimatedGrossProfit: moneyDualSchema.optional()
      }).transform((value) => ({
        meter: value.meter,
        quantity: value.quantity,
        ...normalizeUsageCostMargin(value)
      }))
    ),
    byService: z9.array(
      z9.object({
        service: costServiceSchema,
        quantity: z9.number(),
        /** Back-compat alias for estimatedWholesaleCost (PR2 wire shape). */
        estimatedCost: moneyDualSchema,
        estimatedWholesaleCost: moneyDualSchema.optional(),
        estimatedRetailCharge: moneyDualSchema.optional(),
        estimatedGrossProfit: moneyDualSchema.optional(),
        grossMarginBps: z9.number().int().optional()
      }).transform((value) => ({
        service: value.service,
        quantity: value.quantity,
        ...normalizeUsageCostMarginWithBps(value)
      }))
    )
  })
});
var retentionPolicyRequestSchema = z9.object({
  conversationDays: z9.number().int().min(1).max(3650),
  transcriptDays: z9.number().int().min(1).max(3650),
  recordingDays: z9.number().int().min(1).max(3650),
  auditDays: z9.number().int().min(365).max(3650),
  legalHold: z9.boolean().default(false),
  expectedVersion: z9.number().int().nonnegative()
});
var retentionPolicyViewSchema = retentionPolicyRequestSchema.omit({ expectedVersion: true }).extend({
  tenantId: z9.string(),
  /**
   * 0 = not yet persisted (server default before first PUT).
   * First save uses expectedVersion 0 → version 1.
   */
  version: z9.number().int().nonnegative(),
  updatedAt: z9.iso.datetime({ offset: true })
});
var dataLifecycleRequestSchema = z9.object({
  workspaceId: z9.string().min(1).nullable().default(null),
  reason: z9.string().min(3).max(500),
  idempotencyKey: z9.string().min(8).max(200)
});
var dataLifecycleRequestViewSchema = z9.object({
  id: z9.string(),
  workspaceId: z9.string().nullable(),
  kind: z9.enum(["export", "delete"]),
  status: z9.enum(["queued", "processing", "ready", "completed", "cancelled", "failed"]),
  objectKey: z9.string().nullable(),
  executeAfter: z9.iso.datetime({ offset: true }).nullable(),
  createdAt: z9.iso.datetime({ offset: true }),
  completedAt: z9.iso.datetime({ offset: true }).nullable()
});
var dataLifecycleRequestListSchema = z9.object({
  items: z9.array(dataLifecycleRequestViewSchema),
  nextCursor: z9.string().nullable()
});

// src/personality-presets.ts
import { z as z10 } from "zod";
var personalityPresetIdSchema = z10.enum([
  "warm_empathetic",
  "clear_direct",
  "formal_corporate",
  "sales_consultative",
  "calm_support",
  "energetic_friendly",
  "strict_script",
  "bilingual_tr_en"
]);
var personalityPresetSchema = z10.object({
  id: personalityPresetIdSchema,
  labelTr: z10.string().min(2).max(80),
  labelEn: z10.string().min(2).max(80),
  /** Ready-to-use persona string (TR primary for this product). */
  persona: z10.string().min(10).max(2e3),
  /** Short tone tags for cards. */
  tags: z10.array(z10.string().min(1).max(40)).max(6)
});
var PERSONALITY_PRESETS = [
  {
    id: "warm_empathetic",
    labelTr: "S\u0131cak & empatik",
    labelEn: "Warm & empathetic",
    persona: "S\u0131cak, sab\u0131rl\u0131 ve empatik. M\xFC\u015Fteriyi dinler, duygusunu yans\u0131t\u0131r, panik yaratmaz. K\u0131sa c\xFCmlelerle g\xFCvence verir.",
    tags: ["empati", "destek", "yumu\u015Fak"]
  },
  {
    id: "clear_direct",
    labelTr: "Net & do\u011Frudan",
    labelEn: "Clear & direct",
    persona: "Net, do\u011Frudan ve zaman\u0131na sayg\u0131l\u0131. Gereksiz s\xFCslemeden konu\u015Fur; soruyu cevaplar, sonraki ad\u0131m\u0131 tek c\xFCmlede s\xF6yler.",
    tags: ["net", "k\u0131sa", "operasyon"]
  },
  {
    id: "formal_corporate",
    labelTr: "Resmi kurumsal",
    labelEn: "Formal corporate",
    persona: "Resmi, kurumsal ve \xF6l\xE7\xFCl\xFC. Hitaplar nazik; spek\xFClasyon yapmaz; politika ve s\xFCre\xE7lere sad\u0131k kal\u0131r.",
    tags: ["resmi", "B2B", "kurumsal"]
  },
  {
    id: "sales_consultative",
    labelTr: "Dan\u0131\u015Fman sat\u0131\u015F",
    labelEn: "Consultative sales",
    persona: "Dan\u0131\u015Fman sat\u0131\u015F\xE7\u0131: ihtiyac\u0131 ke\u015Ffeder, bask\u0131 yapmaz, fayda-odakl\u0131 sorar. Fiyat uydurmaz; randevu veya lead ile kapat\u0131r.",
    tags: ["sat\u0131\u015F", "ke\u015Fif", "lead"]
  },
  {
    id: "calm_support",
    labelTr: "Sakin destek",
    labelEn: "Calm support",
    persona: "Sakin teknik destek tonu. Sorunu ad\u0131m ad\u0131m netle\u015Ftirir, jargonu sadele\u015Ftirir, \xE7\xF6z\xFCm yoksa insana aktar\u0131r.",
    tags: ["destek", "sakin", "\xE7\xF6z\xFCm"]
  },
  {
    id: "energetic_friendly",
    labelTr: "Enerjik & samimi",
    labelEn: "Energetic & friendly",
    persona: "Enerjik, samimi ve pozitif. Kar\u015F\u0131lama s\u0131cak; abart\u0131l\u0131 vaat vermez; g\xF6r\xFC\u015Fmeyi ak\u0131c\u0131 tutar.",
    tags: ["samimi", "outbound", "marka"]
  },
  {
    id: "strict_script",
    labelTr: "Script disiplinli",
    labelEn: "Strict script",
    persona: "Script disiplinli: playbook ve talimatlara s\u0131k\u0131 uyum. Do\u011Fa\xE7lama minimum; onay ve kapan\u0131\u015F kal\u0131plar\u0131n\u0131 korur.",
    tags: ["script", "uyum", "compliance"]
  },
  {
    id: "bilingual_tr_en",
    labelTr: "TR/EN esnek",
    labelEn: "Flexible TR/EN",
    persona: "T\xFCrk\xE7e \xF6ncelikli; m\xFC\u015Fteri \u0130ngilizce konu\u015Fursa nazik\xE7e \u0130ngilizceye ge\xE7er. Ton profesyonel ve net kal\u0131r.",
    tags: ["TR", "EN", "\xE7ok dilli"]
  }
];
var personalityPresetListSchema = z10.object({
  items: z10.array(personalityPresetSchema)
});
var resolvePersonalityPreset = (id) => {
  if (!id) return null;
  const parsed = personalityPresetIdSchema.safeParse(id);
  if (!parsed.success) return null;
  return PERSONALITY_PRESETS.find((p) => p.id === parsed.data) ?? null;
};

// src/agent.ts
var voiceProfileBaseSchema = z11.object({
  /** Named professional profile. Optional on input so legacy drafts with only voiceId still parse. */
  profileId: namedVoiceProfileIdSchema.optional(),
  language: z11.string().min(2).max(35).default("tr-TR"),
  /**
   * Provider-native voice id (ElevenLabs library id or OpenAI short name).
   * Optional on input — filled from profileId / platform default in transform.
   * Do not default to 'coral' here: that would mask profile-only payloads and
   * fight ElevenLabs library selections on parse.
   */
  voiceId: z11.string().min(1).max(120).optional(),
  /**
   * Per-agent TTS backend for the pipeline (STT → LLM → TTS) runtime.
   * Ignored while voiceRuntime is openai_realtime — that model listens and speaks itself.
   */
  ttsProvider: z11.enum(["openai", "elevenlabs"]).optional(),
  recordingEnabled: z11.boolean().default(true),
  recordingNotice: z11.string().min(10).max(1e3).default("This call may be recorded for quality purposes."),
  interruptionEnabled: z11.boolean().default(true),
  /**
   * FAZ5: per-agent barge-in policy for media-gateway / softphone.
   * - hard — cancel assistant audio on barge (legacy)
   * - duck — lower agent volume while user speaks, cancel only on sustained speech
   * - off — ignore barge (also when interruptionEnabled=false)
   * When omitted: interruptionEnabled=false → off; else platform VOICE_INTERRUPTION_MODE / hard.
   */
  interruptionMode: z11.enum(["hard", "duck", "off"]).optional(),
  /**
   * Faz 1: line echo cancellation on the phone uplink, using the agent audio we
   * wrote as reference. With 'nlms' and a converged canceller the 1.8 s barge-in
   * blind window is dropped, so callers can interrupt within ~200 ms.
   * 'off' (default) keeps the legacy grace behaviour.
   */
  echoCancellation: z11.enum(["off", "nlms"]).optional(),
  maxCallSeconds: z11.number().int().min(30).max(14400).default(1800),
  handoffDestination: z11.string().regex(/^\+[1-9]\d{7,14}$/u).optional(),
  /**
   * Default transfer style when Live ops / API omits mode.
   * warm — caller hears ringback (Dial); blind — silent ARI redirect.
   * Full attended consult is out of scope.
   */
  defaultHandoffMode: z11.enum(["warm", "blind"]).optional(),
  /** Product knobs (media may wire gradually). */
  turnTimeoutSeconds: z11.number().min(0.5).max(10).optional(),
  silenceTimeoutMs: z11.number().int().min(1e3).max(3e4).optional(),
  endCallOnSilence: z11.boolean().optional(),
  temperature: z11.number().min(0).max(1).optional(),
  /**
   * Looping telephone bed mixed under agent speech / wait.
   * Catalog is code-owned so new beds can be added later without schema churn.
   */
  ambienceBedId: z11.string().min(1).max(80).regex(/^(none|office|custom:[a-zA-Z0-9_-]{6,64})$/u).optional(),
  /** 0–0.3 linear gain on the 8 kHz bed. */
  ambienceGain: z11.number().min(0).max(0.3).optional(),
  ambienceDuringSpeech: z11.boolean().optional(),
  ambienceDuringWait: z11.boolean().optional()
});
var VOICE_AMBIENCE_BEDS = [
  {
    id: "none",
    label: "Off",
    file: null,
    previewUrl: null
  },
  {
    id: "office",
    label: "Office ambience",
    file: "office-24734.pcm",
    previewUrl: "/ambience/office-24734.mp3"
  }
];
var workspaceAmbienceAssetSchema = z11.object({
  id: z11.string().min(8).max(80),
  workspaceId: z11.string().min(1).max(80),
  name: z11.string().min(1).max(80),
  originalName: z11.string().min(1).max(200),
  contentType: z11.string().min(1).max(80),
  durationMs: z11.number().int().positive(),
  createdAt: z11.string(),
  bedId: z11.string().min(1).max(80)
});
var workspaceAmbienceListSchema = z11.object({
  items: z11.array(workspaceAmbienceAssetSchema)
});
var uploadWorkspaceAmbienceRequestSchema = z11.object({
  workspaceId: z11.string().min(1).max(80),
  name: z11.string().min(1).max(80),
  filename: z11.string().min(1).max(200),
  contentType: z11.string().min(1).max(80),
  audioBase64: z11.string().min(64).max(16e6)
});
var llmTemperatureOption = (temperature) => typeof temperature === "number" && Number.isFinite(temperature) ? { temperature } : {};
var OPENAI_SHORT_VOICE_IDS = /* @__PURE__ */ new Set([
  "alloy",
  "echo",
  "fable",
  "onyx",
  "nova",
  "shimmer",
  "coral",
  "verse",
  "ballad",
  "ash",
  "sage",
  "marin",
  "cedar"
]);
var voiceProfileSchema = voiceProfileBaseSchema.transform((value) => {
  const explicitVoice = value.voiceId?.trim() || "";
  const looksLikeElevenLabsLibraryId = explicitVoice.length >= 16 && !OPENAI_SHORT_VOICE_IDS.has(explicitVoice);
  const tts = value.ttsProvider === "elevenlabs" || value.ttsProvider === "openai" ? value.ttsProvider : looksLikeElevenLabsLibraryId ? "elevenlabs" : activeTtsProvider();
  if (looksLikeElevenLabsLibraryId && value.ttsProvider !== "openai") {
    const matched = value.profileId ? resolveNamedVoiceProfile(value.profileId) : resolveNamedVoiceProfileByProviderVoice(explicitVoice);
    const isMapped = matched.provider.openaiVoiceId === explicitVoice || matched.provider.elevenlabsVoiceId === explicitVoice;
    return {
      ...value,
      profileId: value.profileId ? resolveNamedVoiceProfile(value.profileId).id : isMapped ? matched.id : DEFAULT_VOICE_PROFILE_ID,
      voiceId: explicitVoice,
      language: value.language || matched.locale || "tr-TR",
      ttsProvider: value.ttsProvider ?? "elevenlabs"
    };
  }
  if (value.profileId) {
    const resolved = resolveNamedVoiceProfile(value.profileId);
    let voiceId = explicitVoice || profileTtsVoiceId(resolved, tts);
    if (tts === "elevenlabs" && OPENAI_SHORT_VOICE_IDS.has(voiceId)) {
      voiceId = profileTtsVoiceId(resolved, "elevenlabs");
    }
    if (value.ttsProvider === "openai" && looksLikeElevenLabsLibraryId) {
      voiceId = profileTtsVoiceId(resolved, "openai");
    }
    return {
      ...value,
      profileId: resolved.id,
      voiceId,
      language: value.language || resolved.locale,
      ttsProvider: value.ttsProvider ?? tts
    };
  }
  if (explicitVoice) {
    const matched = resolveNamedVoiceProfileByProviderVoice(explicitVoice);
    const isMapped = matched.provider.openaiVoiceId === explicitVoice || matched.provider.elevenlabsVoiceId === explicitVoice;
    return {
      ...value,
      profileId: isMapped ? matched.id : DEFAULT_VOICE_PROFILE_ID,
      voiceId: explicitVoice,
      language: value.language || (isMapped ? matched.locale : "tr-TR"),
      ttsProvider: value.ttsProvider ?? tts
    };
  }
  const fallback = resolveNamedVoiceProfile(DEFAULT_VOICE_PROFILE_ID);
  return {
    ...value,
    profileId: fallback.id,
    voiceId: profileTtsVoiceId(fallback, tts),
    language: value.language || fallback.locale,
    ttsProvider: value.ttsProvider ?? tts
  };
});
var createAgentRequestSchema = z11.object({
  workspaceId: z11.string().min(1).max(200),
  name: z11.string().min(2).max(160),
  /** Optional system template id from GET /v1/agent-templates */
  templateId: z11.string().min(2).max(80).optional(),
  /** When set with templateId, draft config is prefilled server-side. */
  brainId: z11.string().min(1).max(200).optional()
});
var agentInterviewMessageSchema = z11.object({
  role: z11.enum(["assistant", "user"]),
  content: z11.string().min(1).max(4e3)
});
var interviewAgentDraftSchema = z11.object({
  name: z11.string().min(2).max(160),
  role: z11.string().min(2).max(200),
  persona: z11.string().min(2).max(400),
  instructions: z11.string().min(10).max(8e3),
  firstMessage: z11.string().min(8).max(1e3),
  channelMode: agentChannelModeSchema,
  language: z11.string().min(2).max(35),
  blockedTopics: z11.array(z11.string().min(1).max(80)).max(20),
  capabilities: agentCapabilitiesSchema
});
var interviewAgentTurnRequestSchema = z11.object({
  workspaceId: z11.string().min(1).max(200),
  locale: z11.enum(["tr", "en"]).optional(),
  messages: z11.array(agentInterviewMessageSchema).max(24),
  answer: z11.string().min(1).max(4e3).optional()
});
var interviewAgentTurnResponseSchema = z11.object({
  status: z11.enum(["ask", "ready"]),
  question: z11.string().min(1).max(1e3).optional(),
  progress: z11.number().int().min(0).max(100),
  draft: interviewAgentDraftSchema.optional()
});
var agentSttProviderIds = ["auto", "openai", "deepgram"];
var agentSttProviderSchema = z11.enum(agentSttProviderIds);
var agentSttModelIds = [
  "whisper-1",
  "gpt-4o-mini-transcribe",
  "nova-3",
  "nova-2",
  "nova-2-general"
];
var agentSttModelSchema = z11.enum(agentSttModelIds);
var AGENT_STT_PROVIDER_OPTIONS = [
  {
    id: "auto",
    label: "Otomatik (\xF6nerilen)",
    hint: "PC demo \u2192 OpenAI Whisper \xB7 PSTN canl\u0131 \u2192 Deepgram"
  },
  {
    id: "openai",
    label: "OpenAI transcription",
    hint: "Batch STT (WAV/webm/mp3). PC demo ve dosya turlar\u0131."
  },
  {
    id: "deepgram",
    label: "Deepgram",
    hint: "Canl\u0131 / streaming PCM. Telefon hatt\u0131 i\xE7in ideal."
  }
];
var AGENT_STT_MODEL_OPTIONS = [
  {
    id: "whisper-1",
    provider: "openai",
    label: "whisper-1",
    hint: "Dayan\u0131kl\u0131 \xB7 k\u0131sa/zay\u0131f taray\u0131c\u0131 sesi"
  },
  {
    id: "gpt-4o-mini-transcribe",
    provider: "openai",
    label: "gpt-4o-mini-transcribe",
    hint: "Daha yeni OpenAI STT"
  },
  {
    id: "nova-3",
    provider: "deepgram",
    label: "nova-3",
    hint: "Deepgram g\xFCncel \xB7 TR/EN iyi"
  },
  {
    id: "nova-2",
    provider: "deepgram",
    label: "nova-2",
    hint: "Deepgram klasik"
  },
  {
    id: "nova-2-general",
    provider: "deepgram",
    label: "nova-2-general",
    hint: "Deepgram genel"
  }
];
var agentLlmModelIds = [
  "gpt-4o-mini",
  "gpt-4.1-mini",
  "gpt-4o",
  "gpt-4.1",
  "gpt-5-mini",
  "gpt-5",
  "gpt-5.4",
  "gpt-5.5",
  "gpt-5.5-pro"
];
var agentLlmModelSchema = z11.enum(agentLlmModelIds);
var agentVoiceRuntimeIds = ["pipeline", "pipeline_jev", "openai_realtime"];
var agentVoiceRuntimeSchema = z11.enum(["pipeline", "legacy", "pipeline_jev", "openai_realtime"]).transform((value) => value === "legacy" ? "pipeline" : value);
var usesJevCandidateSelection = (runtime) => String(runtime ?? "").trim().toLowerCase() === "pipeline_jev";
var AGENT_VOICE_RUNTIME_OPTIONS = [
  {
    id: "pipeline",
    label: "Pipeline (STT \u2192 LLM \u2192 TTS)",
    hint: "Mevcut telefon hatt\u0131 \xB7 Deepgram + LLM + ElevenLabs/OpenAI TTS"
  },
  {
    id: "pipeline_jev",
    label: "Pipeline + Jev se\xE7ici",
    hint: "Ayn\u0131 telefon hatt\u0131 \xB7 LLM birka\xE7 aday yazar \xB7 Jev skorlar \xB7 en iyisi sese gider"
  },
  {
    id: "openai_realtime",
    label: "OpenAI Realtime",
    hint: "U\xE7tan uca ses \xB7 gpt-realtime-2.1 dinler ve konu\u015Fur"
  }
];
var agentRealtimeModelIds = ["gpt-realtime-2.1"];
var agentRealtimeModelSchema = z11.enum(agentRealtimeModelIds);
var DEFAULT_AGENT_REALTIME_MODEL = "gpt-realtime-2.1";
var AGENT_REALTIME_MODEL_OPTIONS = [
  {
    id: "gpt-realtime-2.1",
    label: "gpt-realtime-2.1",
    hint: "OpenAI Realtime \xB7 d\xFC\u015F\xFCk gecikme ses modeli"
  }
];
var AGENT_LLM_MODEL_OPTIONS = [
  {
    id: "gpt-4o-mini",
    label: "gpt-4o-mini",
    voiceSafe: true,
    hint: "H\u0131zl\u0131 \xB7 telefon / PC demo \xF6nerilen"
  },
  {
    id: "gpt-4.1-mini",
    label: "gpt-4.1-mini",
    voiceSafe: true,
    hint: "H\u0131zl\u0131 \xB7 biraz daha g\xFC\xE7l\xFC mini"
  },
  {
    id: "gpt-4o",
    label: "gpt-4o",
    voiceSafe: true,
    hint: "Kaliteli \xB7 ses i\xE7in uygun"
  },
  {
    id: "gpt-4.1",
    label: "gpt-4.1",
    voiceSafe: true,
    hint: "Kaliteli \xB7 daha pahal\u0131"
  },
  {
    id: "gpt-5-mini",
    label: "gpt-5-mini",
    voiceSafe: false,
    hint: "Reasoning \xB7 seste gpt-4o-mini\u2019ye d\xFC\u015Fer"
  },
  {
    id: "gpt-5",
    label: "gpt-5",
    voiceSafe: false,
    hint: "Platform varsay\u0131lan\u0131 \xB7 seste gpt-4o-mini\u2019ye d\xFC\u015Fer"
  },
  {
    id: "gpt-5.4",
    label: "gpt-5.4",
    voiceSafe: false,
    hint: "Panaryus YZ varsay\u0131lan\u0131 \xB7 seste gpt-4o-mini\u2019ye d\xFC\u015Fer"
  },
  {
    id: "gpt-5.5",
    label: "gpt-5.5",
    voiceSafe: false,
    hint: "G\xFCncel g\xFC\xE7l\xFC model \xB7 seste gpt-4o-mini\u2019ye d\xFC\u015Fer"
  },
  {
    id: "gpt-5.5-pro",
    label: "gpt-5.5-pro",
    voiceSafe: false,
    hint: "En g\xFC\xE7l\xFC / daha maliyetli \xB7 seste gpt-4o-mini\u2019ye d\xFC\u015Fer"
  }
];
var voiceKnowledgeDepths = ["compact", "standard", "rich"];
var voiceKnowledgeDepthSchema = z11.enum(voiceKnowledgeDepths);
var DEFAULT_VOICE_KNOWLEDGE_DEPTH = "standard";
var VOICE_KNOWLEDGE_BUDGETS = {
  compact: { opening: 320, continue: 180, retrievalLimit: 1 },
  standard: { opening: 700, continue: 600, retrievalLimit: 3 },
  rich: { opening: 1400, continue: 1200, retrievalLimit: 5 }
};
var VOICE_KNOWLEDGE_DEPTH_OPTIONS = [
  {
    id: "compact",
    label: "Dar \xB7 en h\u0131zl\u0131",
    hint: "Tur ba\u015F\u0131na ~180 karakter kan\u0131t, tek par\xE7a. En d\xFC\u015F\xFCk gecikme, en az cevaplanan soru."
  },
  {
    id: "standard",
    label: "Orta \xB7 \xF6nerilen",
    hint: "Tur ba\u015F\u0131na ~600 karakter, 3 par\xE7a. \xDCcret/s\xFCre\xE7 gibi sorular cevaplanabilir."
  },
  {
    id: "rich",
    label: "Geni\u015F \xB7 en \xE7ok bilgi",
    hint: "Tur ba\u015F\u0131na ~1200 karakter, 5 par\xE7a. Bilgi yo\u011Fun i\u015Fler i\xE7in; tur ba\u015F\u0131na birka\xE7 y\xFCz ms ekler."
  }
];
var resolveVoiceKnowledgeBudget = (depth, phase = "continue") => {
  const parsed = voiceKnowledgeDepthSchema.safeParse((depth ?? "").trim());
  const key = parsed.success ? parsed.data : DEFAULT_VOICE_KNOWLEDGE_DEPTH;
  const budget = VOICE_KNOWLEDGE_BUDGETS[key];
  return Object.freeze({
    promptChars: phase === "opening" ? budget.opening : budget.continue,
    retrievalLimit: budget.retrievalLimit
  });
};
var CONVERSATION_STYLE_OPTIONS = [
  {
    id: "discovery",
    label: "Ke\u015Fif \u2014 soran",
    hint: "Anlatmaz, sorar. Giden arama ve \xF6n eleme i\xE7in: m\xFC\u015Fterinin bug\xFCn nas\u0131l \xE7al\u0131\u015Ft\u0131\u011F\u0131n\u0131 \xF6\u011Frenir."
  },
  {
    id: "informative",
    label: "Bilgilendirme \u2014 a\xE7\u0131klayan",
    hint: "Soruyu net cevaplar, 2\u20133 c\xFCmle kurar, sonra tek soru sorar. Gelen \xE7a\u011Fr\u0131 ve destek i\xE7in."
  },
  {
    id: "concise",
    label: "K\u0131sa \u2014 en h\u0131zl\u0131",
    hint: "Tek c\xFCmle. Y\xFCksek hacimli, k\u0131sa do\u011Frulama aramalar\u0131 i\xE7in."
  }
];
var agentDraftConfigSchema = z11.object({
  role: z11.string().min(2).max(240),
  persona: z11.string().min(2).max(2e3),
  /** Optional catalog id (A13); persona text remains source of truth at runtime. */
  personalityPresetId: personalityPresetIdSchema.optional(),
  instructions: z11.string().min(10).max(2e4),
  /** Spoken / chat opening line; supports {{business_name}} {{agent_name}} {{purpose}} */
  firstMessage: z11.string().min(1).max(2e3).optional(),
  channelMode: agentChannelModeSchema.default("omni"),
  capabilities: agentCapabilitiesSchema.default({
    handoff: true,
    endConversation: true,
    leadCreate: false,
    callbackSchedule: false,
    appointmentBooking: false,
    caseCreate: false,
    messagingWhatsappTemplate: false,
    messagingSmsPack: false
  }),
  /** Template id used at creation (audit/UX only). */
  templateId: z11.string().min(2).max(80).optional(),
  /**
   * Linear playbook (Wave B). When compileIntoInstructions is true, save merges
   * a `## Playbook (linear)` block into instructions for the existing runtime.
   */
  playbook: agentPlaybookSchema.optional().nullable(),
  /**
   * Public webchat widget branding / hours / domain allowlist (Wave C).
   * Published with the agent version; returned on webchat session start.
   */
  widget: agentWidgetConfigSchema.optional().nullable(),
  modelPolicy: z11.string().min(1).max(120).default("grounded-default"),
  /**
   * Per-agent OpenAI chat model. When omitted, runtime uses plan/profile then platform OPENAI_MODEL.
   * Pipeline (legacy) voice turns still remap reasoning models via pickVoiceLlmModel.
   * openai_realtime does not apply that gpt-5* → gpt-4o-mini remap; it uses realtimeModel.
   */
  llmModel: agentLlmModelSchema.optional(),
  /**
   * Live-phone runtime. Default pipeline = existing STT → LLM → TTS.
   * openai_realtime is a separate OpenAI Realtime voice session (not the pipeline).
   */
  voiceRuntime: agentVoiceRuntimeSchema.optional().default("pipeline"),
  /**
   * Realtime model when voiceRuntime is openai_realtime. Ignored on pipeline.
   * Live use still requires CENTEROS_REALTIME_VOICE_V1 + workspace allowlist.
   */
  realtimeModel: agentRealtimeModelSchema.optional(),
  /**
   * Per-agent STT backend. `auto` keeps channel-aware defaults (file→OpenAI, live PCM→Deepgram).
   * Secondary when voiceRuntime is openai_realtime (Realtime model listens).
   */
  sttProvider: agentSttProviderSchema.optional().default("auto"),
  /** Optional STT model override (whisper-1, nova-3, …). Provider-specific. */
  sttModel: agentSttModelSchema.optional(),
  /**
   * Knowledge source for grounded answers (webchat + general):
   * - brain: published Business Brain retrieval (default)
   * - inline: operator-pasted knowledge pack (no BB/PM retrieval)
   * - none: role/persona/instructions only
   */
  knowledgeMode: z11.enum(["brain", "inline", "none"]).default("brain"),
  /**
   * Instant knowledge pack when knowledgeMode=inline.
   * Injected into the system prompt each turn (not vector retrieval).
   */
  inlineKnowledge: z11.string().max(5e4).optional(),
  /**
   * Voice-only knowledge strategy (does not change webchat knowledgeMode):
   * - brain: per-turn Business Brain retrieve (current default — safe rollback)
   * - pack: publish-time compiled voiceCompanyPack, no per-turn retrieve
   * - none: role/persona/instructions only on phone
   * - pack_with_fallback: pack first; single retrieve only on real knowledge questions
   */
  voiceKnowledgeMode: z11.enum(["brain", "pack", "none", "pack_with_fallback"]).default("brain").optional(),
  /**
   * How this agent carries a conversation: ask-first discovery, full answers,
   * or shortest possible. One business runs several agents with different jobs,
   * so the shape of a reply is an operator choice, not a platform constant.
   */
  conversationStyle: z11.enum(["discovery", "informative", "concise"]).optional(),
  /**
   * Operator-owned recovery lines, in the agent's own language and sector
   * voice. Spoken when a candidate is vetoed (repetition/filler) and as the
   * dead-air breaker. Replaces the platform's built-in locale pool when set —
   * the platform is multi-language and multi-sector, so relational wording is
   * an operator choice, not a platform constant. Claim-free lines only; they
   * still pass the emit gates and the turn guard (a repeated line is skipped).
   */
  voiceRecoveryLines: z11.array(z11.string().min(3).max(160)).max(6).optional(),
  /**
   * How much retrieved knowledge the phone prompt may carry per turn.
   * compact keeps the old 180/320-character clip; standard and rich widen it.
   */
  voiceKnowledgeDepth: voiceKnowledgeDepthSchema.default("standard").optional(),
  /**
   * Compiled company voice identity (publish-time). Used when voiceKnowledgeMode is pack*.
   * Keep short (~800–1500 chars) for low llmMs.
   */
  voiceCompanyPack: z11.string().max(4e3).optional(),
  /** sha256 hex of pack source material for pin/debug. */
  voiceCompanyPackHash: z11.string().max(80).optional(),
  /** ISO timestamp when pack was compiled. */
  voiceCompanyPackAt: z11.string().optional(),
  /** Published brain version used when compiling the pack (if any). */
  voiceCompanyPackBrainVersion: z11.number().int().positive().optional(),
  /** Required when knowledgeMode=brain. Optional pin-only for other modes at publish. */
  brainId: z11.string().min(1).optional(),
  allowedTools: z11.array(z11.string().min(1).max(160)).max(20).default([]),
  blockedTopics: z11.array(z11.string().min(1).max(160)).max(50).default([]),
  handoffEnabled: z11.boolean().default(true),
  maxResponseCharacters: z11.number().int().min(200).max(8e3).default(2e3),
  /**
   * PR-A generic conversation OS config. Optional for agents that do not use CenterOS.
   * Stored as JSON on the agent. Not compiled into linear playbook.
   *
   * Draft update: omitted field preserves the existing value. A valid object
   * replaces control. Omission is never deletion. Ordinary Studio Save must not
   * remove CenterOS control; there is no implicit null/delete path on this schema.
   */
  conversationControl: agentConversationControlSchema.optional(),
  voice: voiceProfileSchema.default({
    profileId: DEFAULT_VOICE_PROFILE_ID,
    language: "tr-TR",
    voiceId: "coral",
    ttsProvider: "openai",
    recordingEnabled: true,
    recordingNotice: "This call may be recorded for quality purposes.",
    interruptionEnabled: true,
    maxCallSeconds: 1800
  })
}).superRefine((value, ctx) => {
  const mode = value.knowledgeMode ?? "brain";
  if (mode === "brain" && !value.brainId?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "BRAIN_ID_REQUIRED",
      path: ["brainId"]
    });
  }
  if (mode === "inline" && !(value.inlineKnowledge?.trim().length ?? 0)) {
    ctx.addIssue({
      code: "custom",
      message: "INLINE_KNOWLEDGE_REQUIRED",
      path: ["inlineKnowledge"]
    });
  }
  for (const issue of validateConversationControl(value.conversationControl)) {
    ctx.addIssue({
      code: "custom",
      message: issue.message,
      path: ["conversationControl", ...issue.path.split(".")]
    });
  }
});
var updateAgentDraftRequestSchema = agentDraftConfigSchema.extend({
  expectedDraftVersion: z11.number().int().positive()
});
var agentViewSchema = z11.object({
  id: z11.string(),
  workspaceId: z11.string(),
  name: z11.string(),
  status: z11.enum(["draft", "active", "archived"]),
  draftVersion: z11.number().int().positive(),
  publishedVersion: z11.number().int().positive().nullable(),
  config: agentDraftConfigSchema.nullable(),
  createdAt: z11.iso.datetime({ offset: true })
});
var agentListSchema = z11.object({
  items: z11.array(agentViewSchema),
  nextCursor: z11.string().nullable(),
  /** Live workspace agent count (all statuses in agents table). */
  usage: z11.number().int().nonnegative(),
  /** Plan cap; null = unlimited (missing entitlement / enforcement none). */
  limit: z11.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema.nullable()
});
var publishAgentRequestSchema = z11.object({
  expectedDraftVersion: z11.number().int().positive()
});
var publishAgentResponseSchema = z11.object({
  agentId: z11.string(),
  agentVersionId: z11.string(),
  deploymentId: z11.string(),
  version: z11.number().int().positive(),
  brainId: z11.string(),
  brainVersion: z11.number().int().positive(),
  webchatKey: z11.string().min(20),
  publishedAt: z11.iso.datetime({ offset: true })
});
var simulateAgentRequestSchema = z11.object({
  message: z11.string().min(1).max(8e3),
  clientMessageId: z11.string().min(1).max(200),
  conversationId: z11.string().optional(),
  ...legacySpeechProtectionRejectionFields
});
var simulateScenarioRequestSchema = z11.object({
  /** Customer lines, spoken in order. */
  turns: z11.array(z11.string().min(1).max(2e3)).min(1).max(20),
  /** Reuse an earlier scenario conversation instead of starting a new one. */
  conversationId: z11.string().optional(),
  label: z11.string().min(1).max(120).optional()
});
var conversationQualityReportSchema = z11.object({
  turns: z11.number().int().min(0),
  blockedInventedClaims: z11.number().int().min(0),
  blockedClaimSamples: z11.array(z11.string()),
  emptyReplies: z11.number().int().min(0),
  repeatedReplies: z11.number().int().min(0),
  recoveredTurns: z11.number().int().min(0),
  answeredQuestions: z11.number().int().min(0),
  unansweredQuestions: z11.number().int().min(0),
  verdict: z11.enum(["clean", "attention", "failing"])
});
var simulateScenarioResponseSchema = z11.object({
  conversationId: z11.string(),
  transcript: z11.array(
    z11.object({
      user: z11.string(),
      assistant: z11.string(),
      emitDecision: z11.enum(["ALLOW", "RECOVERY", "BLOCK"]),
      speechSource: z11.enum(["candidate", "safe_fallback", "recovery", "fail_closed"]),
      blockedClaims: z11.array(z11.string())
    })
  ),
  report: conversationQualityReportSchema
});
var citationSchema = z11.object({
  knowledgeItemId: z11.string(),
  knowledgeVersionId: z11.string(),
  sourceId: z11.string().nullable(),
  sourceLabel: z11.string(),
  content: z11.string()
});
var runtimeResponseSchema = z11.object({
  conversationId: z11.string(),
  messageId: z11.string(),
  text: z11.string(),
  status: z11.enum(["active", "waiting_for_human", "ended"]),
  citations: z11.array(citationSchema),
  usage: z11.object({
    inputTokens: z11.number().int().nonnegative().nullable(),
    outputTokens: z11.number().int().nonnegative().nullable(),
    status: z11.enum(["reported", "unsupported", "missing"]).optional()
  }),
  providerCall: z11.object({
    requestedModel: z11.string(),
    resolvedModel: z11.string().nullable(),
    provider: z11.string(),
    providerRequestId: z11.string().nullable(),
    httpStatus: z11.number().int().nullable(),
    startedAt: z11.string(),
    finishedAt: z11.string(),
    wallTimeMs: z11.number().nonnegative(),
    outcome: z11.enum(["PROVIDER_SUCCESS", "PROVIDER_ERROR", "ADAPTER_PARSE_FAILURE"]),
    errorCode: z11.string().nullable(),
    usageStatus: z11.enum(["reported", "unsupported", "missing"])
  }).optional(),
  recoveryFallbackUsed: z11.boolean().optional(),
  speechFailureClass: z11.enum([
    "MODEL_OUTPUT_FAILURE",
    "PROVIDER_FAILURE",
    "ADAPTER_FAILURE",
    "CENTEROS_POLICY_FAILURE",
    "CENTEROS_STATE_FAILURE",
    "CENTEROS_TOOL_FAILURE",
    "RECOVERY_RESPONSE",
    "NONE"
  ]).optional()
});
var webchatSessionRequestSchema = z11.object({
  deploymentKey: z11.string().min(20).max(300),
  visitorId: z11.string().min(1).max(200).optional()
});
var webchatSessionResponseSchema = z11.object({
  token: z11.string().min(20),
  conversationId: z11.string(),
  agentName: z11.string(),
  expiresAt: z11.iso.datetime({ offset: true }),
  /** Theme / hours / launcher from published agent widget config. */
  widget: agentWidgetConfigSchema.optional().nullable()
});
var webchatMessageRequestSchema = z11.object({
  clientMessageId: z11.string().min(1).max(200),
  content: z11.string().min(1).max(8e3),
  ...legacySpeechProtectionRejectionFields
});
var streamEventSchema = z11.discriminatedUnion("type", [
  z11.object({ type: z11.literal("delta"), text: z11.string() }),
  z11.object({ type: z11.literal("completed"), response: runtimeResponseSchema }),
  z11.object({ type: z11.literal("error"), code: z11.string() })
]);

// src/conversation-turn-agenda.ts
var AGENDA_CUSTOMER_NEEDS = [
  "acknowledge_end_request",
  "answer_question",
  "answer_meta_question",
  "handle_correction",
  "clarify_own_question",
  "handle_objection",
  "fulfill_commitment",
  "acknowledge",
  "advance_business_goal",
  "wait",
  "recover_silence"
];
var AGENDA_MOVES = [
  "acknowledge_end",
  "answer_question",
  "handle_correction",
  "clarify",
  "handle_objection",
  "fulfill_commitment",
  "explain",
  "invite_reaction",
  "ask_for_fact",
  "confirm_fact",
  "acknowledge",
  "propose_next_step",
  "wait_silence",
  "reengage_user",
  "offer_continuation",
  "check_connection",
  "rephrase_pending_question",
  "offer_summary",
  "safe_close"
];
var RECOVERY_AGENDA_MOVES = [
  "wait_silence",
  "reengage_user",
  "offer_continuation",
  "check_connection",
  "rephrase_pending_question",
  "offer_summary",
  "safe_close"
];
var isRecoveryAgendaMove = (value) => RECOVERY_AGENDA_MOVES.includes(value);
var RECOVERY_AGENDA_REALIZATION_CLASSES = [
  "closed_template",
  "contextual_realizer",
  "no_speech"
];
var TURNAGENDA_CAN_GRANT_RECOVERY_PERMISSION = false;
var CONVERSATIONAL_COMMITMENT_TYPES_V1 = [
  "explain",
  "inform",
  "return_to_question",
  "clarify",
  "provide_requested_detail"
];
var AGENDA_TURN_INTENTS = [
  "DIRECT_ANSWER",
  "USER_QUESTION",
  "META_QUESTION",
  "CLARIFICATION_REQUEST",
  "OBJECTION",
  "CORRECTION",
  "END_REQUEST",
  "NORMAL_FACT_REPLY",
  "AMBIGUOUS"
];

// src/conversation-policy.ts
var COMMUNICATIVE_ACTS = [
  "QUESTION",
  "CLARIFICATION",
  "META",
  "OBJECTION",
  "REFUSAL",
  "DEFERRAL",
  "ACKNOWLEDGEMENT",
  "POLAR_ANSWER",
  "EXPLICIT_AGREEMENT",
  "HESITATION",
  "CLOSE",
  "CONTINUATION_REQUEST",
  "OFF_TOPIC",
  "CORRECTION",
  "ASSERTIVE_BUSINESS_REPLY",
  "SILENCE_EVENT",
  "UNKNOWN"
];
var ACK_CLASSES = [
  "ACK_NONE",
  "ACK_RECEIPT",
  "ACK_CONTINUE",
  "ACK_COMMITMENT_ACCEPT"
];
var ACK_STRENGTHS = ["none", "weak", "medium", "strong"];
var BIND_TARGETS = [
  "none",
  "last_explanation",
  "last_explicit_question",
  "pending_commitment",
  "last_business_proposition",
  "unbound"
];
var CLOSE_POLARITIES = ["stay", "defer", "close"];
var CLOSE_STRENGTHS = ["none", "soft", "likely", "explicit"];
var CLOSE_KINDS = [
  "none",
  "farewell",
  "deferral",
  "refusal_to_continue",
  "terminal_hangup_request"
];
var BUSINESS_PROGRESSION_READINESS = ["READY", "HOLD", "PREEMPTED"];
var ASSISTANT_SPEECH_ACTS = [
  "none",
  "explanation",
  "explicit_question",
  "offer_to_explain",
  "acknowledgement",
  "wait",
  "other"
];
var INITIATIVE_FLOORS = ["customer", "assistant", "none"];
var RECOVERY_STEPS = [
  "NONE",
  "WAIT",
  "REENGAGE_USER",
  "OFFER_CONTINUATION",
  "CHECK_CONNECTION",
  "REPHRASE_PENDING_QUESTION",
  "OFFER_SUMMARY",
  "SAFE_CLOSE"
];
var SILENCE_EVENTS = [
  "PHONE_DIGIT_FALSE_EOU",
  "SILENCE_SHORT",
  "SILENCE_ACTIONABLE",
  "SILENCE_LONG",
  "SILENCE_REPEATED",
  "CONNECTION_UNCERTAIN",
  "CHANNEL_IDLE_ACTIONABLE"
];
var POLICY_PRIMARY_NEEDS = [
  "acknowledge_end_request",
  "acknowledge_close",
  "handle_deferral",
  "handle_refusal",
  "answer_question",
  "answer_meta_question",
  "handle_correction",
  "clarify_own_question",
  "handle_objection",
  "fulfill_commitment",
  "acknowledge",
  "recover_silence",
  "advance_business_goal",
  "wait",
  "none"
];
var POLICY_CONFIDENCE_LEVELS = ["low", "medium", "high"];
var POLICY_COMMITMENT_TYPES = ["explain", "inform", "return_to_question", "clarify"];

// src/conversation-continuity.ts
import { z as z12 } from "zod";
var CONVERSATION_TURN_TRIGGERS = ["customer", "continuity_idle"];
var CONTINUITY_IDLE_TURN_HAS_CUSTOMER_CONTENT = false;
var isCustomerTurnEnvelope = (value) => value.trigger === "customer";
var isContinuityIdleTurnEnvelope = (value) => value.trigger === "continuity_idle";
var continuityIdleMustNotUseCustomerSemanticExtraction = (trigger) => trigger === "continuity_idle";
var CONTINUITY_CHANNELS = [
  "voice",
  "webchat",
  "messaging",
  "sms",
  "email"
];
var CONTINUITY_VOICE_RUNTIMES = ["legacy_audiosocket", "realtime"];
var REALTIME_CONTINUITY_V1_ELIGIBLE = false;
var isContinuityVoiceRuntimeV1Eligible = (voiceRuntime) => voiceRuntime === "legacy_audiosocket";
var isRealtimeContinuityV1Eligible = (voiceRuntime) => {
  void voiceRuntime;
  return false;
};
var isContinuityV1WiredChannel = (channel) => channel === "voice";
var CONTINUITY_FIRST_IDLE_SILENCE_EVENTS = [
  "SILENCE_ACTIONABLE",
  "CHANNEL_IDLE_ACTIONABLE"
];
var PLAYBACK_SESSION_KINDS = [
  "customer_turn_tts",
  "greeting_first_message",
  "continuity_tts"
];
var PLAYBACK_SESSION_KINDS_EXCLUDED = [
  "backchannel_ack",
  "cut_resume",
  "tone",
  "ambience",
  "presence_filler",
  "realtime_output",
  "other_non_conversational"
];
var PLAYBACK_SESSION_OUTCOMES = [
  "completed",
  "cancelled",
  "socket_destroyed"
];
var isPlaybackKindWatchEligible = (kind) => PLAYBACK_SESSION_KINDS.includes(kind);
var isPlaybackOutcomeWatchEligible = (outcome) => outcome === "completed";
var isPlaybackSessionFutureWatchEligible = (input) => isPlaybackKindWatchEligible(input.kind) && isPlaybackOutcomeWatchEligible(input.outcome);
var CONTINUITY_WATCH_STATUSES = [
  "idle",
  "armed",
  "firing",
  "in_flight",
  "cancelled",
  "stopped"
];
var CONTINUITY_WATCH_DISPOSITIONS = [
  "rearm",
  "rearm_subsequent_once",
  "stop"
];
var CONTINUITY_MEDIA_EVENTS = [
  "ASSISTANT_PLAYBACK_COMPLETED",
  "ASSISTANT_PLAYBACK_CANCELLED",
  "ASSISTANT_PLAYBACK_SOCKET_DESTROYED",
  "CUSTOMER_AUDIO_START",
  "CUSTOMER_TURN_ACCEPTED",
  "WATCH_FIRED",
  "RECOVERY_PLAYBACK_COMPLETED",
  "RECOVERY_PLAYBACK_CANCELLED",
  "RECOVERY_PLAYBACK_SOCKET_DESTROYED",
  "IDLE_TURN_CANCELLED_BEFORE_PLAY"
];
var CONTINUITY_MEDIA_EVENTS_RESERVED = [
  "CONNECTION_UNCERTAIN_MEDIA",
  "PHONE_DIGIT_FALSE_EOU_MEDIA"
];
var CONTINUITY_CONVERSATION_EVENTS = [
  "CONVERSATION_IDLE",
  "CONVERSATION_IDLE_CANCELLED",
  "CONVERSATION_IDLE_SUPERSEDED",
  "CONVERSATION_IDLE_SKIPPED"
];
var CONTINUITY_OBSERVABILITY_EVENTS = [
  "conversation_continuity.watch_armed",
  "conversation_continuity.watch_cancelled",
  "conversation_continuity.idle_fired",
  "conversation_continuity.idle_turn_received",
  "conversation_continuity.idle_skipped",
  "conversation_continuity.idle_superseded",
  "conversation_continuity.idle_duplicate",
  "conversation_continuity.policy_evaluated",
  "conversation_continuity.policy_skipped",
  "conversation_continuity.policy_failed",
  "conversation_continuity.speech_emitted",
  "conversation_continuity.speech_suppressed",
  "conversation_continuity.playback_completed",
  "conversation_continuity.playback_cancelled",
  "conversation_continuity.fail_closed",
  "conversation_continuity.safe_close_proposed",
  "conversation_continuity.recovery_agenda_derived",
  "conversation_continuity.recovery_agenda_skipped",
  "conversation_continuity.recovery_agenda_failed",
  "conversation_continuity.recovery_candidate_generated",
  "conversation_continuity.recovery_candidate_verified",
  "conversation_continuity.recovery_candidate_rejected",
  "conversation_continuity.recovery_speech_emitted",
  "conversation_continuity.recovery_playback_completed",
  "conversation_continuity.recovery_playback_completed_local",
  "conversation_continuity.recovery_playback_committed",
  "conversation_continuity.recovery_playback_commit_failed",
  "conversation_continuity.recovery_playback_cancelled"
];
var CONTINUITY_IDLE_SKIP_REASONS = [
  "flag_off",
  "workspace_not_allowlisted",
  "channel_or_runtime_ineligible",
  "duration_unconfigured",
  "playback_kind_ineligible",
  "playback_outcome_not_completed",
  "realtime_runtime"
];
var CONTINUITY_DURATION_ENV_KEYS = {
  idleActionableMs: "CENTEROS_CONVO_CONTINUITY_VOICE_IDLE_ACTIONABLE_MS",
  idleSubsequentMs: "CENTEROS_CONVO_CONTINUITY_VOICE_IDLE_SUBSEQUENT_MS",
  safeCloseWaitMs: "CENTEROS_CONVO_CONTINUITY_VOICE_SAFE_CLOSE_WAIT_MS"
};
var CONTINUITY_CONNECTION_UNCERTAIN_MS_ENV = "CENTEROS_CONVO_CONTINUITY_VOICE_CONNECTION_UNCERTAIN_MS";
var HANGUP_AND_EOU_TIMER_FIELDS = [
  "endCallOnSilence",
  "silenceTimeoutMs",
  "endCallSilenceMs",
  "turnSilenceMs",
  "turnTimeoutSeconds"
];
var CONTINUITY_DURATION_MS_MIN = 1;
var CONTINUITY_DURATION_MS_MAX = 6e5;
var channelContinuityPolicyFor = (channel, durationsMs = null) => {
  switch (channel) {
    case "voice":
      return {
        schemaVersion: 1,
        channel: "voice",
        eligibleForOrdinarySilenceRecovery: true,
        realizeWaitSpeech: false,
        durationsMs,
        firstIdleSilenceEvent: "SILENCE_ACTIONABLE"
      };
    case "webchat":
    case "messaging":
      return {
        schemaVersion: 1,
        channel,
        eligibleForOrdinarySilenceRecovery: true,
        realizeWaitSpeech: false,
        durationsMs: null,
        firstIdleSilenceEvent: "CHANNEL_IDLE_ACTIONABLE"
      };
    case "sms":
    case "email":
      return {
        schemaVersion: 1,
        channel,
        eligibleForOrdinarySilenceRecovery: false,
        realizeWaitSpeech: false,
        durationsMs: null,
        firstIdleSilenceEvent: null
      };
  }
};
var parseContinuityDurationMs = (raw) => {
  if (raw === void 0) return { ok: false, reason: "missing" };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, reason: "missing" };
  if (!/^-?\d+$/u.test(trimmed)) {
    const asNumber = Number(trimmed);
    if (!Number.isFinite(asNumber)) return { ok: false, reason: "non_numeric" };
    if (!Number.isInteger(asNumber)) return { ok: false, reason: "not_integer" };
    return { ok: false, reason: "non_numeric" };
  }
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(value)) return { ok: false, reason: "non_numeric" };
  if (value < CONTINUITY_DURATION_MS_MIN) return { ok: false, reason: "not_positive" };
  if (value > CONTINUITY_DURATION_MS_MAX) return { ok: false, reason: "above_max" };
  return { ok: true, value };
};
var parseRequiredDurationKey = (env, envKey) => parseContinuityDurationMs(env[envKey]);
var parseChannelContinuityDurationsFromEnv = (env = {}) => {
  const actionable = parseRequiredDurationKey(
    env,
    CONTINUITY_DURATION_ENV_KEYS.idleActionableMs
  );
  if (!actionable.ok) {
    return {
      ok: false,
      reason: "duration_unconfigured",
      failedKey: CONTINUITY_DURATION_ENV_KEYS.idleActionableMs,
      detail: actionable.reason
    };
  }
  const subsequent = parseRequiredDurationKey(
    env,
    CONTINUITY_DURATION_ENV_KEYS.idleSubsequentMs
  );
  if (!subsequent.ok) {
    return {
      ok: false,
      reason: "duration_unconfigured",
      failedKey: CONTINUITY_DURATION_ENV_KEYS.idleSubsequentMs,
      detail: subsequent.reason
    };
  }
  const safeCloseRaw = env[CONTINUITY_DURATION_ENV_KEYS.safeCloseWaitMs];
  let safeCloseWaitMs = null;
  if (safeCloseRaw !== void 0 && safeCloseRaw.trim().length > 0) {
    const parsedSafeClose = parseContinuityDurationMs(safeCloseRaw);
    if (!parsedSafeClose.ok) {
      return {
        ok: false,
        reason: "duration_unconfigured",
        failedKey: CONTINUITY_DURATION_ENV_KEYS.safeCloseWaitMs,
        detail: parsedSafeClose.reason
      };
    }
    safeCloseWaitMs = parsedSafeClose.value;
  }
  return {
    ok: true,
    durationsMs: {
      idleActionableMs: actionable.value,
      idleSubsequentMs: subsequent.value,
      safeCloseWaitMs
    }
  };
};
var classifyContinuityWatchEligibility = (input) => {
  if (!isContinuityV1WiredChannel(input.channel)) {
    return { eligible: false, reason: "channel_or_runtime_ineligible" };
  }
  if (!isContinuityVoiceRuntimeV1Eligible(input.voiceRuntime)) {
    return { eligible: false, reason: "realtime_runtime" };
  }
  if (!isPlaybackKindWatchEligible(input.playbackKind)) {
    return { eligible: false, reason: "playback_kind_ineligible" };
  }
  if (!isPlaybackOutcomeWatchEligible(input.playbackOutcome)) {
    return { eligible: false, reason: "playback_outcome_not_completed" };
  }
  if (!input.durationsMs) {
    return { eligible: false, reason: "duration_unconfigured" };
  }
  return { eligible: true, reason: "eligible" };
};
var CONTINUITY_IDLE_CLIENT_MESSAGE_ID_PREFIX = "continuity_idle:";
var isNonEmptyId = (value) => value.trim().length > 0;
var isNonNegativeInt = (value) => Number.isInteger(value) && Number.isFinite(value) && value >= 0;
var normalizeContinuityIdempotencyIdentity = (input) => {
  if (!isNonEmptyId(input.conversationId) || !isNonEmptyId(input.assistantTurnId)) {
    return null;
  }
  if (!isNonNegativeInt(input.continuityGeneration) || !isNonNegativeInt(input.recoveryAttempt)) {
    return null;
  }
  return {
    conversationId: input.conversationId.trim(),
    assistantTurnId: input.assistantTurnId.trim(),
    continuityGeneration: input.continuityGeneration,
    recoveryAttempt: input.recoveryAttempt
  };
};
var buildContinuityIdempotencyKey = (input) => {
  const normalized = normalizeContinuityIdempotencyIdentity(input);
  if (!normalized) return null;
  return [
    normalized.conversationId,
    normalized.assistantTurnId,
    String(normalized.continuityGeneration),
    String(normalized.recoveryAttempt)
  ].join("");
};
var continuityIdleClientMessageId = (idempotencyKey) => `${CONTINUITY_IDLE_CLIENT_MESSAGE_ID_PREFIX}${idempotencyKey}`;
var CONTINUITY_IDLE_FORBIDDEN_AUTHORITY_KEYS = [
  "facts",
  "stage",
  "completion",
  "tools",
  "turnAgenda",
  "recoveryAgenda",
  "recoveryMove",
  "resumeObjective",
  "prbPrimaryKind",
  "prbHasObjective",
  "eligibleActions",
  "nextAction",
  "policyDecision",
  "recoveryStep",
  "hangupAuthorized",
  "conversationEndAuthorized",
  "content",
  "utterance"
];
var continuityIdleRequestHasForbiddenAuthority = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return CONTINUITY_IDLE_FORBIDDEN_AUTHORITY_KEYS.some((key) => key in value);
};
var CONTINUITY_POLICY_EVALUATION_OUTCOMES = [
  "evaluated",
  "skipped",
  "failed",
  "duplicate"
];
var CONTINUITY_APPLICATION_SKIP_REASONS = [
  "flag_off",
  "workspace_not_allowlisted",
  "channel_or_runtime_ineligible",
  "missing_conversation",
  "missing_committed_state",
  "conversation_not_active",
  "ended_conversation",
  "call_not_found",
  "call_identity_mismatch",
  "stale_generation",
  "customer_activity"
];
var CONTINUITY_POLICY_FAILURE_REASONS = [
  "policy_unavailable",
  "malformed_policy_result",
  "required_context_unavailable",
  "state_read_failed",
  "prb_recompute_failed"
];
var CONTINUITY_STALE_GENERATION_TRUST_V1 = "bounded_process_local";
var CONTINUITY_SHADOW_TEXT = "";
var RECOVERY_STEP_TO_AGENDA_MOVE = Object.freeze({
  WAIT: "wait_silence",
  REENGAGE_USER: "reengage_user",
  OFFER_CONTINUATION: "offer_continuation",
  CHECK_CONNECTION: "check_connection",
  REPHRASE_PENDING_QUESTION: "rephrase_pending_question",
  OFFER_SUMMARY: "offer_summary",
  SAFE_CLOSE: "safe_close"
});
var RECOVERY_STEP_TO_AGENDA_NEED = Object.freeze({
  WAIT: "wait",
  REENGAGE_USER: "recover_silence",
  OFFER_CONTINUATION: "recover_silence",
  CHECK_CONNECTION: "recover_silence",
  REPHRASE_PENDING_QUESTION: "recover_silence",
  OFFER_SUMMARY: "recover_silence",
  SAFE_CLOSE: "recover_silence"
});
var RECOVERY_STEP_TO_REALIZATION_CLASS = Object.freeze({
  WAIT: "closed_template",
  REENGAGE_USER: "contextual_realizer",
  OFFER_CONTINUATION: "closed_template",
  CHECK_CONNECTION: "closed_template",
  REPHRASE_PENDING_QUESTION: "contextual_realizer",
  OFFER_SUMMARY: "contextual_realizer",
  SAFE_CLOSE: "closed_template"
});
var isMappedRecoveryStep = (value) => Object.prototype.hasOwnProperty.call(RECOVERY_STEP_TO_AGENDA_MOVE, value);
var CONTINUITY_RECOVERY_AGENDA_OUTCOMES = [
  "derived",
  "skipped",
  "failed"
];
var CONTINUITY_RECOVERY_AGENDA_SKIP_REASONS = [
  "no_policy_turn",
  "recovery_step_none",
  "policy_not_evaluated",
  "untrusted_caller_input"
];
var CONTINUITY_RECOVERY_AGENDA_FAILURE_REASONS = [
  "unknown_recovery_step",
  "malformed_policy_result"
];
var EMPTY_CONTINUITY_RECOVERY_AGENDA = Object.freeze({
  derived: true,
  schemaVersion: 1,
  outcome: "skipped",
  skipReason: "policy_not_evaluated",
  failureReason: null,
  recovery: null
});
var toContinuityRecoveryAgendaSnapshot = (result) => Object.freeze({
  derived: true,
  schemaVersion: 1,
  outcome: result.outcome,
  skipReason: result.skipReason,
  failureReason: result.failureReason,
  recoveryMove: result.recovery?.recoveryMove ?? null,
  realizationClass: result.recovery?.realizationClass ?? null,
  primaryCustomerNeed: result.recovery?.agenda.primary_customer_need ?? null,
  mustDo: [...result.recovery?.agenda.must_do ?? []],
  openQuestion: result.recovery?.descriptiveContext.openQuestion ?? false,
  objectiveKind: result.recovery?.descriptiveContext.objectiveKind ?? null,
  askTargetFactKeyHash: result.recovery?.descriptiveContext.askTargetFactKeyHash ?? null,
  resumeTargetNeed: result.recovery?.descriptiveContext.resumeTargetNeed ?? null,
  pendingCommitment: result.recovery?.descriptiveContext.pendingCommitment ?? false,
  hangupAuthorized: false,
  conversationEndAuthorized: false,
  grantsBusinessProgression: false,
  mayRequestTool: false,
  text: CONTINUITY_SHADOW_TEXT
});
var PLAYBACK_SESSION_KIND_SCHEMA = z12.enum(PLAYBACK_SESSION_KINDS);
var CONTINUITY_CHANNEL_SCHEMA = z12.enum(CONTINUITY_CHANNELS);
var CONTINUITY_FIRST_IDLE_SILENCE_EVENT_SCHEMA = z12.enum(
  CONTINUITY_FIRST_IDLE_SILENCE_EVENTS
);
var internalMediaContinuityIdleRequestSchema = z12.object({
  callId: z12.string().min(1).max(200).optional(),
  mediaStreamId: z12.string().min(8).max(120).optional(),
  clientEventId: z12.string().min(8).max(200),
  trigger: z12.literal("continuity_idle"),
  mediaFact: z12.enum(["WATCH_FIRED"]),
  assistantTurnId: z12.string().min(1).max(200),
  playbackSessionId: z12.string().min(1).max(200).optional(),
  playbackSessionKind: PLAYBACK_SESSION_KIND_SCHEMA,
  continuityGeneration: z12.number().int().nonnegative(),
  recoveryAttempt: z12.number().int().nonnegative(),
  channel: CONTINUITY_CHANNEL_SCHEMA.optional(),
  silenceEvent: CONTINUITY_FIRST_IDLE_SILENCE_EVENT_SCHEMA.optional(),
  conversationId: z12.string().min(1).max(200).optional(),
  tenantId: z12.string().min(1).max(200).optional(),
  workspaceId: z12.string().min(1).max(200).optional()
}).strict().refine((value) => Boolean(value.callId || value.mediaStreamId), {
  message: "CALL_OR_MEDIA_STREAM_REQUIRED"
});
var internalMediaContinuityIdleResponseSchema = z12.object({
  callId: z12.string(),
  conversationId: z12.string().nullable(),
  duplicate: z12.boolean(),
  suppressed: z12.boolean(),
  suppressReason: z12.string().nullable(),
  recoveryStep: z12.enum([
    "NONE",
    "WAIT",
    "REENGAGE_USER",
    "OFFER_CONTINUATION",
    "CHECK_CONNECTION",
    "REPHRASE_PENDING_QUESTION",
    "OFFER_SUMMARY",
    "SAFE_CLOSE"
  ]),
  watchDisposition: z12.enum(CONTINUITY_WATCH_DISPOSITIONS),
  text: z12.string(),
  status: z12.enum(["active", "waiting_for_human", "ended"]),
  tools: z12.tuple([]),
  evaluation: z12.object({
    schemaVersion: z12.literal(1),
    outcome: z12.enum(CONTINUITY_POLICY_EVALUATION_OUTCOMES),
    recoveryStep: z12.enum([
      "NONE",
      "WAIT",
      "REENGAGE_USER",
      "OFFER_CONTINUATION",
      "CHECK_CONNECTION",
      "REPHRASE_PENDING_QUESTION",
      "OFFER_SUMMARY",
      "SAFE_CLOSE"
    ]),
    noPolicyTurn: z12.boolean(),
    failClosed: z12.boolean(),
    silenceEvent: z12.string().nullable(),
    silenceRepeatCount: z12.number().int().nonnegative(),
    openQuestion: z12.boolean(),
    lastNonRecoveryAssistantTurnId: z12.string().nullable(),
    lastNonRecoveryAssistantSpeechAct: z12.string().nullable(),
    prbPrimaryKind: z12.string().nullable(),
    prbHasObjective: z12.boolean(),
    safeCloseProposed: z12.boolean(),
    text: z12.string(),
    hangupAuthorized: z12.literal(false),
    conversationEndAuthorized: z12.literal(false),
    skipReason: z12.string().nullable(),
    failureReason: z12.string().nullable(),
    duplicate: z12.boolean(),
    shadowWouldEmitCount: z12.number().int().nonnegative(),
    recoveryAgenda: z12.object({
      derived: z12.literal(true),
      schemaVersion: z12.literal(1),
      outcome: z12.enum(CONTINUITY_RECOVERY_AGENDA_OUTCOMES),
      skipReason: z12.enum(CONTINUITY_RECOVERY_AGENDA_SKIP_REASONS).nullable(),
      failureReason: z12.enum(CONTINUITY_RECOVERY_AGENDA_FAILURE_REASONS).nullable(),
      recoveryMove: z12.enum([
        "wait_silence",
        "reengage_user",
        "offer_continuation",
        "check_connection",
        "rephrase_pending_question",
        "offer_summary",
        "safe_close"
      ]).nullable(),
      realizationClass: z12.enum(["closed_template", "contextual_realizer", "no_speech"]).nullable(),
      primaryCustomerNeed: z12.string().nullable(),
      mustDo: z12.array(z12.string()),
      openQuestion: z12.boolean(),
      objectiveKind: z12.string().nullable(),
      askTargetFactKeyHash: z12.string().nullable(),
      resumeTargetNeed: z12.enum(["fulfill_commitment", "answer_question", "advance_business_goal"]).nullable(),
      pendingCommitment: z12.boolean(),
      hangupAuthorized: z12.literal(false),
      conversationEndAuthorized: z12.literal(false),
      grantsBusinessProgression: z12.literal(false),
      mayRequestTool: z12.literal(false),
      text: z12.literal("")
    })
  })
});
var CONTINUITY_PLAYBACK_MEDIA_FACTS = [
  "RECOVERY_PLAYBACK_COMPLETED",
  "RECOVERY_PLAYBACK_CANCELLED",
  "RECOVERY_PLAYBACK_SOCKET_DESTROYED",
  "IDLE_TURN_CANCELLED_BEFORE_PLAY"
];
var internalMediaContinuityPlaybackRequestSchema = z12.object({
  callId: z12.string().min(1).max(200).optional(),
  mediaStreamId: z12.string().min(8).max(120).optional(),
  clientEventId: z12.string().min(8).max(200),
  trigger: z12.literal("continuity_idle"),
  mediaFact: z12.enum(CONTINUITY_PLAYBACK_MEDIA_FACTS),
  assistantTurnId: z12.string().min(1).max(200),
  playbackSessionKind: z12.literal("continuity_tts"),
  continuityGeneration: z12.number().int().nonnegative(),
  recoveryAttempt: z12.number().int().nonnegative(),
  outcome: z12.enum(PLAYBACK_SESSION_OUTCOMES),
  conversationId: z12.string().min(1).max(200).optional(),
  tenantId: z12.string().min(1).max(200).optional(),
  workspaceId: z12.string().min(1).max(200).optional()
}).strict().refine((value) => Boolean(value.callId || value.mediaStreamId), {
  message: "CALL_OR_MEDIA_STREAM_REQUIRED"
});
var CONTINUITY_PLAYBACK_COMMIT_STATUSES = [
  "committed",
  "already_committed",
  "not_committed"
];
var internalMediaContinuityPlaybackResponseSchema = z12.object({
  callId: z12.string(),
  conversationId: z12.string().nullable(),
  persisted: z12.boolean(),
  duplicate: z12.boolean(),
  hangupAuthorized: z12.literal(false),
  conversationEndAuthorized: z12.literal(false),
  /** Authoritative persist result. HTTP 200 alone is not a commit ack. */
  committed: z12.boolean(),
  commitStatus: z12.enum(CONTINUITY_PLAYBACK_COMMIT_STATUSES)
});
var CLOSED_RECOVERY_USES_LLM = false;
var SPEECH_LLM_SELECTS_RECOVERY_ACT = false;
var CONTEXTUAL_RECOVERY_CAN_EMIT_WITHOUT_TRUSTED_SEMANTIC_VERIFIER = false;
var SCRIPTED_TEST_CLASSIFIER_CAN_ENABLE_PRODUCTION_EMIT = false;
var SEMANTIC_MODEL_GRANTS_AUTHORITY = false;
var CLOSED_TEMPLATE_SEMANTICS_PREVALIDATED = true;
var UNSUPPORTED_LOCALE_CAN_EMIT_RECOVERY = false;
var RECOVERY_LLM_RETRY_MAX = 1;
var LEXICAL_CHECK_CAN_GRANT_EMIT = false;
var RECOVERY_ASSISTANT_ROW_TAGGED_STRUCTURALLY = true;
var REJECTED_RECOVERY_CANDIDATE_PERSISTED_AS_ASSISTANT_SPEECH = false;
var CUSTOMER_ACTIVITY_CAN_BE_TALKED_OVER_BY_STALE_RECOVERY = false;
var SILENCE_REPEAT_COUNT_INCREMENT_POINT = "RECOVERY_PLAYBACK_COMPLETED_ONLY";
var DUPLICATE_RECOVERY_PLAYBACK_COMPLETION_CAN_DOUBLE_INCREMENT = false;
var RECOVERY_COMPLETED_REARMS_CONTINUITY_WATCH = true;
var SAFE_CLOSE_REARMS_CONTINUITY = false;
var RECOVERY_SPEECH_WRITES_BUSINESS_STATE = false;
var RECOVERY_SPEECH_CAN_REQUEST_TOOL = false;
var RECOVERY_SPEECH_AUTHORIZES_HANGUP = false;
var SHADOW_MODE_CAN_PLAY_RECOVERY_TTS = false;
var CONTEXTUAL_RECOVERY_EMITTED_WITHOUT_COMPARATOR_PASS = false;
var SEMANTIC_MODEL_SELECTED = false;
var SAFE_CLOSE_CALLS_CONVERSATION_END = false;
var SAFE_CLOSE_CALLS_SOCKET_DESTROY = false;

// src/calendar.ts
import { z as z13 } from "zod";
var calendarProviderSchema = z13.enum(["google", "mock"]);
var calendarConnectionStatusSchema = z13.enum(["active", "disabled", "error"]);
var calendarEnvironmentSchema = z13.enum(["sandbox", "production"]);
var upsertCalendarConnectionRequestSchema = z13.object({
  workspaceId: z13.string().min(1).max(200),
  provider: calendarProviderSchema.default("mock"),
  calendarId: z13.string().min(1).max(200).default("primary"),
  accountEmail: z13.string().email().max(200).optional().nullable(),
  /**
   * Google OAuth refresh token for this customer workspace.
   * Write-only: never returned in views. Encrypted at rest.
   */
  refreshToken: z13.string().min(20).max(8e3).optional().nullable(),
  /**
   * @deprecated Dev/smoke only. Production API must reject this field.
   */
  accessToken: z13.string().min(10).max(8e3).optional().nullable(),
  /**
   * Optional legacy ops env key name. Prefer refreshToken (encrypted) for SaaS tenants.
   */
  secretRef: z13.string().max(500).optional().nullable(),
  environment: calendarEnvironmentSchema.optional(),
  enabled: z13.boolean().default(true)
}).superRefine((value, ctx) => {
  if (value.provider === "mock" && value.environment === "production") {
    ctx.addIssue({
      code: "custom",
      message: "MOCK_REQUIRES_SANDBOX",
      path: ["environment"]
    });
  }
  if (value.provider === "google" && !value.refreshToken && !value.secretRef && !value.accessToken) {
  }
});
var calendarConnectionViewSchema = z13.object({
  id: z13.string(),
  workspaceId: z13.string(),
  provider: calendarProviderSchema,
  status: calendarConnectionStatusSchema,
  environment: calendarEnvironmentSchema,
  calendarId: z13.string(),
  accountEmail: z13.string().nullable(),
  /** True when encrypted credentials or mock provider is ready. */
  credentialsConfigured: z13.boolean(),
  lastError: z13.string().nullable(),
  createdAt: z13.iso.datetime({ offset: true }),
  updatedAt: z13.iso.datetime({ offset: true })
});
var calendarConnectionListSchema = z13.object({
  items: z13.array(calendarConnectionViewSchema),
  /**
   * True when platform env has GOOGLE_CALENDAR_CLIENT_ID + CLIENT_SECRET + redirect URI.
   * Never includes secret values — presence-only flag for operator UX.
   */
  platformOAuthConfigured: z13.boolean()
});
var calendarEventResultSchema = z13.object({
  provider: calendarProviderSchema,
  eventId: z13.string(),
  htmlLink: z13.string().url().nullable(),
  calendarId: z13.string(),
  status: z13.enum(["confirmed", "tentative", "cancelled", "mock"]),
  environment: calendarEnvironmentSchema
});
var startGoogleCalendarOAuthRequestSchema = z13.object({
  workspaceId: z13.string().min(1).max(200),
  calendarId: z13.string().min(1).max(200).default("primary"),
  /**
   * Optional post-callback return URL (must match allowed app origins when enforced).
   * Relative paths like `/integrations?tab=calendar` are preferred.
   */
  returnPath: z13.string().min(1).max(500).regex(/^\/[A-Za-z0-9/?#&=_\-.%]*$/u, "RETURN_PATH_MUST_BE_RELATIVE").optional().nullable()
});
var startGoogleCalendarOAuthResponseSchema = z13.object({
  authorizeUrl: z13.string().url(),
  stateExpiresAt: z13.iso.datetime({ offset: true }),
  provider: z13.literal("google"),
  workspaceId: z13.string()
});

// src/agent-tools.ts
import { z as z14 } from "zod";
var internalAgentToolNameSchema = z14.enum([
  "conversation.add_note",
  "handoff.request",
  /**
   * Agent-initiated hangup / end of call. Completes the voice call when present
   * and marks the conversation finished (capability: endConversation).
   */
  "conversation.end",
  /** CRM-lite: durable callback request captured for human/operator follow-up. */
  "callback.schedule",
  /** CRM-lite: appointment request (no external calendar connector in v1). */
  "appointment.book",
  /** CRM-lite: durable lead capture (no external CRM write until connector is installed). */
  "lead.create",
  /** CRM-lite: support/case ticket capture (no external ticketing until connector is installed). */
  "case.create"
]);
var agentToolStatusSchema = z14.enum(["succeeded", "failed", "denied"]);
var agentToolExecutionViewSchema = z14.object({
  id: z14.string().min(1),
  toolName: z14.string().min(1),
  status: agentToolStatusSchema,
  input: z14.record(z14.string(), z14.unknown()),
  output: z14.record(z14.string(), z14.unknown()),
  /**
   * Operator/customer-safe summary. Empty string is valid: e.g. conversation.end
   * succeeds silently so farewell speech is not duplicated (see userSafeToolMessage).
   */
  userSafeMessage: z14.string().max(500),
  errorCode: z14.string().nullable(),
  createdAt: z14.iso.datetime({ offset: true })
});
var agentRuntimeTurnRequestSchema = z14.object({
  conversationId: z14.string().min(1).max(200),
  clientMessageId: z14.string().min(1).max(200),
  content: z14.string().min(1).max(8e3),
  callId: z14.string().min(1).max(200).optional(),
  greetingPlayOutcome: z14.enum(["completed", "cancelled", "socket_destroyed"]).optional(),
  openerSuppressed: z14.boolean().optional(),
  ...legacySpeechProtectionRejectionFields
});
var agentRuntimeTurnResponseSchema = z14.object({
  response: runtimeResponseSchema,
  tools: z14.array(agentToolExecutionViewSchema),
  citations: z14.array(citationSchema)
});
var internalMediaTurnRequestSchema = z14.object({
  callId: z14.string().min(1).max(200).optional(),
  mediaStreamId: z14.string().min(8).max(120).optional(),
  clientEventId: z14.string().min(8).max(200),
  utterance: z14.string().min(1).max(8e3),
  bargeIn: z14.boolean().default(false),
  ...legacySpeechProtectionRejectionFields,
  /**
   * Text the agent was speaking when playback was cancelled (barge-in / cut).
   * Runtime uses this so the next turn resumes instead of restarting.
   */
  unspokenRemainder: z14.string().min(1).max(500).optional(),
  /**
   * When true, skip voice_turn_metrics insert (media-gateway will POST turn-metrics
   * after STT+LLM+TTS with full latency fields).
   */
  deferMetrics: z14.boolean().default(false),
  /**
   * Finalized STT confidence 0..1 from the accepted transcript only.
   * Omit on chat/non-voice. Missing is not 0. Out-of-range / non-finite is rejected.
   */
  sttConfidence: z14.number().min(0).max(1).optional(),
  /** Media-gateway greeting playback outcome. Cancelled/suppressed means purpose was not heard. */
  greetingPlayOutcome: z14.enum(["completed", "cancelled", "socket_destroyed"]).optional(),
  openerSuppressed: z14.boolean().optional()
}).refine((value) => Boolean(value.callId || value.mediaStreamId), {
  message: "CALL_OR_MEDIA_STREAM_REQUIRED"
});
var ms = z14.number().int().min(-12e4).max(12e4).nullable().optional();
var voiceTurnTimelineSchema = z14.object({
  speechEndAt: z14.number().int().positive().nullable().optional(),
  turnDecisionMs: ms,
  sttFinalMs: ms,
  llmFirstTokenMs: ms,
  /** Whole runtime turn done (LLM + gates). First-token arrives in Faz 2 streaming. */
  llmDoneMs: ms,
  ttsFirstByteMs: ms,
  audioStartMs: ms,
  bargeDetectMs: ms,
  bargeStopMs: ms,
  echoPath: z14.enum(["unknown", "none", "cancelled"]).nullable().optional(),
  aecErleDb: z14.number().min(-200).max(200).nullable().optional(),
  aecDelayMs: z14.number().min(0).max(2e3).nullable().optional(),
  playbackOutcome: z14.enum(["completed", "cancelled", "socket_destroyed"]).nullable().optional()
});
var internalMediaTurnMetricsRequestSchema = z14.object({
  callId: z14.string().min(1).max(200).optional(),
  mediaStreamId: z14.string().min(8).max(120).optional(),
  clientEventId: z14.string().min(8).max(200),
  bargeIn: z14.boolean().default(false),
  status: z14.enum(["active", "waiting_for_human", "ended"]).default("active"),
  sttLatencyMs: z14.number().int().nonnegative().max(12e4),
  ttsLatencyMs: z14.number().int().nonnegative().max(12e4),
  llmLatencyMs: z14.number().int().nonnegative().max(12e4).optional(),
  silenceToFlushMs: z14.number().int().nonnegative().max(12e4).optional(),
  stopToFirstAudioMs: z14.number().int().nonnegative().max(12e4).optional(),
  timeline: voiceTurnTimelineSchema.optional()
}).refine((value) => Boolean(value.callId || value.mediaStreamId), {
  message: "CALL_OR_MEDIA_STREAM_REQUIRED"
});
var internalMediaTurnMetricsResponseSchema = z14.object({
  callId: z14.string(),
  recorded: z14.boolean()
});
var internalMediaTurnResponseSchema = z14.object({
  callId: z14.string(),
  conversationId: z14.string(),
  text: z14.string(),
  status: z14.enum(["active", "waiting_for_human", "ended"]),
  tools: z14.array(agentToolExecutionViewSchema),
  citations: z14.array(citationSchema)
});
var internalMediaRecordingRequestSchema = z14.object({
  callId: z14.string().min(1).max(200),
  mediaStreamId: z14.string().min(8).max(120).optional(),
  contentType: z14.enum(["audio/wav", "audio/pcm"]).default("audio/wav"),
  /**
   * Base64-encoded audio body.
   * Full-duplex phone mix (~8kHz mono): allow ~10–12 min so user+AI legs both fit.
   */
  audioBase64: z14.string().min(8).max(18e6),
  durationMs: z14.number().int().nonnegative().max(144e5).optional()
});
var internalMediaRecordingResponseSchema = z14.object({
  callId: z14.string(),
  recordingId: z14.string(),
  storageKey: z14.string(),
  byteSize: z14.number().int().nonnegative(),
  checksumSha256: z14.string().min(32).max(128)
});
var internalMediaCompleteCallRequestSchema = z14.object({
  callId: z14.string().min(1).max(200).optional(),
  mediaStreamId: z14.string().min(8).max(120).optional(),
  reason: z14.enum(["caller_hangup", "agent_hangup", "timeout", "failed"]).default("caller_hangup")
}).refine((value) => Boolean(value.callId || value.mediaStreamId), {
  message: "CALL_OR_MEDIA_STREAM_REQUIRED"
});
var internalMediaCompleteCallResponseSchema = z14.object({
  callId: z14.string(),
  status: z14.enum(["completed", "failed", "already_terminal"])
});

// src/voice.ts
import { z as z18 } from "zod";

// src/dialogue-styles.ts
import { z as z15 } from "zod";
var dialogueStyleSchema = z15.enum([
  "natural_chat",
  "script_strict",
  "pitch_close",
  "sales_discovery"
]);
var DIALOGUE_STYLE_CATALOG = [
  {
    id: "natural_chat",
    label: "Do\u011Fal sohbet (\xF6nerilen)",
    description: "A\xE7\u0131l\u0131\u015Ftan sonra ChatGPT gibi konu\u015Fur; soruya cevap verir, tekrar monolog okumaz.",
    hint: "Serbest diyalog + kampanya \xE7er\xE7evesi"
  },
  {
    id: "script_strict",
    label: "S\u0131k\u0131 script",
    description: "Operat\xF6r notundaki A\xC7ILI\u015E / Evet / Hay\u0131r sat\u0131rlar\u0131na s\u0131k\u0131 ba\u011Fl\u0131 kal\u0131r; az do\u011Fa\xE7lama.",
    hint: "IVR / sabit metin hissi"
  },
  {
    id: "pitch_close",
    label: "Pitch + kapan\u0131\u015F",
    description: "K\u0131sa tan\u0131t\u0131m, ilgilenir misiniz; evet \u2192 not al\u0131p kapat, hay\u0131r \u2192 te\u015Fekk\xFCr kapat. Uzun sohbet yok.",
    hint: "K\u0131sa sat\u0131\u015F aramas\u0131"
  },
  {
    // ADR-0015/0016 DEBT: style id may remain generic; logistics copy must not grow.
    id: "sales_discovery",
    label: "Sat\u0131\u015F ke\u015Ffi",
    description: "\u0130lgi sonras\u0131 1\u20132 ke\u015Fif sorusu sorar (ihtiya\xE7, tonaj, zaman); sonra randevu/not.",
    hint: "Soru sorarak ilerler"
  }
];
var DEFAULT_DIALOGUE_STYLE = "natural_chat";
var isDialogueStyle = (value) => typeof value === "string" && dialogueStyleSchema.safeParse(value).success;
var resolveDialogueStyle = (value) => isDialogueStyle(value) ? value : DEFAULT_DIALOGUE_STYLE;

// src/intelligence.ts
import { z as z16 } from "zod";
var analysisJobStatusSchema = z16.enum(["queued", "running", "completed", "failed"]);
var startConversationAnalysisRequestSchema = z16.object({
  workspaceId: z16.string().min(1).max(200),
  idempotencyKey: z16.string().min(8).max(200)
});
var analysisJobViewSchema = z16.object({
  id: z16.string(),
  workspaceId: z16.string(),
  conversationId: z16.string(),
  status: analysisJobStatusSchema,
  analysisId: z16.string().nullable(),
  errorCode: z16.string().nullable(),
  createdAt: z16.iso.datetime({ offset: true }),
  completedAt: z16.iso.datetime({ offset: true }).nullable()
});
var intelligenceEvidenceSchema = z16.object({
  messageId: z16.string(),
  role: z16.enum(["user", "assistant"]),
  claim: z16.string().min(1).max(120),
  excerpt: z16.string().min(1).max(500),
  occurredAt: z16.iso.datetime({ offset: true })
});
var outcomeTypeSchema = z16.enum([
  "converted",
  "qualified",
  "follow_up",
  "resolved",
  "abandoned",
  "unknown"
]);
var scoreDimensionsSchema = z16.object({
  grounding: z16.number().int().min(0).max(100),
  resolution: z16.number().int().min(0).max(100),
  tone: z16.number().int().min(0).max(100),
  compliance: z16.number().int().min(0).max(100)
});
var recommendationTypeSchema = z16.enum([
  "missing_knowledge",
  "frequent_objection",
  "handoff_topic",
  "agent_policy",
  "workflow"
]);
var recommendationStatusSchema = z16.enum(["detected", "accepted", "rejected"]);
var brainImprovementProposalSchema = z16.object({
  id: z16.string(),
  brainId: z16.string(),
  recommendationId: z16.string(),
  status: z16.literal("proposed"),
  knowledgeItemId: z16.string(),
  createdAt: z16.iso.datetime({ offset: true })
});
var recommendationViewSchema = z16.object({
  id: z16.string(),
  analysisId: z16.string(),
  conversationId: z16.string(),
  workspaceId: z16.string(),
  brainId: z16.string(),
  type: recommendationTypeSchema,
  title: z16.string(),
  rationale: z16.string(),
  proposedContent: z16.string().nullable(),
  status: recommendationStatusSchema,
  version: z16.number().int().positive(),
  evidence: z16.array(intelligenceEvidenceSchema).min(1),
  brainProposal: brainImprovementProposalSchema.nullable(),
  createdAt: z16.iso.datetime({ offset: true }),
  reviewedAt: z16.iso.datetime({ offset: true }).nullable()
});
var conversationAnalysisViewSchema = z16.object({
  id: z16.string(),
  workspaceId: z16.string(),
  conversationId: z16.string(),
  channel: z16.enum(["webchat", "simulator", "voice", "messaging", "email", "sms"]),
  agentId: z16.string(),
  agentVersionId: z16.string(),
  brainId: z16.string(),
  brainVersion: z16.number().int().positive(),
  summary: z16.string(),
  intent: z16.string(),
  topics: z16.array(z16.string()),
  sentiment: z16.enum(["positive", "neutral", "negative", "mixed"]),
  unansweredQuestions: z16.array(z16.string()),
  objections: z16.array(z16.string()),
  riskFlags: z16.array(z16.string()),
  outcome: z16.object({
    type: outcomeTypeSchema,
    converted: z16.boolean(),
    confidence: z16.number().min(0).max(1)
  }),
  scorecard: z16.object({
    total: z16.number().int().min(0).max(100),
    dimensions: scoreDimensionsSchema
  }),
  evidence: z16.array(intelligenceEvidenceSchema).min(1),
  recommendations: z16.array(recommendationViewSchema),
  analyzedAt: z16.iso.datetime({ offset: true })
});
var conversationAnalysisListSchema = z16.object({
  items: z16.array(conversationAnalysisViewSchema),
  nextCursor: z16.string().nullable()
});
var recommendationListSchema = z16.object({
  items: z16.array(recommendationViewSchema),
  nextCursor: z16.string().nullable()
});
var reviewRecommendationRequestSchema = z16.object({
  decision: z16.enum(["accept", "reject"]),
  expectedVersion: z16.number().int().positive(),
  note: z16.string().max(1e3).optional(),
  approvedContent: z16.string().min(10).max(8e3).optional()
});
var intelligenceDashboardSchema = z16.object({
  workspaceId: z16.string(),
  analyzedConversations: z16.number().int().nonnegative(),
  averageQualityScore: z16.number().min(0).max(100),
  conversionRate: z16.number().min(0).max(1),
  pendingRecommendations: z16.number().int().nonnegative(),
  outcomes: z16.array(z16.object({ type: outcomeTypeSchema, count: z16.number().int().nonnegative() })),
  channels: z16.array(z16.object({ channel: z16.string(), count: z16.number().int().nonnegative() })),
  topRecommendationTypes: z16.array(
    z16.object({ type: recommendationTypeSchema, count: z16.number().int().nonnegative() })
  )
});

// src/leads.ts
import { z as z17 } from "zod";
var leadStageSchema = z17.enum([
  "new",
  "contacted",
  "qualified",
  "callback",
  "not_interested",
  "converted",
  "closed"
]);
var leadScoreSchema = z17.enum(["hot", "warm", "cold"]);
var leadSentimentSchema = z17.enum(["positive", "neutral", "negative", "unknown"]);
var leadChannelSchema = z17.enum([
  "webchat",
  "simulator",
  "voice",
  "messaging",
  "email",
  "sms"
]);
var voiceLeadViewSchema = z17.object({
  id: z17.string(),
  workspaceId: z17.string(),
  /**
   * Voice rows still send a non-empty call id (historical required field).
   * Non-voice channel leads send JSON `null`. Current clients must accept both.
   * Historical voice-only parsers (`z.string()`, no `channel`) still parse
   * voice items because `channel` defaults to `voice` when absent.
   */
  callId: z17.string().min(1).nullable(),
  conversationId: z17.string().nullable(),
  contactId: z17.string().nullable(),
  campaignId: z17.string().nullable(),
  displayName: z17.string().nullable(),
  phoneMasked: z17.string().nullable(),
  stage: leadStageSchema,
  leadScore: leadScoreSchema,
  sentiment: leadSentimentSchema,
  aiSummary: z17.string().nullable(),
  aiNextAction: z17.string().nullable(),
  aiCategory: z17.string().nullable(),
  tags: z17.array(z17.string()),
  source: z17.enum(["voice_call", "tool", "manual"]),
  /** Additive. Absent on historical JSON; default voice so old payloads parse. */
  channel: leadChannelSchema.default("voice"),
  createdAt: z17.iso.datetime({ offset: true }),
  updatedAt: z17.iso.datetime({ offset: true })
});
var legacyVoiceLeadViewSchema = voiceLeadViewSchema.omit({ channel: true }).extend({
  callId: z17.string().min(1)
});
var LEAD_LIST_CONTRACT_VOICE_V1 = "voice-v1";
var LEAD_LIST_CONTRACT_WORKSPACE_V1 = "workspace-v1";
var leadListContractSchema = z17.enum([
  LEAD_LIST_CONTRACT_VOICE_V1,
  LEAD_LIST_CONTRACT_WORKSPACE_V1
]);
var LEAD_LIST_CONTRACT_HEADER = "x-agentos-leads-contract";
var DEFAULT_LEAD_LIST_CONTRACT = LEAD_LIST_CONTRACT_VOICE_V1;
var voiceLeadListSchema = z17.object({
  items: z17.array(voiceLeadViewSchema),
  nextCursor: z17.string().nullable(),
  /** Live workspace durable lead count (workspace_leads ∪ orphan voice_leads). */
  usage: z17.number().int().nonnegative(),
  /** Plan cap; null = unlimited (missing entitlement / enforcement none). */
  limit: z17.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema.nullable()
});
var legacyVoiceLeadListSchema = voiceLeadListSchema.extend({
  items: z17.array(legacyVoiceLeadViewSchema)
});
var updateVoiceLeadRequestSchema = z17.object({
  stage: leadStageSchema.optional(),
  leadScore: leadScoreSchema.optional(),
  aiCategory: z17.string().min(1).max(80).nullable().optional(),
  aiNextAction: z17.string().min(1).max(500).nullable().optional(),
  tags: z17.array(z17.string().min(1).max(40)).max(20).optional()
}).refine(
  (value) => value.stage !== void 0 || value.leadScore !== void 0 || value.aiCategory !== void 0 || value.aiNextAction !== void 0 || value.tags !== void 0,
  { message: "LEAD_PATCH_EMPTY" }
);
var bulkUpdateVoiceLeadStagesRequestSchema = z17.object({
  leadIds: z17.array(z17.string().min(1).max(80)).min(1).max(50),
  stage: leadStageSchema
});
var bulkUpdateVoiceLeadStagesResponseSchema = z17.object({
  updated: z17.number().int().nonnegative(),
  items: z17.array(voiceLeadViewSchema)
});
var LEAD_STAGE_BOARD = [
  "new",
  "contacted",
  "qualified",
  "callback",
  "not_interested",
  "converted",
  "closed"
];

// src/voice.ts
var e164Schema = z18.string().regex(/^\+[1-9]\d{7,14}$/u);
var createVoiceCallRequestSchema = z18.object({
  workspaceId: z18.string().min(1).max(200),
  /**
   * Published agent id. Optional when `numberId` has a `defaultInboundAgentId`
   * bind (inbound DID → agent) — application resolves it.
   * Required for `transport: browser_demo`.
   */
  agentId: z18.string().min(1).max(200).optional(),
  direction: z18.enum(["inbound", "outbound"]),
  /**
   * `pstn` — real telephony trunk (default when omitted).
   * `browser_demo` — PC softphone path: no PSTN, mic↔agent in browser for voice quality + task QA.
   */
  transport: z18.enum(["pstn", "browser_demo"]).optional(),
  /** Legacy freeform CLI; preferred path is numberId from inventory. */
  from: e164Schema.optional(),
  /** Workspace phone inventory id (platform_external / customer_byo / internal). */
  numberId: z18.string().min(1).max(200).optional(),
  /** Required for PSTN; optional for browser_demo (defaults to synthetic demo E.164). */
  to: e164Schema.optional(),
  clientCallId: z18.string().min(8).max(200),
  recordingConsent: z18.boolean(),
  /** Optional directory contact + outbound purpose (marketing/support/custom). */
  contactId: z18.string().min(1).max(200).optional(),
  purpose: z18.enum(["general", "support", "sales", "marketing", "callback", "custom"]).optional(),
  purposeNote: z18.string().max(1e3).optional().nullable(),
  dialogueStyle: dialogueStyleSchema.optional(),
  useBusinessBrain: z18.boolean().optional(),
  /** Plan voice tier override; else from embed entitlement voice.profile.* */
  serviceProfile: z18.enum(["starter", "growth", "pro"]).optional(),
  /** Calling factory: link call to campaign membership for sequential advance. */
  campaignId: z18.string().min(1).max(200).optional(),
  campaignContactId: z18.string().min(1).max(200).optional()
}).superRefine((value, ctx) => {
  const transport = value.transport ?? "pstn";
  if (transport === "browser_demo") {
    if (!value.agentId) {
      ctx.addIssue({
        code: "custom",
        message: "AGENT_ID_REQUIRED_FOR_BROWSER_DEMO",
        path: ["agentId"]
      });
    }
    return;
  }
  if (!value.to) {
    ctx.addIssue({
      code: "custom",
      message: "TO_REQUIRED",
      path: ["to"]
    });
  }
  if (!value.numberId && !value.from) {
    ctx.addIssue({
      code: "custom",
      message: "NUMBER_ID_OR_FROM_REQUIRED",
      path: ["numberId"]
    });
  }
  if (!value.agentId && !value.numberId) {
    ctx.addIssue({
      code: "custom",
      message: "AGENT_ID_OR_NUMBER_BIND_REQUIRED",
      path: ["agentId"]
    });
  }
});
var voiceCallStatusSchema = z18.enum([
  "queued",
  "ringing",
  "active",
  "waiting_for_human",
  "completed",
  "failed"
]);
var voiceCallViewSchema = z18.object({
  id: z18.string(),
  conversationId: z18.string(),
  workspaceId: z18.string(),
  agentId: z18.string(),
  agentVersionId: z18.string(),
  direction: z18.enum(["inbound", "outbound"]),
  status: voiceCallStatusSchema,
  fromMasked: z18.string(),
  toMasked: z18.string(),
  provider: z18.string(),
  providerCallId: z18.string().nullable(),
  recording: z18.object({
    enabled: z18.boolean(),
    status: z18.enum(["disabled", "capturing", "available", "failed"]),
    storageKey: z18.string().nullable()
  }),
  handoffRequested: z18.boolean(),
  bargeInCount: z18.number().int().nonnegative(),
  durationSeconds: z18.number().int().nonnegative(),
  summary: z18.string().nullable(),
  startedAt: z18.iso.datetime({ offset: true }),
  endedAt: z18.iso.datetime({ offset: true }).nullable()
});
var voiceCallListSchema = z18.object({
  items: z18.array(voiceCallViewSchema),
  nextCursor: z18.string().nullable()
});
var voiceCallListFiltersSchema = z18.object({
  status: voiceCallStatusSchema.optional(),
  direction: z18.enum(["inbound", "outbound"]).optional(),
  agentId: z18.string().min(1).max(200).optional(),
  campaignId: z18.string().min(1).max(200).optional(),
  hasRecording: z18.boolean().optional(),
  hasSummary: z18.boolean().optional(),
  startedAfter: z18.iso.datetime({ offset: true }).optional(),
  startedBefore: z18.iso.datetime({ offset: true }).optional()
});
var voiceTurnRequestSchema = z18.object({
  clientEventId: z18.string().min(8).max(200),
  /**
   * Text utterance (sim / browser speech-to-text already done client-side).
   * Optional when `audioBase64` is provided for server STT.
   */
  utterance: z18.string().max(8e3).optional(),
  /**
   * Raw browser capture (webm/ogg/wav/pcm) for server STT.
   * Preferred path for PC demo quality testing.
   */
  audioBase64: z18.string().min(8).max(6e6).optional(),
  audioEncoding: z18.enum(["webm", "ogg", "wav", "mp3", "pcm16", "mpeg"]).optional().default("webm"),
  /** When true, response includes playable TTS (mp3 base64) for browser playback. */
  includeAudio: z18.boolean().default(false),
  startedAtMs: z18.number().int().nonnegative().default(0),
  endedAtMs: z18.number().int().nonnegative().default(0),
  bargeIn: z18.boolean().default(false),
  ...legacySpeechProtectionRejectionFields
}).superRefine((value, ctx) => {
  const hasText = Boolean(value.utterance?.trim());
  const hasAudio = Boolean(value.audioBase64?.trim());
  if (!hasText && !hasAudio) {
    ctx.addIssue({
      code: "custom",
      message: "UTTERANCE_OR_AUDIO_REQUIRED",
      path: ["utterance"]
    });
  }
});
var voiceTurnResponseSchema = z18.object({
  callId: z18.string(),
  transcript: z18.object({
    text: z18.string(),
    confidence: z18.number().min(0).max(1),
    startedAtMs: z18.number().int().nonnegative(),
    endedAtMs: z18.number().int().nonnegative()
  }),
  response: runtimeResponseSchema,
  audio: z18.object({
    format: z18.enum(["pcm16", "mp3"]),
    chunkCount: z18.number().int().nonnegative(),
    byteLength: z18.number().int().nonnegative(),
    estimatedDurationMs: z18.number().int().nonnegative(),
    /** Present when request.includeAudio=true (browser softphone playback). */
    contentType: z18.string().optional(),
    audioBase64: z18.string().optional()
  }),
  bargeInAcknowledged: z18.boolean(),
  tools: z18.array(agentToolExecutionViewSchema).default([])
});
var voiceGreetingResponseSchema = z18.object({
  callId: z18.string(),
  text: z18.string().min(1),
  audio: z18.object({
    contentType: z18.literal("audio/mpeg"),
    audioBase64: z18.string().min(8),
    byteLength: z18.number().int().positive(),
    estimatedDurationMs: z18.number().int().positive()
  })
});
var callToolTimelineSchema = z18.object({
  callId: z18.string(),
  conversationId: z18.string(),
  items: z18.array(agentToolExecutionViewSchema)
});
var callFollowUpSchema = z18.object({
  type: z18.enum(["note", "callback", "appointment", "lead", "case", "handoff"]),
  title: z18.string().min(1).max(200),
  detail: z18.string().min(1).max(1500),
  toolExecutionId: z18.string().min(1),
  toolName: z18.string().min(1),
  createdAt: z18.iso.datetime({ offset: true })
});
var callAnalysisSnapshotSchema = z18.object({
  analysisId: z18.string(),
  summary: z18.string(),
  intent: z18.string(),
  /** Fixed v1 outcome enum (aligned with conversation intelligence). */
  outcome: outcomeTypeSchema,
  sentiment: z18.enum(["positive", "neutral", "negative", "mixed", "unknown"]),
  topics: z18.array(z18.string()),
  nextStep: z18.string().nullable(),
  analyzedAt: z18.iso.datetime({ offset: true })
});
var callCardContextSchema = z18.object({
  purpose: z18.enum(["general", "support", "sales", "marketing", "callback", "custom"]).nullable(),
  purposeNote: z18.string().nullable(),
  dialogueStyle: dialogueStyleSchema.nullable(),
  serviceProfile: z18.enum(["starter", "growth", "pro"]).nullable(),
  useBusinessBrain: z18.boolean(),
  voiceProfileId: z18.string().nullable(),
  contact: z18.object({
    id: z18.string(),
    displayName: z18.string()
  }).nullable(),
  campaign: z18.object({
    id: z18.string(),
    name: z18.string()
  }).nullable()
});
var callOpsCostSchema = z18.object({
  callId: z18.string(),
  /** Configured stack at publish (agent map). */
  configured: z18.object({
    llmModel: z18.string().nullable(),
    sttProvider: z18.string().nullable(),
    ttsProvider: z18.string().nullable(),
    ttsVoiceId: z18.string().nullable()
  }),
  meters: z18.array(
    z18.object({
      meter: z18.string(),
      quantity: z18.number(),
      estimatedCost: z18.object({
        tryMinor: z18.number().int(),
        usdMinor: z18.number().int()
      })
    })
  ),
  estimatedCost: z18.object({
    tryMinor: z18.number().int(),
    usdMinor: z18.number().int()
  }),
  fx: z18.object({
    usdTry: z18.number().positive(),
    source: z18.literal("config")
  }),
  note: z18.string()
});
var voiceCallCardSchema = z18.object({
  call: voiceCallViewSchema,
  transcriptEntryCount: z18.number().int().nonnegative(),
  tools: z18.array(agentToolExecutionViewSchema),
  followUps: z18.array(callFollowUpSchema),
  analysis: callAnalysisSnapshotSchema.nullable(),
  analysisJob: z18.object({
    id: z18.string(),
    status: z18.enum(["queued", "running", "completed", "failed"]),
    errorCode: z18.string().nullable()
  }).nullable(),
  /** Outbound purpose / style / campaign membership from call_media_sessions. */
  context: callCardContextSchema,
  /** Post-call lead card when upserted from complete (P0.5). */
  lead: voiceLeadViewSchema.nullable(),
  /**
   * Ops-only estimated cost (platform.admin). Omitted for normal customers.
   * Rate-card estimate at read time — not an invoice.
   */
  opsCost: callOpsCostSchema.nullable().optional(),
  /**
   * What the speech gates did on this call: invented claims refused before
   * being spoken, silent turns, repeats. Null for calls that ran before the
   * per-turn quality record existed.
   */
  quality: conversationQualityReportSchema.nullable().optional()
});
var completeVoiceCallRequestSchema = z18.object({
  reason: z18.enum(["caller_hangup", "agent_hangup", "timeout", "failed"]).default("caller_hangup")
});
var handoffVoiceCallRequestSchema = z18.object({
  reason: z18.string().min(2).max(500),
  destination: e164Schema.optional(),
  /**
   * blind — silent redirect.
   * warm — caller hears ringback while agent leg is dialed.
   * Optional: when omitted, application uses agent voice.defaultHandoffMode then warm.
   */
  mode: z18.enum(["blind", "warm"]).optional()
});
var voiceTranscriptEntrySchema = z18.object({
  id: z18.string(),
  role: z18.enum(["user", "assistant"]),
  content: z18.string(),
  createdAt: z18.iso.datetime({ offset: true }),
  citations: z18.array(citationSchema)
});
var voiceTranscriptSchema = z18.object({
  callId: z18.string(),
  conversationId: z18.string(),
  entries: z18.array(voiceTranscriptEntrySchema)
});
var voicePerformanceQuerySchema = z18.object({
  workspaceId: z18.string().min(1).max(200),
  from: z18.iso.datetime({ offset: true }).optional(),
  to: z18.iso.datetime({ offset: true }).optional(),
  agentId: z18.string().min(1).max(200).optional(),
  campaignId: z18.string().min(1).max(200).optional()
});
var voicePerformanceDashboardSchema = z18.object({
  workspaceId: z18.string(),
  range: z18.object({
    from: z18.iso.datetime({ offset: true }),
    to: z18.iso.datetime({ offset: true })
  }),
  kpis: z18.object({
    callsStarted: z18.number().int().nonnegative(),
    callsCompleted: z18.number().int().nonnegative(),
    callsFailed: z18.number().int().nonnegative(),
    callsActive: z18.number().int().nonnegative(),
    talkMinutes: z18.number().nonnegative(),
    avgDurationSeconds: z18.number().nonnegative(),
    handoffRate: z18.number().min(0).max(1),
    containmentRate: z18.number().min(0).max(1),
    leadsCreated: z18.number().int().nonnegative(),
    toolSuccessRate: z18.number().min(0).max(1).nullable(),
    latencyP50Ms: z18.number().nonnegative().nullable(),
    latencyP95Ms: z18.number().nonnegative().nullable()
  }),
  series: z18.object({
    callsByDay: z18.array(
      z18.object({
        date: z18.string(),
        started: z18.number().int().nonnegative(),
        completed: z18.number().int().nonnegative(),
        failed: z18.number().int().nonnegative()
      })
    ),
    outcomes: z18.array(
      z18.object({ type: outcomeTypeSchema, count: z18.number().int().nonnegative() })
    ),
    sentiment: z18.array(
      z18.object({
        sentiment: z18.enum(["positive", "neutral", "negative", "mixed", "unknown"]),
        count: z18.number().int().nonnegative()
      })
    ),
    byAgent: z18.array(
      z18.object({
        agentId: z18.string(),
        agentName: z18.string().nullable(),
        calls: z18.number().int().nonnegative(),
        completed: z18.number().int().nonnegative(),
        avgDurationSeconds: z18.number().nonnegative()
      })
    )
  })
});

// src/phone-numbers.ts
import { z as z19 } from "zod";
var e164Schema2 = z19.string().regex(/^\+[1-9]\d{7,14}$/u);
var phoneNumberClassSchema = z19.enum([
  "panaryus_internal",
  "platform_external",
  "customer_byo"
]);
var phoneNumberProviderSchema = z19.enum([
  "manual",
  "zadarma",
  "verimor",
  "twilio",
  "byo_sip",
  "other"
]);
var phoneNumberGeoScopeSchema = z19.enum(["tr", "intl", "unknown"]);
var phoneNumberStatusSchema = z19.enum(["active", "pending", "disabled", "archived"]);
var phoneNumberTrustBandSchema = z19.enum(["internal", "strict", "light"]);
var createByoPhoneNumberRequestSchema = z19.object({
  workspaceId: z19.string().min(1).max(200),
  e164: e164Schema2,
  label: z19.string().min(1).max(120).optional().nullable(),
  provider: z19.enum(["byo_sip", "twilio", "other", "manual"]).default("byo_sip"),
  geoScope: phoneNumberGeoScopeSchema.default("unknown"),
  providerRef: z19.string().max(200).optional().nullable(),
  notes: z19.string().max(1e3).optional().nullable()
});
var createManagedPhoneNumberRequestSchema = z19.object({
  workspaceId: z19.string().min(1).max(200),
  e164: e164Schema2,
  label: z19.string().min(1).max(120).optional().nullable(),
  numberClass: z19.enum(["platform_external", "panaryus_internal"]),
  provider: phoneNumberProviderSchema.default("manual"),
  geoScope: phoneNumberGeoScopeSchema.default("unknown"),
  providerRef: z19.string().max(200).optional().nullable(),
  notes: z19.string().max(1e3).optional().nullable()
});
var updatePhoneNumberRequestSchema = z19.object({
  label: z19.string().min(1).max(120).optional().nullable(),
  status: phoneNumberStatusSchema.optional(),
  geoScope: phoneNumberGeoScopeSchema.optional(),
  notes: z19.string().max(1e3).optional().nullable(),
  providerRef: z19.string().max(200).optional().nullable(),
  /**
   * Bind a published agent for inbound calls to this DID.
   * Pass `null` to clear the binding. Omit to leave unchanged.
   */
  defaultInboundAgentId: z19.string().min(1).max(200).optional().nullable()
});
var phoneNumberViewSchema = z19.object({
  id: z19.string(),
  workspaceId: z19.string(),
  e164: z19.string(),
  e164Masked: z19.string(),
  label: z19.string().nullable(),
  numberClass: phoneNumberClassSchema,
  provider: phoneNumberProviderSchema,
  geoScope: phoneNumberGeoScopeSchema,
  status: phoneNumberStatusSchema,
  trustBand: phoneNumberTrustBandSchema,
  providerRef: z19.string().nullable(),
  notes: z19.string().nullable(),
  /** Published agent that answers inbound to this DID (null = unbound). */
  defaultInboundAgentId: z19.string().nullable(),
  createdAt: z19.iso.datetime({ offset: true }),
  updatedAt: z19.iso.datetime({ offset: true })
});
var phoneNumberListSchema = z19.object({
  items: z19.array(phoneNumberViewSchema),
  nextCursor: z19.string().nullable(),
  /** Live workspace active phone number count (status='active'). */
  usage: z19.number().int().nonnegative(),
  /** Plan cap; null = unlimited (missing entitlement / enforcement none). */
  limit: z19.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema.nullable()
});
var importPhoneNumberPoolRequestSchema = z19.object({
  workspaceId: z19.string().min(1).max(200),
  provider: phoneNumberProviderSchema.default("verimor"),
  numberClass: z19.enum(["platform_external", "panaryus_internal"]).default("platform_external"),
  geoScope: phoneNumberGeoScopeSchema.default("tr"),
  numbers: z19.array(
    z19.object({
      e164: e164Schema2,
      label: z19.string().min(1).max(120).optional().nullable(),
      providerRef: z19.string().max(200).optional().nullable(),
      notes: z19.string().max(500).optional().nullable()
    })
  ).min(1).max(50)
});
var importPhoneNumberPoolResponseSchema = z19.object({
  created: z19.array(phoneNumberViewSchema),
  skipped: z19.array(z19.object({ e164: z19.string(), reason: z19.string() }))
});
var modeBRateCardSchema = z19.object({
  currency: z19.literal("TRY"),
  mode: z19.literal("platform_did"),
  trust: z19.literal("strict"),
  lines: z19.array(
    z19.object({
      sku: z19.string(),
      label: z19.string(),
      unit: z19.enum(["month", "minute", "message", "seat"]),
      unitAmountTry: z19.number().nonnegative(),
      notes: z19.string()
    })
  ),
  rules: z19.array(z19.string()),
  trunks: z19.array(
    z19.object({
      provider: phoneNumberProviderSchema,
      role: z19.enum(["primary", "failover", "regional", "byo"]),
      status: z19.enum(["live", "configured", "not_configured"])
    })
  )
});

// src/contacts.ts
import { z as z20 } from "zod";
var e164Schema3 = z20.string().regex(/^\+[1-9]\d{7,14}$/u);
var contactPurposeSchema = z20.enum([
  "general",
  "support",
  "sales",
  "marketing",
  "callback",
  "custom"
]);
var createContactRequestSchema = z20.object({
  workspaceId: z20.string().min(1).max(200),
  displayName: z20.string().min(1).max(160),
  phoneE164: e164Schema3,
  email: z20.string().email().max(200).optional().nullable(),
  company: z20.string().max(160).optional().nullable(),
  tags: z20.array(z20.string().min(1).max(40)).max(20).default([]),
  defaultPurpose: contactPurposeSchema.default("general"),
  defaultPurposeNote: z20.string().max(1e3).optional().nullable(),
  defaultDialogueStyle: dialogueStyleSchema.optional().nullable(),
  preferBusinessBrain: z20.boolean().default(false),
  notes: z20.string().max(2e3).optional().nullable()
});
var updateContactRequestSchema = z20.object({
  displayName: z20.string().min(1).max(160).optional(),
  phoneE164: e164Schema3.optional(),
  email: z20.string().email().max(200).optional().nullable(),
  company: z20.string().max(160).optional().nullable(),
  tags: z20.array(z20.string().min(1).max(40)).max(20).optional(),
  defaultPurpose: contactPurposeSchema.optional(),
  defaultPurposeNote: z20.string().max(1e3).optional().nullable(),
  defaultDialogueStyle: dialogueStyleSchema.optional().nullable(),
  preferBusinessBrain: z20.boolean().optional(),
  notes: z20.string().max(2e3).optional().nullable(),
  status: z20.enum(["active", "archived"]).optional()
});
var contactViewSchema = z20.object({
  id: z20.string(),
  workspaceId: z20.string(),
  displayName: z20.string(),
  phoneE164: z20.string(),
  email: z20.string().nullable(),
  company: z20.string().nullable(),
  tags: z20.array(z20.string()),
  defaultPurpose: contactPurposeSchema,
  defaultPurposeNote: z20.string().nullable(),
  defaultDialogueStyle: dialogueStyleSchema.nullable().optional().default(null),
  preferBusinessBrain: z20.boolean(),
  notes: z20.string().nullable(),
  status: z20.enum(["active", "archived"]),
  createdAt: z20.iso.datetime({ offset: true }),
  updatedAt: z20.iso.datetime({ offset: true })
});
var contactListSchema = z20.object({
  items: z20.array(contactViewSchema),
  nextCursor: z20.string().nullable(),
  /** Live workspace active contact count (status='active'). */
  usage: z20.number().int().nonnegative(),
  /** Plan cap; null = unlimited (missing entitlement / enforcement none). */
  limit: z20.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema.nullable()
});
var contactCallRequestSchema = z20.object({
  agentId: z20.string().min(1).max(200),
  from: e164Schema3.optional(),
  numberId: z20.string().min(1).max(200).optional(),
  purpose: contactPurposeSchema.default("general"),
  purposeNote: z20.string().max(1e3).optional().nullable(),
  /** Conversation rule pack for this call (panel selectable). */
  dialogueStyle: dialogueStyleSchema.optional(),
  serviceProfile: z20.enum(["starter", "growth", "pro"]).optional(),
  useBusinessBrain: z20.boolean().default(true),
  recordingConsent: z20.boolean().default(true),
  clientCallId: z20.string().min(8).max(200).optional()
}).superRefine((value, ctx) => {
  if (!value.numberId && !value.from) {
    ctx.addIssue({
      code: "custom",
      message: "NUMBER_ID_OR_FROM_REQUIRED",
      path: ["numberId"]
    });
  }
});
var contactMessageRequestSchema = z20.object({
  connectorId: z20.string().min(1).max(200),
  operation: z20.enum(["messages_send_template", "messages_send_text"]).default("messages_send_template"),
  templateName: z20.string().min(1).max(120).optional(),
  languageCode: z20.string().min(2).max(12).default("tr"),
  text: z20.string().min(1).max(1500).optional(),
  purpose: contactPurposeSchema.default("general"),
  purposeNote: z20.string().max(1e3).optional().nullable(),
  idempotencyKey: z20.string().min(8).max(200).optional()
});

// src/campaigns.ts
import { z as z21 } from "zod";
var campaignStatusSchema = z21.enum([
  "draft",
  "scheduled",
  "running",
  "paused",
  "completed",
  "failed",
  "archived"
]);
var scheduleDaySchema = z21.number().int().min(0).max(6);
var hhmmSchema = z21.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u, "Expected HH:MM 24h");
var campaignRetryPolicySchema = z21.object({
  enabled: z21.boolean(),
  maxAttempts: z21.number().int().min(1).max(10),
  intervalMinutes: z21.number().int().min(1).max(10080),
  onBusy: z21.boolean(),
  onNoAnswer: z21.boolean(),
  onFailed: z21.boolean()
});
var campaignSchedulePolicySchema = z21.object({
  enabled: z21.boolean(),
  days: z21.array(scheduleDaySchema).min(1).max(7),
  timeStart: hhmmSchema,
  timeEnd: hhmmSchema,
  timezone: z21.string().min(1).max(80)
});
var defaultRetryPolicy = () => ({
  enabled: false,
  maxAttempts: 1,
  intervalMinutes: 30,
  onBusy: true,
  onNoAnswer: true,
  onFailed: true
});
var defaultSchedulePolicy = () => ({
  enabled: false,
  days: [1, 2, 3, 4, 5],
  timeStart: "09:00",
  timeEnd: "18:00",
  timezone: "Europe/Istanbul"
});
var retryPolicyInputSchema = campaignRetryPolicySchema.partial().optional();
var schedulePolicyInputSchema = campaignSchedulePolicySchema.partial().optional();
var createCampaignRequestSchema = z21.object({
  workspaceId: z21.string().min(1).max(200),
  name: z21.string().min(1).max(160),
  agentId: z21.string().min(1).max(200).optional().nullable(),
  dialogueStyle: dialogueStyleSchema.optional().nullable(),
  defaultPurpose: contactPurposeSchema.default("general"),
  defaultPurposeNote: z21.string().max(2e3).optional().nullable(),
  serviceProfile: z21.enum(["starter", "growth", "pro"]).optional().nullable(),
  useBusinessBrain: z21.boolean().default(true),
  goal: z21.string().max(500).optional().nullable(),
  retry: retryPolicyInputSchema,
  schedule: schedulePolicyInputSchema
});
var updateCampaignRequestSchema = z21.object({
  name: z21.string().min(1).max(160).optional(),
  agentId: z21.string().min(1).max(200).optional().nullable(),
  dialogueStyle: dialogueStyleSchema.optional().nullable(),
  defaultPurpose: contactPurposeSchema.optional(),
  defaultPurposeNote: z21.string().max(2e3).optional().nullable(),
  serviceProfile: z21.enum(["starter", "growth", "pro"]).optional().nullable(),
  useBusinessBrain: z21.boolean().optional(),
  goal: z21.string().max(500).optional().nullable(),
  status: campaignStatusSchema.optional(),
  retry: retryPolicyInputSchema,
  schedule: schedulePolicyInputSchema
});
var campaignViewSchema = z21.object({
  id: z21.string(),
  workspaceId: z21.string(),
  name: z21.string(),
  status: campaignStatusSchema,
  agentId: z21.string().nullable(),
  dialogueStyle: dialogueStyleSchema.nullable(),
  defaultPurpose: contactPurposeSchema,
  defaultPurposeNote: z21.string().nullable(),
  serviceProfile: z21.enum(["starter", "growth", "pro"]).nullable(),
  useBusinessBrain: z21.boolean(),
  goal: z21.string().nullable(),
  totalContacts: z21.number().int().nonnegative(),
  successfulCalls: z21.number().int().nonnegative(),
  failedCalls: z21.number().int().nonnegative(),
  defaultFromE164: z21.string().nullable().optional(),
  retry: campaignRetryPolicySchema,
  schedule: campaignSchedulePolicySchema,
  createdAt: z21.iso.datetime({ offset: true }),
  updatedAt: z21.iso.datetime({ offset: true })
});
var campaignListSchema = z21.object({
  items: z21.array(campaignViewSchema),
  nextCursor: z21.string().nullable(),
  /** Live workspace campaign count (non-archived). */
  usage: z21.number().int().nonnegative(),
  /** Plan cap; null = unlimited (missing entitlement / enforcement none). */
  limit: z21.number().nonnegative().nullable(),
  enforcement: quotaEnforcementSchema.nullable()
});
var campaignContactStatusSchema = z21.enum([
  "pending",
  "queued",
  "calling",
  "completed",
  "failed",
  "skipped"
]);
var campaignContactViewSchema = z21.object({
  id: z21.string(),
  campaignId: z21.string(),
  workspaceId: z21.string(),
  contactId: z21.string().nullable(),
  displayName: z21.string().nullable(),
  phoneE164: z21.string(),
  status: campaignContactStatusSchema,
  lastCallId: z21.string().nullable(),
  attempts: z21.number().int().nonnegative(),
  nextAttemptAt: z21.iso.datetime({ offset: true }).nullable(),
  lastError: z21.string().nullable(),
  createdAt: z21.iso.datetime({ offset: true }),
  updatedAt: z21.iso.datetime({ offset: true })
});
var campaignContactListSchema = z21.object({
  items: z21.array(campaignContactViewSchema),
  nextCursor: z21.string().nullable(),
  total: z21.number().int().nonnegative()
});
var addCampaignContactsRequestSchema = z21.object({
  contactIds: z21.array(z21.string().min(1).max(200)).max(500).optional(),
  rows: z21.array(
    z21.object({
      phoneE164: z21.string().min(3).max(32),
      displayName: z21.string().max(160).optional().nullable()
    })
  ).max(500).optional()
});
var importCampaignContactsCsvRequestSchema = z21.object({
  csv: z21.string().min(1).max(15e5)
});
var campaignContactImportResultSchema = z21.object({
  added: z21.number().int().nonnegative(),
  skippedDuplicate: z21.number().int().nonnegative(),
  skippedInvalid: z21.number().int().nonnegative(),
  totalContacts: z21.number().int().nonnegative(),
  items: z21.array(campaignContactViewSchema)
});
var e164Schema4 = z21.string().regex(/^\+[1-9]\d{7,14}$/u);
var startCampaignRequestSchema = z21.object({
  from: e164Schema4.optional(),
  numberId: z21.string().min(1).max(200).optional(),
  recordingConsent: z21.boolean().default(true)
}).superRefine((value, ctx) => {
  if (!value.numberId && !value.from) {
    ctx.addIssue({
      code: "custom",
      message: "NUMBER_ID_OR_FROM_REQUIRED",
      path: ["numberId"]
    });
  }
});
var campaignRunResultSchema = z21.object({
  campaign: campaignViewSchema,
  dialed: campaignContactViewSchema.nullable(),
  callId: z21.string().nullable(),
  message: z21.string(),
  finished: z21.boolean()
});

// src/provider.ts
import { z as z22 } from "zod";
var providerCapabilitySchema = z22.enum(["telephony", "stt", "tts", "llm"]);
var providerAccountStatusSchema = z22.enum(["active", "disabled"]);
var providerHealthSchema = z22.enum(["healthy", "degraded", "unavailable", "unknown"]);
var createProviderAccountRequestSchema = z22.object({
  workspaceId: z22.string().min(1).max(200),
  providerKey: z22.string().regex(/^[a-z0-9][a-z0-9_-]{1,79}$/u),
  displayName: z22.string().min(2).max(160),
  capabilities: z22.array(providerCapabilitySchema).min(1).max(4),
  secretRef: z22.string().min(8).max(500),
  region: z22.string().min(2).max(80),
  locales: z22.array(z22.string().min(2).max(35)).min(1).max(50),
  countries: z22.array(z22.string().length(2)).max(100).default([]),
  priority: z22.number().int().min(1).max(100).default(50),
  fallbackEligible: z22.boolean().default(true)
});
var providerAccountViewSchema = z22.object({
  id: z22.string(),
  workspaceId: z22.string(),
  providerKey: z22.string(),
  displayName: z22.string(),
  capabilities: z22.array(providerCapabilitySchema),
  status: providerAccountStatusSchema,
  health: providerHealthSchema,
  credentialConfigured: z22.boolean(),
  region: z22.string(),
  locales: z22.array(z22.string()),
  countries: z22.array(z22.string()),
  priority: z22.number().int(),
  fallbackEligible: z22.boolean(),
  createdAt: z22.iso.datetime({ offset: true })
});
var providerAccountListSchema = z22.object({
  items: z22.array(providerAccountViewSchema),
  nextCursor: z22.string().nullable()
});
var upsertRoutingPolicyRequestSchema = z22.object({
  workspaceId: z22.string().min(1).max(200),
  capability: providerCapabilitySchema,
  primaryProviderAccountId: z22.string().min(1),
  fallbackProviderAccountIds: z22.array(z22.string().min(1)).max(5).default([]),
  locale: z22.string().min(2).max(35),
  dataRegion: z22.string().min(2).max(80),
  quality: z22.enum(["standard", "premium"]).default("standard"),
  timeoutMs: z22.number().int().min(100).max(6e4).default(8e3),
  maxAttempts: z22.number().int().min(1).max(5).default(2),
  circuitFailureThreshold: z22.number().int().min(1).max(100).default(3),
  circuitCooldownSeconds: z22.number().int().min(1).max(3600).default(30),
  expectedVersion: z22.number().int().positive().nullable().default(null)
});
var routingPolicyViewSchema = upsertRoutingPolicyRequestSchema.omit({ expectedVersion: true }).extend({
  id: z22.string(),
  version: z22.number().int().positive(),
  updatedAt: z22.iso.datetime({ offset: true })
});
var routingPolicyListSchema = z22.object({ items: z22.array(routingPolicyViewSchema) });
var providerHealthViewSchema = z22.object({
  providerAccountId: z22.string(),
  health: providerHealthSchema,
  consecutiveFailures: z22.number().int().nonnegative(),
  circuit: z22.enum(["closed", "open", "half_open"]),
  observedAt: z22.iso.datetime({ offset: true }),
  latencyMs: z22.number().int().nonnegative().nullable(),
  errorCode: z22.string().nullable()
});
var providerUsageSummarySchema = z22.object({
  workspaceId: z22.string(),
  from: z22.iso.datetime({ offset: true }),
  to: z22.iso.datetime({ offset: true }),
  totals: z22.array(
    z22.object({
      providerAccountId: z22.string(),
      capability: providerCapabilitySchema,
      unit: z22.enum(["request", "second", "character", "token", "minute"]),
      quantity: z22.number().nonnegative(),
      providerCostMinor: z22.number().int().nonnegative(),
      currency: z22.string().length(3),
      successCount: z22.number().int().nonnegative(),
      failureCount: z22.number().int().nonnegative()
    })
  )
});
var reconcileProvidersRequestSchema = z22.object({
  workspaceId: z22.string().min(1).max(200)
});
var reconcileProvidersResponseSchema = z22.object({
  runId: z22.string(),
  workspaceId: z22.string(),
  checked: z22.number().int().nonnegative(),
  healthy: z22.number().int().nonnegative(),
  unavailable: z22.number().int().nonnegative(),
  completedAt: z22.iso.datetime({ offset: true })
});

// src/omnichannel.ts
import { z as z23 } from "zod";
var channelKindSchema = z23.enum(["messaging", "email", "sms"]);
var runOmnichannelTurnRequestSchema = z23.object({
  workspaceId: z23.string().min(1).max(200),
  agentId: z23.string().min(1).max(200),
  channel: channelKindSchema,
  externalConversationId: z23.string().min(1).max(300),
  recipientRef: z23.string().min(1).max(300),
  clientMessageId: z23.string().min(8).max(200),
  content: z23.string().min(1).max(8e3),
  action: z23.object({
    connectorId: z23.string().min(1),
    operation: z23.string().min(1).max(80),
    idempotencyKey: z23.string().min(8).max(200),
    input: z23.record(z23.string(), z23.unknown()).default({})
  }).optional()
});
var workflowRunStatusSchema = z23.enum([
  "queued",
  "running",
  "retrying",
  "completed",
  "failed",
  "dead_letter"
]);
var workflowRunViewSchema = z23.object({
  id: z23.string(),
  workspaceId: z23.string(),
  workflowType: z23.literal("channel-delivery"),
  channel: channelKindSchema,
  conversationId: z23.string(),
  status: workflowRunStatusSchema,
  attemptCount: z23.number().int().nonnegative(),
  maxAttempts: z23.number().int().positive(),
  nextAttemptAt: z23.iso.datetime({ offset: true }).nullable(),
  errorCode: z23.string().nullable(),
  createdAt: z23.iso.datetime({ offset: true }),
  completedAt: z23.iso.datetime({ offset: true }).nullable()
});
var omnichannelTurnResponseSchema = z23.object({
  conversationId: z23.string(),
  responseMessageId: z23.string(),
  text: z23.string(),
  citations: z23.array(
    z23.object({
      knowledgeItemId: z23.string(),
      knowledgeVersionId: z23.string(),
      sourceId: z23.string().nullable(),
      sourceLabel: z23.string(),
      content: z23.string()
    })
  ),
  workflow: workflowRunViewSchema,
  action: z23.lazy(() => connectorInvocationViewSchema).nullable()
});
var workflowRunListSchema = z23.object({
  items: z23.array(workflowRunViewSchema),
  nextCursor: z23.string().nullable()
});
var connectorOperationSchema = z23.object({
  name: z23.string().regex(/^[a-z][a-z0-9_-]{1,79}$/u),
  method: z23.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
  path: z23.string().startsWith("/").max(500).refine((value) => !value.includes(".."), "Path traversal is forbidden")
});
var createConnectorRequestSchema = z23.object({
  workspaceId: z23.string().min(1).max(200),
  name: z23.string().min(2).max(160),
  baseUrl: z23.url().max(500),
  secretRef: z23.string().min(8).max(500),
  operations: z23.array(connectorOperationSchema).min(1).max(50),
  timeoutMs: z23.number().int().min(100).max(3e4).default(5e3)
});
var connectorViewSchema = z23.object({
  id: z23.string(),
  workspaceId: z23.string(),
  name: z23.string(),
  baseOrigin: z23.string(),
  credentialConfigured: z23.boolean(),
  operations: z23.array(connectorOperationSchema),
  timeoutMs: z23.number().int(),
  status: z23.enum(["active", "disabled"]),
  createdAt: z23.iso.datetime({ offset: true })
});
var connectorListSchema = z23.object({
  items: z23.array(connectorViewSchema),
  nextCursor: z23.string().nullable()
});
var invokeConnectorRequestSchema = z23.object({
  operation: z23.string().min(1).max(80),
  idempotencyKey: z23.string().min(8).max(200),
  input: z23.record(z23.string(), z23.unknown()).default({})
});
var connectorInvocationViewSchema = z23.object({
  invocationId: z23.string(),
  connectorId: z23.string(),
  operation: z23.string(),
  status: z23.enum(["succeeded", "failed"]),
  output: z23.record(z23.string(), z23.unknown()).nullable(),
  errorCode: z23.string().nullable(),
  completedAt: z23.iso.datetime({ offset: true })
});

// src/integrations.ts
import { z as z24 } from "zod";
var customerToolMethodSchema = z24.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]);
var workspaceApiKeyScopeSchema = z24.enum([
  "calls.read",
  "calls.start",
  "leads.read",
  "webhooks.manage"
]);
var customerToolViewSchema = z24.object({
  id: z24.string(),
  workspaceId: z24.string(),
  name: z24.string(),
  description: z24.string(),
  method: customerToolMethodSchema,
  url: z24.string().url(),
  authHeaderName: z24.string(),
  /** Secret is write-only; list/get never returns raw secret. */
  hasAuthSecret: z24.boolean(),
  inputSchema: z24.record(z24.string(), z24.unknown()),
  timeoutMs: z24.number().int().positive(),
  enabled: z24.boolean(),
  createdAt: z24.iso.datetime({ offset: true }),
  updatedAt: z24.iso.datetime({ offset: true })
});
var customerToolListSchema = z24.object({
  items: z24.array(customerToolViewSchema)
});
var createCustomerToolRequestSchema = z24.object({
  workspaceId: z24.string().min(1).max(200),
  name: z24.string().min(2).max(80).regex(/^[a-z][a-z0-9_]*$/u, "TOOL_NAME_SNAKE_CASE"),
  description: z24.string().max(500).default(""),
  method: customerToolMethodSchema.default("POST"),
  url: z24.string().url().max(2e3),
  authHeaderName: z24.string().min(1).max(80).default("Authorization"),
  authSecret: z24.string().max(2e3).optional(),
  inputSchema: z24.record(z24.string(), z24.unknown()).default({}),
  timeoutMs: z24.number().int().min(100).max(3e4).default(8e3),
  enabled: z24.boolean().default(true)
});
var updateCustomerToolRequestSchema = z24.object({
  description: z24.string().max(500).optional(),
  method: customerToolMethodSchema.optional(),
  url: z24.string().url().max(2e3).optional(),
  authHeaderName: z24.string().min(1).max(80).optional(),
  authSecret: z24.string().max(2e3).optional().nullable(),
  inputSchema: z24.record(z24.string(), z24.unknown()).optional(),
  timeoutMs: z24.number().int().min(100).max(3e4).optional(),
  enabled: z24.boolean().optional()
});
var workspaceApiKeyViewSchema = z24.object({
  id: z24.string(),
  workspaceId: z24.string(),
  name: z24.string(),
  keyPrefix: z24.string(),
  scopes: z24.array(workspaceApiKeyScopeSchema),
  status: z24.enum(["active", "revoked"]),
  lastUsedAt: z24.iso.datetime({ offset: true }).nullable(),
  createdAt: z24.iso.datetime({ offset: true }),
  revokedAt: z24.iso.datetime({ offset: true }).nullable()
});
var workspaceApiKeyListSchema = z24.object({
  items: z24.array(workspaceApiKeyViewSchema)
});
var createWorkspaceApiKeyRequestSchema = z24.object({
  workspaceId: z24.string().min(1).max(200),
  name: z24.string().min(2).max(80),
  scopes: z24.array(workspaceApiKeyScopeSchema).min(1).max(8)
});
var createWorkspaceApiKeyResponseSchema = z24.object({
  key: workspaceApiKeyViewSchema,
  /** Full secret — store now; never returned again. */
  secret: z24.string().min(20)
});
var webhookEventTypeSchema = z24.enum([
  "call.completed",
  "lead.created",
  "tool.failed",
  "risk.flagged"
]);
var webhookSubscriptionViewSchema = z24.object({
  id: z24.string(),
  workspaceId: z24.string(),
  eventType: webhookEventTypeSchema,
  targetUrl: z24.string().url(),
  hasSecret: z24.boolean(),
  enabled: z24.boolean(),
  createdAt: z24.iso.datetime({ offset: true }),
  updatedAt: z24.iso.datetime({ offset: true })
});
var webhookSubscriptionListSchema = z24.object({
  items: z24.array(webhookSubscriptionViewSchema)
});
var createWebhookSubscriptionRequestSchema = z24.object({
  workspaceId: z24.string().min(1).max(200),
  eventType: webhookEventTypeSchema,
  targetUrl: z24.string().url().max(2e3),
  secret: z24.string().max(500).optional(),
  enabled: z24.boolean().default(true)
});
var integrationDocsSchema = z24.object({
  workspaceId: z24.string(),
  baseUrlHint: z24.string(),
  auth: z24.object({
    header: z24.literal("Authorization"),
    scheme: z24.literal("Bearer"),
    note: z24.string()
  }),
  scopes: z24.array(
    z24.object({
      id: workspaceApiKeyScopeSchema,
      description: z24.string()
    })
  ),
  endpoints: z24.array(
    z24.object({
      method: z24.string(),
      path: z24.string(),
      scope: workspaceApiKeyScopeSchema,
      summary: z24.string()
    })
  ),
  toolConventions: z24.array(z24.string())
});
var toolAuditItemSchema = z24.object({
  id: z24.string(),
  toolName: z24.string(),
  status: z24.string(),
  callId: z24.string().nullable(),
  conversationId: z24.string(),
  userSafeMessage: z24.string(),
  errorCode: z24.string().nullable(),
  createdAt: z24.iso.datetime({ offset: true })
});
var toolAuditListSchema = z24.object({
  items: z24.array(toolAuditItemSchema),
  nextCursor: z24.string().nullable()
});
var webhookDeliveryViewSchema = z24.object({
  id: z24.string(),
  subscriptionId: z24.string(),
  eventType: webhookEventTypeSchema,
  eventId: z24.string(),
  targetUrl: z24.string(),
  status: z24.enum(["succeeded", "failed"]),
  httpStatus: z24.number().int().nullable(),
  attempt: z24.number().int().positive(),
  responseSnippet: z24.string().nullable(),
  errorCode: z24.string().nullable(),
  createdAt: z24.iso.datetime({ offset: true })
});
var webhookDeliveryListSchema = z24.object({
  items: z24.array(webhookDeliveryViewSchema),
  nextCursor: z24.string().nullable()
});

// src/trust.ts
import { z as z25 } from "zod";
var trustPurposeTemplateSchema = z25.object({
  id: z25.string(),
  purpose: z25.enum(["general", "support", "sales", "marketing", "callback", "custom"]),
  title: z25.string(),
  scriptHint: z25.string(),
  allowedOnPlatformExternal: z25.boolean(),
  freeformNoteAllowed: z25.boolean()
});
var trustDncEntrySchema = z25.object({
  id: z25.string(),
  workspaceId: z25.string().nullable(),
  phoneMasked: z25.string(),
  reason: z25.string(),
  source: z25.enum(["manual", "tool", "import", "system"]),
  createdAt: z25.iso.datetime({ offset: true })
});
var trustRiskEventSchema = z25.object({
  id: z25.string(),
  workspaceId: z25.string(),
  callId: z25.string().nullable(),
  numberClass: z25.string().nullable(),
  eventType: z25.string(),
  detail: z25.string(),
  createdAt: z25.iso.datetime({ offset: true })
});
var trustWarmUpStatusSchema = z25.object({
  workspaceId: z25.string(),
  dayUtc: z25.string(),
  platformMinutesUsed: z25.number().nonnegative(),
  platformCallsStarted: z25.number().int().nonnegative(),
  dailyMinuteCap: z25.number().nonnegative(),
  remainingMinutes: z25.number().nonnegative()
});
var trustOverviewSchema = z25.object({
  workspaceId: z25.string(),
  band: z25.object({
    platformExternal: z25.literal("strict"),
    customerByo: z25.literal("light"),
    panaryusInternal: z25.literal("internal")
  }),
  rules: z25.array(z25.string()),
  templates: z25.array(trustPurposeTemplateSchema),
  denylistCount: z25.number().int().nonnegative(),
  dnc: z25.array(trustDncEntrySchema),
  warmUp: trustWarmUpStatusSchema,
  recentRiskEvents: z25.array(trustRiskEventSchema)
});
var createDncRequestSchema = z25.object({
  workspaceId: z25.string().min(1).max(200),
  phoneE164: z25.string().regex(/^\+[1-9]\d{7,14}$/u),
  reason: z25.string().max(200).default("opt_out")
});

// src/live-ops.ts
import { z as z26 } from "zod";
var liveOpsActiveCallSchema = z26.object({
  id: z26.string(),
  workspaceId: z26.string(),
  workspaceName: z26.string(),
  direction: z26.enum(["inbound", "outbound"]),
  status: voiceCallStatusSchema,
  fromMasked: z26.string(),
  toMasked: z26.string(),
  agentId: z26.string(),
  numberClass: z26.string().nullable(),
  provider: z26.string(),
  durationSeconds: z26.number().int().nonnegative(),
  bargeInCount: z26.number().int().nonnegative(),
  handoffRequested: z26.boolean().default(false),
  startedAt: z26.iso.datetime({ offset: true })
});
var liveOpsLatencySchema = z26.object({
  windowMinutes: z26.number().int().positive(),
  turnCount: z26.number().int().nonnegative(),
  sttP50Ms: z26.number().nonnegative().nullable(),
  sttP95Ms: z26.number().nonnegative().nullable(),
  ttsP50Ms: z26.number().nonnegative().nullable(),
  ttsP95Ms: z26.number().nonnegative().nullable(),
  /** Agent/LLM wall (phone hybrid/script turns often near 0). */
  llmP50Ms: z26.number().nonnegative().nullable().optional(),
  llmP95Ms: z26.number().nonnegative().nullable().optional(),
  /** End-of-utterance silence before turn processing. */
  silenceP50Ms: z26.number().nonnegative().nullable().optional(),
  silenceP95Ms: z26.number().nonnegative().nullable().optional(),
  /**
   * Primary UX: user stopped speaking → first agent audio on the wire.
   * Prefer this over e2e when present (populated from media-gateway).
   */
  stopToAudioP50Ms: z26.number().nonnegative().nullable().optional(),
  stopToAudioP95Ms: z26.number().nonnegative().nullable().optional(),
  /** Legacy e2e = stt + tts when stop-to-audio unavailable. */
  e2eP50Ms: z26.number().nonnegative().nullable(),
  e2eP95Ms: z26.number().nonnegative().nullable(),
  bargeInRate: z26.number().min(0).max(1).nullable()
});
var liveOpsChannelLatencySchema = z26.object({
  channel: z26.enum(["softphone", "pstn"]),
  turnCount: z26.number().int().nonnegative(),
  callCount: z26.number().int().nonnegative(),
  stopToAudioP50Ms: z26.number().nonnegative().nullable(),
  stopToAudioP95Ms: z26.number().nonnegative().nullable()
});
var liveOpsQualityTrendDaySchema = z26.object({
  date: z26.string().min(8).max(12),
  turnCount: z26.number().int().nonnegative(),
  stopToAudioP50Ms: z26.number().nonnegative().nullable(),
  toolSuccessRate: z26.number().min(0).max(1).nullable(),
  earlyHangupRate: z26.number().min(0).max(1).nullable()
});
var liveOpsQualitySchema = z26.object({
  windowMinutes: z26.number().int().positive(),
  toolTotal: z26.number().int().nonnegative(),
  toolSucceeded: z26.number().int().nonnegative(),
  toolSuccessRate: z26.number().min(0).max(1).nullable(),
  completedCalls: z26.number().int().nonnegative(),
  earlyHangupCount: z26.number().int().nonnegative(),
  /** Completed calls shorter than earlyHangupSeconds (default 20s). */
  earlyHangupRate: z26.number().min(0).max(1).nullable(),
  earlyHangupSeconds: z26.number().int().positive().default(20),
  /** Provider circuit=open snapshots in the window (not process-local LLM circuit). */
  circuitOpenCount: z26.number().int().nonnegative(),
  softphone: liveOpsChannelLatencySchema.nullable(),
  pstn: liveOpsChannelLatencySchema.nullable(),
  /** Always last 7 UTC days (independent of windowMinutes when window is shorter). */
  trend7d: z26.array(liveOpsQualityTrendDaySchema).max(14)
});
var liveOpsStaleRecoverySchema = z26.object({
  /** Contacts currently status=calling (in-flight or stuck). */
  callingNow: z26.number().int().nonnegative(),
  /**
   * Of callingNow, last updated older than staleAfterSeconds
   * (same grace as RecoverStaleCampaignCallsUseCase default 90s).
   */
  callingStale: z26.number().int().nonnegative(),
  /** Contacts released/failed with stale_* last_error inside the latency window. */
  recoveredInWindow: z26.number().int().nonnegative(),
  staleAfterSeconds: z26.number().int().positive(),
  windowMinutes: z26.number().int().positive()
});
var liveOpsDashboardSchema = z26.object({
  /** Primary workspace when scope=workspace; first membership when scope=all. */
  workspaceId: z26.string(),
  /** workspace = single; all = every workspace in the session. */
  scope: z26.enum(["workspace", "all"]).default("workspace"),
  generatedAt: z26.iso.datetime({ offset: true }),
  /** Workspaces included in this response (labels for filter chips). */
  workspaces: z26.array(z26.object({ id: z26.string(), name: z26.string() })).default([]),
  activeCalls: z26.array(liveOpsActiveCallSchema),
  counts: z26.object({
    active: z26.number().int().nonnegative(),
    ringing: z26.number().int().nonnegative(),
    queued: z26.number().int().nonnegative(),
    waitingForHuman: z26.number().int().nonnegative()
  }),
  latency: liveOpsLatencySchema,
  /** FAZ4 quality board; optional for older clients — default empty-ish via parse. */
  quality: liveOpsQualitySchema.optional(),
  /** B3: campaign stale-calling health (optional for older clients). */
  staleRecovery: liveOpsStaleRecoverySchema.optional(),
  trustFeed: z26.array(trustRiskEventSchema),
  warmUp: z26.object({
    platformMinutesUsed: z26.number().nonnegative(),
    dailyMinuteCap: z26.number().nonnegative(),
    remainingMinutes: z26.number().nonnegative(),
    platformCallsStarted: z26.number().int().nonnegative()
  }).nullable()
});

// src/brain-metrics.ts
import { z as z27 } from "zod";
var rateSchema = z27.number().min(0).max(1).nullable();
var msSchema = z27.number().nonnegative().nullable();
var brainLiveOpsFiltersSchema = z27.object({
  workspace_ids: z27.array(z27.string()),
  channel: z27.string().nullable(),
  agent_id: z27.string().nullable(),
  brain_path: z27.string().nullable(),
  primary_intent: z27.string().nullable()
});
var brainPathDistributionSchema = z27.object({
  deterministic: z27.number().int().nonnegative(),
  llm: z27.number().int().nonnegative(),
  legacy: z27.number().int().nonnegative(),
  deterministic_pct: rateSchema,
  llm_pct: rateSchema,
  legacy_pct: rateSchema
});
var brainIntentRowSchema = z27.object({
  intent: z27.string(),
  turns: z27.number().int().nonnegative(),
  pct_total: rateSchema,
  deterministic_pct: rateSchema,
  llm_pct: rateSchema,
  stop_p50_ms: msSchema,
  stop_avg_ms: msSchema
});
var brainTemplateRowSchema = z27.object({
  template_id: z27.string(),
  usage_count: z27.number().int().nonnegative(),
  repeat_count: z27.number().int().nonnegative(),
  repeat_rate: rateSchema,
  stop_p50_ms: msSchema,
  stop_avg_ms: msSchema,
  next_turn_llm_fallback_rate: rateSchema
});
var brainLatencyTimeseriesPointSchema = z27.object({
  bucket: z27.string(),
  deterministic_p50_ms: msSchema,
  deterministic_p95_ms: msSchema,
  llm_p50_ms: msSchema,
  llm_p95_ms: msSchema,
  sample_count: z27.number().int().nonnegative()
});
var brainTurnDrilldownRowSchema = z27.object({
  id: z27.string(),
  created_at: z27.string(),
  workspace_id: z27.string(),
  conversation_id: z27.string(),
  call_id: z27.string().nullable(),
  turn_id: z27.string(),
  agent_id: z27.string().nullable(),
  channel: z27.string().nullable(),
  brain_path: z27.enum(["deterministic", "llm", "legacy"]),
  brain_active: z27.boolean(),
  primary_intent: z27.string().nullable(),
  template_id: z27.string().nullable(),
  llm_called: z27.boolean().nullable(),
  llm_input_tokens: z27.number().int().nullable(),
  llm_output_tokens: z27.number().int().nullable(),
  stop_to_first_audio_ms: msSchema,
  hangup_source: z27.string().nullable(),
  fail_open: z27.boolean().nullable(),
  policy_lead_actionable: z27.boolean().nullable(),
  policy_callback_actionable: z27.boolean().nullable(),
  policy_end_terminal: z27.boolean().nullable(),
  tool_lead_create: z27.boolean().nullable(),
  tool_callback_schedule: z27.boolean().nullable(),
  tool_conversation_end: z27.boolean().nullable(),
  end_policy_tool_conflict: z27.boolean().nullable()
});
var brainLiveOpsDashboardSchema = z27.object({
  generated_at: z27.string(),
  last_metric_at: z27.string().nullable(),
  freshness_lag_seconds: z27.number().int().nonnegative().nullable(),
  range: z27.object({ from: z27.string(), to: z27.string() }),
  baseline_range: z27.object({ from: z27.string(), to: z27.string() }).nullable(),
  baseline_selected: z27.boolean(),
  filters: brainLiveOpsFiltersSchema,
  kpi_definitions: z27.record(z27.string(), z27.union([z27.string(), z27.number()])),
  empty: z27.boolean(),
  overview: z27.object({
    total_turns: z27.number().int().nonnegative(),
    brain_active_turns: z27.number().int().nonnegative(),
    deterministic_rate: rateSchema,
    llm_call_rate: rateSchema,
    baseline_llm_call_rate: rateSchema,
    pilot_llm_call_rate: rateSchema,
    llm_call_rate_delta: z27.number().nullable(),
    llm_reduction_label: z27.string().nullable(),
    fail_open_rate: rateSchema,
    latency_coverage: rateSchema,
    latency_coverage_warning: z27.boolean(),
    conversations_count: z27.number().int().nonnegative(),
    calls_count: z27.number().int().nonnegative()
  }),
  path_distribution: brainPathDistributionSchema,
  latency: z27.object({
    samples_available: z27.boolean(),
    coverage: rateSchema,
    coverage_warning: z27.boolean(),
    deterministic_p50_ms: msSchema,
    deterministic_p95_ms: msSchema,
    llm_p50_ms: msSchema,
    llm_p95_ms: msSchema,
    legacy_p50_ms: msSchema,
    legacy_p95_ms: msSchema,
    timeseries: z27.array(brainLatencyTimeseriesPointSchema)
  }),
  intents: z27.array(brainIntentRowSchema),
  templates: z27.array(brainTemplateRowSchema),
  template_repeat: z27.object({
    repeated_turns: z27.number().int().nonnegative(),
    top_repeated_template_ids: z27.array(
      z27.object({
        template_id: z27.string(),
        repeat_turns: z27.number().int().nonnegative()
      })
    )
  }),
  fail_open: z27.object({
    fail_open_count: z27.number().int().nonnegative(),
    fail_open_rate: rateSchema,
    classifier_exception_count: z27.number().int().nonnegative(),
    policy_exception_count: z27.number().int().nonnegative(),
    deterministic_missing_response_count: z27.number().int().nonnegative(),
    metrics_write_failed_count: z27.number().int().nonnegative()
  }),
  policy: z27.object({
    lead_detected: z27.number().int().nonnegative(),
    lead_actionable: z27.number().int().nonnegative(),
    callback_detected: z27.number().int().nonnegative(),
    callback_actionable: z27.number().int().nonnegative(),
    decline_detected: z27.number().int().nonnegative(),
    decline_terminal: z27.number().int().nonnegative(),
    end_detected: z27.number().int().nonnegative(),
    end_terminal: z27.number().int().nonnegative(),
    lead_reason_counts: z27.record(z27.string(), z27.number()),
    callback_reason_counts: z27.record(z27.string(), z27.number()),
    decline_reason_counts: z27.record(z27.string(), z27.number()),
    end_reason_counts: z27.record(z27.string(), z27.number())
  }),
  divergence: z27.object({
    policy_lead_actionable_tool_none: z27.number().int().nonnegative(),
    legacy_lead_tool_policy_not_actionable: z27.number().int().nonnegative(),
    policy_callback_actionable_tool_none: z27.number().int().nonnegative(),
    legacy_callback_tool_policy_not_actionable: z27.number().int().nonnegative(),
    end_policy_tool_conflict_count: z27.number().int().nonnegative(),
    end_conflict_alert: z27.boolean()
  }),
  hangup: z27.object({
    counts: z27.record(z27.string(), z27.number()),
    soft_farewell_detected: z27.number().int().nonnegative(),
    soft_farewell_not_ended: z27.number().int().nonnegative(),
    soft_farewell_not_ended_rate: rateSchema
  }),
  pilot_status: z27.object({
    brain_v1: z27.boolean(),
    router: z27.boolean(),
    require_allowlist: z27.boolean(),
    allowlist_workspaces: z27.array(z27.string()).nullable(),
    filtered_workspace_in_allowlist: z27.boolean().nullable(),
    pilot_banner: z27.boolean(),
    safety_warning: z27.string().nullable()
  }),
  tokens: z27.object({
    input_sum: z27.number().int().nonnegative(),
    output_sum: z27.number().int().nonnegative()
  }),
  // summary is internal-compatible; keep as loose object for forward-compat
  summary: z27.record(z27.string(), z27.unknown())
});
var brainTurnsPageSchema = z27.object({
  generated_at: z27.string(),
  page: z27.number().int().positive(),
  page_size: z27.number().int().positive(),
  total: z27.number().int().nonnegative(),
  items: z27.array(brainTurnDrilldownRowSchema)
});

// src/sms.ts
import { z as z28 } from "zod";
var smsStatusSchema = z28.enum(["queued", "sent", "failed", "simulated"]);
var smsMessageViewSchema = z28.object({
  id: z28.string(),
  workspaceId: z28.string(),
  toE164: z28.string(),
  toMasked: z28.string(),
  bodyPreview: z28.string(),
  status: smsStatusSchema,
  provider: z28.string(),
  providerMessageId: z28.string().nullable(),
  header: z28.string().nullable(),
  errorCode: z28.string().nullable(),
  createdAt: z28.iso.datetime({ offset: true })
});
var smsMessageListSchema = z28.object({
  items: z28.array(smsMessageViewSchema),
  nextCursor: z28.string().nullable()
});
var sendSmsRequestSchema = z28.object({
  workspaceId: z28.string().min(1).max(200),
  toE164: z28.string().min(8).max(20).regex(/^\+[1-9]\d{7,14}$/u, "SMS_TO_E164_INVALID"),
  body: z28.string().min(1).max(1e3),
  /** Approved Verimor SMS header / source title when required. */
  header: z28.string().min(2).max(40).optional(),
  /** Force simulated path even if provider is configured (dev/QA). */
  simulate: z28.boolean().optional()
});

// src/index.ts
var createEmbedSessionRequestSchema = z29.object({
  hostUserId: z29.string().min(1).max(200),
  tenantExternalId: z29.string().min(1).max(200),
  workspaceExternalIds: z29.array(z29.string().min(1).max(200)).max(100).optional(),
  initialRoute: z29.string().startsWith("/").max(200).optional(),
  /** Panaryus plan voice tier: starter | growth | pro */
  voiceServiceProfile: z29.enum(["starter", "growth", "pro"]).optional(),
  /**
   * Preferred UI locale from host Panaryus session (e.g. ar, tr-TR, en).
   * When present, CenterOS persists it on the mapped user so session-manifest
   * drives console i18n/RTL for this principal.
   */
  locale: z29.string().min(2).max(32).regex(/^[A-Za-z]{2,3}([_-][A-Za-z0-9]{2,8})*$/).optional(),
  /** Human display name from host (e.g. "Nakliye Yolda") — principal greeting + avatar. */
  displayName: z29.string().min(1).max(160).optional(),
  /** Tenant/workspace label from host (business name preferred). */
  tenantName: z29.string().min(1).max(160).optional(),
  /**
   * Panaryus platform operator (super-admin / admin console access).
   * When true, session gets entitlement `platform.admin` for CenterOS Platform settings.
   */
  platformAdmin: z29.boolean().optional(),
  /**
   * Platform DID policy from the host plan pack:
   * - all: seed full shared pool
   * - selected: seed only `allowedPlatformDids`
   * - none: do not seed platform CLIs (BYO only)
   */
  platformDidMode: z29.enum(["all", "selected", "none"]).optional(),
  /** E.164 list when platformDidMode is `selected` (or filter when provided). */
  allowedPlatformDids: z29.array(z29.string().regex(/^\+[1-9]\d{7,14}$/u)).max(50).optional(),
  /**
   * Max platform DIDs to seed from the admin pool for this package (TR-first order).
   * Null/omit = no extra cap beyond mode/allow-list.
   */
  platformDidMaxCount: z29.number().int().positive().max(50).optional(),
  /** Package service stack (informational + future runtime gates). */
  llmProvider: z29.enum(["openai", "none"]).optional(),
  sttProvider: z29.enum(["deepgram", "openai", "none"]).optional(),
  ttsProvider: z29.enum(["elevenlabs", "openai", "none"]).optional(),
  realtimeEnabled: z29.boolean().optional()
});
var embedSessionResponseSchema = z29.object({
  token: z29.string().min(20),
  expiresAt: z29.iso.datetime({ offset: true })
});
var principalSchema = z29.object({
  id: z29.string().min(1),
  displayName: z29.string().min(1)
});
var tenantSchema = z29.object({ id: z29.string().min(1), name: z29.string().min(1) });
var workspaceSchema = z29.object({ id: z29.string().min(1), name: z29.string().min(1) });
var navigationItemSchema = z29.object({
  id: z29.string().min(1),
  route: z29.string().startsWith("/"),
  label: z29.string().min(1),
  icon: z29.string().nullable().optional(),
  children: z29.array(z29.object({ id: z29.string(), route: z29.string(), label: z29.string() })).optional()
});
var sessionManifestSchema = z29.object({
  principal: principalSchema,
  tenant: tenantSchema,
  workspaces: z29.array(workspaceSchema),
  permissions: z29.array(z29.string()).readonly(),
  entitlements: z29.array(z29.string()).readonly(),
  navigation: z29.array(navigationItemSchema).readonly(),
  theme: z29.record(z29.string(), z29.string()),
  locale: z29.string().optional(),
  expiresAt: z29.iso.datetime({ offset: true })
});
var problemDetailsSchema = z29.object({
  type: z29.string(),
  title: z29.string(),
  status: z29.number().int().min(400).max(599),
  detail: z29.string().optional(),
  instance: z29.string().optional(),
  requestId: z29.string(),
  code: z29.string()
});
export {
  ACK_CLASSES,
  ACK_STRENGTHS,
  AGENDA_CUSTOMER_NEEDS,
  AGENDA_MOVES,
  AGENDA_TURN_INTENTS,
  AGENT_LLM_MODEL_OPTIONS,
  AGENT_REALTIME_MODEL_OPTIONS,
  AGENT_STT_MODEL_OPTIONS,
  AGENT_STT_PROVIDER_OPTIONS,
  AGENT_TEMPLATE_CATALOG,
  AGENT_TEMPLATE_VARIABLES,
  AGENT_VOICE_RUNTIME_OPTIONS,
  ASSISTANT_SPEECH_ACTS,
  BIND_TARGETS,
  BUSINESS_PROGRESSION_READINESS,
  CLOSED_RECOVERY_USES_LLM,
  CLOSED_TEMPLATE_SEMANTICS_PREVALIDATED,
  CLOSE_KINDS,
  CLOSE_POLARITIES,
  CLOSE_STRENGTHS,
  COMMUNICATIVE_ACTS,
  CONTEXTUAL_RECOVERY_CAN_EMIT_WITHOUT_TRUSTED_SEMANTIC_VERIFIER,
  CONTEXTUAL_RECOVERY_EMITTED_WITHOUT_COMPARATOR_PASS,
  CONTINUITY_APPLICATION_SKIP_REASONS,
  CONTINUITY_CHANNELS,
  CONTINUITY_CHANNEL_SCHEMA,
  CONTINUITY_CONNECTION_UNCERTAIN_MS_ENV,
  CONTINUITY_CONVERSATION_EVENTS,
  CONTINUITY_DURATION_ENV_KEYS,
  CONTINUITY_DURATION_MS_MAX,
  CONTINUITY_DURATION_MS_MIN,
  CONTINUITY_FIRST_IDLE_SILENCE_EVENTS,
  CONTINUITY_FIRST_IDLE_SILENCE_EVENT_SCHEMA,
  CONTINUITY_IDLE_CLIENT_MESSAGE_ID_PREFIX,
  CONTINUITY_IDLE_FORBIDDEN_AUTHORITY_KEYS,
  CONTINUITY_IDLE_SKIP_REASONS,
  CONTINUITY_IDLE_TURN_HAS_CUSTOMER_CONTENT,
  CONTINUITY_MEDIA_EVENTS,
  CONTINUITY_MEDIA_EVENTS_RESERVED,
  CONTINUITY_OBSERVABILITY_EVENTS,
  CONTINUITY_PLAYBACK_COMMIT_STATUSES,
  CONTINUITY_PLAYBACK_MEDIA_FACTS,
  CONTINUITY_POLICY_EVALUATION_OUTCOMES,
  CONTINUITY_POLICY_FAILURE_REASONS,
  CONTINUITY_RECOVERY_AGENDA_FAILURE_REASONS,
  CONTINUITY_RECOVERY_AGENDA_OUTCOMES,
  CONTINUITY_RECOVERY_AGENDA_SKIP_REASONS,
  CONTINUITY_SHADOW_TEXT,
  CONTINUITY_STALE_GENERATION_TRUST_V1,
  CONTINUITY_VOICE_RUNTIMES,
  CONTINUITY_WATCH_DISPOSITIONS,
  CONTINUITY_WATCH_STATUSES,
  CONVERSATIONAL_COMMITMENT_TYPES_V1,
  CONVERSATION_STYLE_OPTIONS,
  CONVERSATION_TURN_TRIGGERS,
  CUSTOMER_ACTIVITY_CAN_BE_TALKED_OVER_BY_STALE_RECOVERY,
  DEFAULT_AGENT_REALTIME_MODEL,
  DEFAULT_DIALOGUE_STYLE,
  DEFAULT_LEAD_LIST_CONTRACT,
  DEFAULT_VOICE_KNOWLEDGE_DEPTH,
  DEFAULT_VOICE_PREVIEW_TEXT_EN,
  DEFAULT_VOICE_PREVIEW_TEXT_TR,
  DEFAULT_VOICE_PROFILE_ID,
  DIALOGUE_STYLE_CATALOG,
  DUPLICATE_RECOVERY_PLAYBACK_COMPLETION_CAN_DOUBLE_INCREMENT,
  EMPTY_CONTINUITY_RECOVERY_AGENDA,
  HANGUP_AND_EOU_TIMER_FIELDS,
  INITIATIVE_FLOORS,
  LEAD_LIST_CONTRACT_HEADER,
  LEAD_LIST_CONTRACT_VOICE_V1,
  LEAD_LIST_CONTRACT_WORKSPACE_V1,
  LEAD_STAGE_BOARD,
  LEGACY_SPEECH_PROTECTION_PARAM_KEYS,
  LEXICAL_CHECK_CAN_GRANT_EMIT,
  PERSONALITY_PRESETS,
  PLAYBACK_SESSION_KINDS,
  PLAYBACK_SESSION_KINDS_EXCLUDED,
  PLAYBACK_SESSION_KIND_SCHEMA,
  PLAYBACK_SESSION_OUTCOMES,
  POLICY_COMMITMENT_TYPES,
  POLICY_CONFIDENCE_LEVELS,
  POLICY_PRIMARY_NEEDS,
  REALTIME_CONTINUITY_V1_ELIGIBLE,
  RECOVERY_AGENDA_MOVES,
  RECOVERY_AGENDA_REALIZATION_CLASSES,
  RECOVERY_ASSISTANT_ROW_TAGGED_STRUCTURALLY,
  RECOVERY_COMPLETED_REARMS_CONTINUITY_WATCH,
  RECOVERY_LLM_RETRY_MAX,
  RECOVERY_SPEECH_AUTHORIZES_HANGUP,
  RECOVERY_SPEECH_CAN_REQUEST_TOOL,
  RECOVERY_SPEECH_WRITES_BUSINESS_STATE,
  RECOVERY_STEPS,
  RECOVERY_STEP_TO_AGENDA_MOVE,
  RECOVERY_STEP_TO_AGENDA_NEED,
  RECOVERY_STEP_TO_REALIZATION_CLASS,
  REJECTED_RECOVERY_CANDIDATE_PERSISTED_AS_ASSISTANT_SPEECH,
  REQUEST_TOOL_BUSINESS_INTENTS,
  SAFE_CLOSE_CALLS_CONVERSATION_END,
  SAFE_CLOSE_CALLS_SOCKET_DESTROY,
  SAFE_CLOSE_REARMS_CONTINUITY,
  SCRIPTED_TEST_CLASSIFIER_CAN_ENABLE_PRODUCTION_EMIT,
  SEMANTIC_MODEL_GRANTS_AUTHORITY,
  SEMANTIC_MODEL_SELECTED,
  SHADOW_MODE_CAN_PLAY_RECOVERY_TTS,
  SILENCE_EVENTS,
  SILENCE_REPEAT_COUNT_INCREMENT_POINT,
  SPEECH_LLM_SELECTS_RECOVERY_ACT,
  SPEECH_PROTECTION_LEGACY_PARAM_REJECTED,
  TURNAGENDA_CAN_GRANT_RECOVERY_PERMISSION,
  UNSUPPORTED_LOCALE_CAN_EMIT_RECOVERY,
  VOICE_AMBIENCE_BEDS,
  VOICE_KNOWLEDGE_DEPTH_OPTIONS,
  activeTtsProvider,
  addCampaignContactsRequestSchema,
  addTextSourceRequestSchema,
  agentCapabilitiesSchema,
  agentChannelModeSchema,
  agentConversationControlSchema,
  agentDraftConfigSchema,
  agentFactSchemaEntrySchema,
  agentInterviewMessageSchema,
  agentListSchema,
  agentLlmModelIds,
  agentLlmModelSchema,
  agentMissionSchema,
  agentPlaybookSchema,
  agentRealtimeModelIds,
  agentRealtimeModelSchema,
  agentRuntimeTurnRequestSchema,
  agentRuntimeTurnResponseSchema,
  agentSttModelIds,
  agentSttModelSchema,
  agentSttProviderIds,
  agentSttProviderSchema,
  agentTemplateCategorySchema,
  agentTemplateListSchema,
  agentTemplateSchema,
  agentToolExecutionViewSchema,
  agentToolStatusSchema,
  agentViewSchema,
  agentVoiceRuntimeIds,
  agentVoiceRuntimeSchema,
  agentWidgetConfigSchema,
  analysisJobStatusSchema,
  analysisJobViewSchema,
  authorizeUsageRequestSchema,
  billingSummarySchema,
  billingUsageDashboardSchema,
  brainImprovementProposalSchema,
  brainIntentRowSchema,
  brainLatencyTimeseriesPointSchema,
  brainListSchema,
  brainLiveOpsDashboardSchema,
  brainLiveOpsFiltersSchema,
  brainPathDistributionSchema,
  brainTemplateRowSchema,
  brainTurnDrilldownRowSchema,
  brainTurnsPageSchema,
  brainViewSchema,
  brandingSchema,
  buildContinuityIdempotencyKey,
  buildWebchatEmbedSnippet,
  bulkUpdateVoiceLeadStagesRequestSchema,
  bulkUpdateVoiceLeadStagesResponseSchema,
  calendarConnectionListSchema,
  calendarConnectionStatusSchema,
  calendarConnectionViewSchema,
  calendarEnvironmentSchema,
  calendarEventResultSchema,
  calendarProviderSchema,
  callAnalysisSnapshotSchema,
  callCardContextSchema,
  callFollowUpSchema,
  callOpsCostSchema,
  callToolTimelineSchema,
  campaignContactImportResultSchema,
  campaignContactListSchema,
  campaignContactStatusSchema,
  campaignContactViewSchema,
  campaignListSchema,
  campaignRetryPolicySchema,
  campaignRunResultSchema,
  campaignSchedulePolicySchema,
  campaignStatusSchema,
  campaignViewSchema,
  capabilitiesFromPlaybook,
  capabilitiesFromTools,
  channelContinuityPolicyFor,
  channelKindSchema,
  citationSchema,
  classifyContinuityWatchEligibility,
  compilePlaybookInstructions,
  completeVoiceCallRequestSchema,
  completionRulesSchema,
  confirmationPolicySchema,
  connectorInvocationViewSchema,
  connectorListSchema,
  connectorOperationSchema,
  connectorViewSchema,
  contactCallRequestSchema,
  contactListSchema,
  contactMessageRequestSchema,
  contactPurposeSchema,
  contactViewSchema,
  continuityIdleClientMessageId,
  continuityIdleMustNotUseCustomerSemanticExtraction,
  continuityIdleRequestHasForbiddenAuthority,
  conversationAnalysisListSchema,
  conversationAnalysisViewSchema,
  conversationFactRecordSchema,
  conversationPlaybookSchema,
  conversationPoliciesSchema,
  conversationQualityReportSchema,
  costFxSchema,
  costServiceSchema,
  createAgentRequestSchema,
  createBrainRequestSchema,
  createByoPhoneNumberRequestSchema,
  createCampaignRequestSchema,
  createConnectorRequestSchema,
  createContactRequestSchema,
  createCustomerToolRequestSchema,
  createDncRequestSchema,
  createEmbedSessionRequestSchema,
  createManagedPhoneNumberRequestSchema,
  createPlanRequestSchema,
  createProviderAccountRequestSchema,
  createVoiceCallRequestSchema,
  createWebhookSubscriptionRequestSchema,
  createWorkspaceApiKeyRequestSchema,
  createWorkspaceApiKeyResponseSchema,
  customerToolListSchema,
  customerToolMethodSchema,
  customerToolViewSchema,
  dataLifecycleRequestListSchema,
  dataLifecycleRequestSchema,
  dataLifecycleRequestViewSchema,
  defaultAgentWidgetConfig,
  defaultInboundPlaybook,
  defaultRetryPolicy,
  defaultSchedulePolicy,
  dialogueStyleSchema,
  eligibleActionSchema,
  embedSessionResponseSchema,
  factExtractionMethodSchema,
  factKeySchema,
  factStatusSchema,
  factTypeSchema,
  factValidationSchema,
  genericActionKindSchema,
  getAgentTemplate,
  handoffVoiceCallRequestSchema,
  importCampaignContactsCsvRequestSchema,
  importPhoneNumberPoolRequestSchema,
  importPhoneNumberPoolResponseSchema,
  integrationDocsSchema,
  intelligenceDashboardSchema,
  intelligenceEvidenceSchema,
  internalAgentToolNameSchema,
  internalMediaCompleteCallRequestSchema,
  internalMediaCompleteCallResponseSchema,
  internalMediaContinuityIdleRequestSchema,
  internalMediaContinuityIdleResponseSchema,
  internalMediaContinuityPlaybackRequestSchema,
  internalMediaContinuityPlaybackResponseSchema,
  internalMediaRecordingRequestSchema,
  internalMediaRecordingResponseSchema,
  internalMediaTurnMetricsRequestSchema,
  internalMediaTurnMetricsResponseSchema,
  internalMediaTurnRequestSchema,
  internalMediaTurnResponseSchema,
  interviewAgentDraftSchema,
  interviewAgentTurnRequestSchema,
  interviewAgentTurnResponseSchema,
  invokeConnectorRequestSchema,
  isContinuityIdleTurnEnvelope,
  isContinuityV1WiredChannel,
  isContinuityVoiceRuntimeV1Eligible,
  isCustomerTurnEnvelope,
  isDialogueStyle,
  isMappedRecoveryStep,
  isPlaybackKindWatchEligible,
  isPlaybackOutcomeWatchEligible,
  isPlaybackSessionFutureWatchEligible,
  isRealtimeContinuityV1Eligible,
  isRecoveryAgendaMove,
  isWhatsAppMessagingConnectorTool,
  isWidgetDomainAllowed,
  knowledgeGapReportSchema,
  knowledgeGapSchema,
  knowledgeKindSchema,
  knowledgeProposalSchema,
  leadChannelSchema,
  leadListContractSchema,
  leadScoreSchema,
  leadSentimentSchema,
  leadStageSchema,
  legacySpeechProtectionRejectionFields,
  legacyVoiceLeadListSchema,
  legacyVoiceLeadViewSchema,
  listAgentTemplates,
  listNamedVoiceProfiles,
  liveOpsActiveCallSchema,
  liveOpsChannelLatencySchema,
  liveOpsDashboardSchema,
  liveOpsLatencySchema,
  liveOpsQualitySchema,
  liveOpsQualityTrendDaySchema,
  liveOpsStaleRecoverySchema,
  llmTemperatureOption,
  memoryScopeSchema,
  mergePlaybookIntoInstructions,
  modeBRateCardSchema,
  moneyDualSchema,
  namedVoiceProfileIdSchema,
  namedVoiceProfileSchema,
  namedVoiceProfiles,
  navigationItemSchema,
  normalizeContinuityIdempotencyIdentity,
  omnichannelTurnResponseSchema,
  onboardingMessageRequestSchema,
  onboardingMessageResponseSchema,
  outcomeTypeSchema,
  parseChannelContinuityDurationsFromEnv,
  parseContinuityDurationMs,
  partnerBrandingViewSchema,
  personalityPresetIdSchema,
  personalityPresetListSchema,
  personalityPresetSchema,
  phoneNumberClassSchema,
  phoneNumberGeoScopeSchema,
  phoneNumberListSchema,
  phoneNumberProviderSchema,
  phoneNumberStatusSchema,
  phoneNumberTrustBandSchema,
  phoneNumberViewSchema,
  pickTemplateFirstMessage,
  planEntitlementSchema,
  planViewSchema,
  playbookStageSchema,
  playbookStepKindSchema,
  playbookStepSchema,
  problemDetailsSchema,
  profileTtsVoiceId,
  proposalListSchema,
  providerAccountListSchema,
  providerAccountStatusSchema,
  providerAccountViewSchema,
  providerCapabilitySchema,
  providerHealthSchema,
  providerHealthViewSchema,
  providerUsageSummarySchema,
  providerVoiceCatalogItemSchema,
  providerVoiceCatalogSchema,
  provisionPartnerTenantRequestSchema,
  provisionedTenantViewSchema,
  publishAgentRequestSchema,
  publishAgentResponseSchema,
  publishBrainRequestSchema,
  publishBrainResponseSchema,
  quotaDecisionSchema,
  quotaEnforcementSchema,
  recommendationListSchema,
  recommendationStatusSchema,
  recommendationTypeSchema,
  recommendationViewSchema,
  reconcileProvidersRequestSchema,
  reconcileProvidersResponseSchema,
  recordUsageRequestSchema,
  requestToolBusinessIntentSchema,
  resolveAgentTemplateVars,
  resolveDialogueStyle,
  resolveNamedVoiceProfile,
  resolveNamedVoiceProfileByProviderVoice,
  resolvePersonalityPreset,
  resolveVoiceKnowledgeBudget,
  retentionPolicyRequestSchema,
  retentionPolicyViewSchema,
  retrievalResponseSchema,
  retrievalResultSchema,
  reverseUsageRequestSchema,
  reviewRecommendationRequestSchema,
  routingPolicyListSchema,
  routingPolicyViewSchema,
  runOmnichannelTurnRequestSchema,
  runtimeResponseSchema,
  scheduleDaySchema,
  scoreDimensionsSchema,
  sendSmsRequestSchema,
  sessionManifestSchema,
  simulateAgentRequestSchema,
  simulateScenarioRequestSchema,
  simulateScenarioResponseSchema,
  smsMessageListSchema,
  smsMessageViewSchema,
  smsStatusSchema,
  sourceAcceptedSchema,
  stageIdSchema,
  startCampaignRequestSchema,
  startConversationAnalysisRequestSchema,
  startGoogleCalendarOAuthRequestSchema,
  startGoogleCalendarOAuthResponseSchema,
  streamEventSchema,
  stripPlaybookSection,
  subscriptionStatusSchema,
  subscriptionViewSchema,
  toContinuityRecoveryAgendaSnapshot,
  toolAuditItemSchema,
  toolAuditListSchema,
  toolsFromCapabilities,
  toolsFromPlaybook,
  trustDncEntrySchema,
  trustOverviewSchema,
  trustPurposeTemplateSchema,
  trustRiskEventSchema,
  trustWarmUpStatusSchema,
  updateAgentDraftRequestSchema,
  updateCampaignRequestSchema,
  updateContactRequestSchema,
  updateCustomerToolRequestSchema,
  updatePartnerBrandingRequestSchema,
  updatePhoneNumberRequestSchema,
  updateVoiceLeadRequestSchema,
  uploadWorkspaceAmbienceRequestSchema,
  upsertCalendarConnectionRequestSchema,
  upsertRoutingPolicyRequestSchema,
  usageEventViewSchema,
  usageMeterSchema,
  usesJevCandidateSelection,
  validateConversationControl,
  voiceCallCardSchema,
  voiceCallListFiltersSchema,
  voiceCallListSchema,
  voiceCallStatusSchema,
  voiceCallViewSchema,
  voiceGreetingResponseSchema,
  voiceKnowledgeDepthSchema,
  voiceKnowledgeDepths,
  voiceLeadListSchema,
  voiceLeadViewSchema,
  voiceOnboardingAnswerRequestSchema,
  voiceOnboardingStatusSchema,
  voiceOnboardingTopicViewSchema,
  voicePerformanceDashboardSchema,
  voicePerformanceQuerySchema,
  voicePreviewRequestSchema,
  voicePreviewResponseSchema,
  voiceProfileSchema,
  voiceTranscriptEntrySchema,
  voiceTranscriptSchema,
  voiceTurnRequestSchema,
  voiceTurnResponseSchema,
  voiceTurnTimelineSchema,
  webchatMessageRequestSchema,
  webchatSessionRequestSchema,
  webchatSessionResponseSchema,
  webchatWidgetThemeSchema,
  webhookDeliveryListSchema,
  webhookDeliveryViewSchema,
  webhookEventTypeSchema,
  webhookSubscriptionListSchema,
  webhookSubscriptionViewSchema,
  websiteSourceRequestSchema,
  workflowRunListSchema,
  workflowRunStatusSchema,
  workflowRunViewSchema,
  workspaceAmbienceAssetSchema,
  workspaceAmbienceListSchema,
  workspaceApiKeyListSchema,
  workspaceApiKeyScopeSchema,
  workspaceApiKeyViewSchema
};
